import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  CalendarDays,
  Clock,
  Users,
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Phone,
  MessageSquare,
  Check,
  X,
  UserCheck,
  LogIn,
  CheckCircle,
  AlertCircle,
  XCircle,
  Edit3,
  Trash2,
  Mail,
  MapPin,
  Ban,
  ArrowRightLeft,
  Unlock,
  Lock,
  Paperclip,
  Send,
  List,
  LayoutGrid,
  UserPlus,
  Coffee,
  Bath,
  DoorOpen,
  Flower2,
  Columns,
  RectangleHorizontal,
  Smartphone,
  Globe,
  Bot,
  Move,
  Grid3X3,
  Settings,
  ZoomIn,
  ZoomOut,
  Maximize2,
  UtensilsCrossed,
  ChevronDown,
  ChevronUp,
  Minus,
  Edit2,
  RefreshCw,
} from "lucide-react";
import {
  format,
  addDays,
  subDays,
  isToday,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  parseISO,
  isValid,
} from "date-fns";
import { es } from "date-fns/locale";
import api from "../lib/api-client";
import CustomerDetailModal from "../components/CustomerDetailModal";
import {
  colorProps,
  statusBadgeColorProps,
  tableStatusColorProps,
  statusButtonColorProps,
} from "../lib/status-colors";
import { useLocation } from "../contexts/LocationContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { useGlobalToast } from "../contexts/ToastContext";
import { usePermissions } from "../hooks/usePermissions";
import CustomerEditModal, {
  type Customer as CustomerRecord,
} from "../components/CustomerEditModal";
import TablePopover from "../components/TablePopover";
import {
  getOrderedCountries,
  defaultCountryCodeForTimezone,
} from "@reservamanager/shared-types";

// Type for table blocks
interface TableBlock {
  id: string;
  tableId: string;
  date: string;
  shift: string | null;
  reason: string | null;
}

// Warning que puede venir en la respuesta de crear/actualizar-mover/walk-in/
// aprobar-cambio de una reserva cuando la mesa asignada está bloqueada
// (TableBlock) o pertenece a un salón (Room) deshabilitado ese día
// (ScheduleOverride.disabledRoomIds). El backend ya lo permite en ambos casos
// (aviso SOFT para staff, nunca bloquea la operación), solo avisa.
interface ReservationWarning {
  code: string;
  tableId?: string;
  tableName?: string;
  reason?: string | null;
  shift?: string | null;
  roomId?: string;
  roomName?: string;
}

// Shape mínimo común de las respuestas de /api/reservations que pueden traer
// `warnings`. `data` no se tipa en detalle porque cada handler ya usa su
// propio shape local; aquí solo interesa el array de warnings.
interface ReservationApiResponse {
  success: boolean;
  data?: unknown;
  warnings?: ReservationWarning[];
}

// Type for table capacity overrides (aforo ajustado por día/turno)
interface TableCapacityOverride {
  id: string;
  tableId: string;
  date: string;
  shift: string | null; // "comida" | "cena" | null (null = todo el día)
  minCapacity: number;
  maxCapacity: number;
  reason: string | null;
}

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  name?: string;
  email: string | null;
  phone: string | null;
}

interface CustomerSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
}

interface Table {
  id: string;
  name: string;
  minCapacity: number;
  maxCapacity: number;
  shape: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  roomId: string;
}

interface Room {
  id: string;
  name: string;
  gridSize?: number;
  tables: Table[];
  decorativeObjects?: DecorativeObject[];
}

// Tipos de objetos decorativos
type DecorativeType = "bar" | "bathroom" | "door" | "wall" | "plant" | "column";

interface DecorativeObject {
  id: string;
  type: DecorativeType;
  label?: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  rotation: number;
}

const DECORATIVE_TYPES: {
  type: DecorativeType;
  label: string;
  color: string;
}[] = [
  { type: "bar", label: "Barra", color: "bg-amber-600" },
  { type: "bathroom", label: "Baños", color: "bg-blue-500" },
  { type: "door", label: "Puerta", color: "bg-green-600" },
  { type: "wall", label: "Muro", color: "bg-gray-700" },
  { type: "plant", label: "Planta", color: "bg-emerald-500" },
  { type: "column", label: "Columna", color: "bg-stone-500" },
];

// Lienzo fijo del mapa en vivo: 50x50 celdas, igual que el editor permanente
// (/tables). `Room.gridSize` sigue llegando de la API pero ya no se usa para
// pintar — 50 es superconjunto de todos los tamaños de grid que existían
// antes, así que las posiciones guardadas siguen siendo válidas tal cual.
const CANVAS_CELLS = 50;
// Tamaño de celda DINÁMICO (zoom): vive en `useMapViewport` (estado por
// superficie de mapa). Estas son sus cotas y su valor por defecto (sala
// vacía / antes de la primera medición).
const DEFAULT_CELL_PX = 18;
const MIN_CELL_PX = 8;
const MAX_CELL_PX = 48;
const ZOOM_STEP = 1.25;
// Margen alrededor de las mesas/objetos al auto-encuadrar, en celdas.
const FIT_MARGIN_CELLS = 1.5;
// Recuadro visible (viewport): tamaño FIJO en px (no flex-1/vh/zoom) para
// que el alto sea siempre determinista — un alto basado solo en `flex-1`
// podía colapsar a algo muy pequeño según el layout ambiente. ~30 celdas a
// la celda por defecto (18px). El ancho se adapta hacia abajo si la
// pantalla es más estrecha que el recuadro.
const RECUADRO_PX = 540;

interface CellRect {
  positionX: number;
  positionY: number;
  width: number;
  height: number;
}

interface ContentBBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

// Bounding box (en celdas) de todo el contenido de una sala (mesas +
// objetos decorativos). `null` si la sala está vacía → se usa el zoom por
// defecto en vez de intentar encuadrar la nada.
function computeContentBBox(
  tables: CellRect[],
  decorativeObjects: CellRect[],
): ContentBBox | null {
  const items = [...tables, ...decorativeObjects];
  if (items.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const item of items) {
    minX = Math.min(minX, item.positionX);
    minY = Math.min(minY, item.positionY);
    maxX = Math.max(maxX, item.positionX + (item.width || 1));
    maxY = Math.max(maxY, item.positionY + (item.height || 1));
  }
  return { minX, minY, maxX, maxY };
}

function clampNum(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// Cuántas líneas de texto (de arriba a abajo, por orden de prioridad)
// caben en `boxHeightPx` sin cortarse a la mitad — o cabe la línea entera
// o no se pinta. `fontSizes` es el tamaño (px) de cada línea candidata.
function countFittingLines(boxHeightPx: number, fontSizes: number[]): number {
  const lineHeightFactor = 1.15;
  let used = 0;
  let count = 0;
  for (const fontPx of fontSizes) {
    const lineH = fontPx * lineHeightFactor;
    if (used + lineH > boxHeightPx + 0.5) break;
    used += lineH;
    count++;
  }
  return count;
}

// El NOMBRE de la mesa NUNCA se recorta con "…" en condiciones normales:
// en vez de truncar a una línea, se envuelve en como mucho 2 líneas
// (`line-clamp-2` + `break-words` en el render). Aquí solo se decide el
// tamaño de fuente: se busca, bajando de 1 en 1 desde `idealFontPx` sin
// pasar de `minFontPx` (el mismo clamp que ya se usaba), el mayor tamaño
// con el que el nombre COMPLETO quepa en 2 líneas envueltas — estimando
// caracteres por línea con un ancho medio de carácter de ~0.5×fontPx
// (razonable para la tipografía fina/sin negrita de este lienzo). Si ni al
// tamaño mínimo cabe en 2 líneas, se usa el mínimo igualmente: el
// `line-clamp-2` del render actúa como último recurso con "…" al final de
// la 2ª línea, no como comportamiento normal.
function fitNameToTwoLines(
  name: string,
  boxWidthPx: number,
  idealFontPx: number,
  minFontPx: number,
): { fontPx: number; lines: number } {
  const charsPerLine = (fontPx: number) =>
    Math.max(1, Math.floor(boxWidthPx / (fontPx * 0.5)));
  let fontPx = idealFontPx;
  while (fontPx > minFontPx && name.length > charsPerLine(fontPx) * 2) {
    fontPx -= 1;
  }
  const lines = name.length > charsPerLine(fontPx) ? 2 : 1;
  return { fontPx, lines };
}

// Configuración de orígenes de reservas
const RESERVATION_SOURCES: Record<
  string,
  { icon: React.ElementType; label: string; color: string; bgColor: string }
> = {
  dashboard: {
    icon: Edit3,
    label: "Dashboard",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  widget: {
    icon: Smartphone,
    label: "Widget",
    color: "text-purple-600",
    bgColor: "bg-purple-100",
  },
  phone: {
    icon: Phone,
    label: "Teléfono",
    color: "text-green-600",
    bgColor: "bg-green-100",
  },
  "phone-ai": {
    icon: Bot,
    label: "IA Teléfono",
    color: "text-orange-600",
    bgColor: "bg-orange-100",
  },
  "whatsapp-ai": {
    icon: MessageSquare,
    label: "IA WhatsApp",
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
  },
  "instagram-ai": {
    icon: MessageSquare,
    label: "IA Instagram",
    color: "text-pink-600",
    bgColor: "bg-pink-100",
  },
  "messenger-ai": {
    icon: MessageSquare,
    label: "IA Messenger",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  mobile_app: {
    icon: Smartphone,
    label: "App",
    color: "text-teal-600",
    bgColor: "bg-teal-100",
  },
  "walk-in": {
    icon: UserPlus,
    label: "Walk-in",
    color: "text-amber-600",
    bgColor: "bg-amber-100",
  },
  web: {
    icon: Globe,
    label: "Web",
    color: "text-cyan-600",
    bgColor: "bg-cyan-100",
  },
};

interface TableCombinationTable {
  id: string;
  name: string;
  room?: { id: string; name: string };
}

interface TableCombination {
  id: string;
  name: string;
  minCapacity: number;
  maxCapacity: number;
  tables: TableCombinationTable[];
}

interface Reservation {
  id: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  source: string;
  notes: string | null;
  internalNotes: string | null;
  customerId: string;
  customerNoShowCount?: number; // histórico de no-shows del cliente (aviso ⚠️)
  // Nombre con el que el cliente HIZO la reserva ("a nombre de"), tal como lo
  // tecleó en el widget/app. `bookingNameDiffers` lo calcula el backend: true
  // cuando no cuadra con el nombre de la ficha (persona/empresa distinta) y
  // entonces se pinta el chip "a nombre de …" junto al nombre de la ficha.
  bookingName?: string | null;
  bookingNameDiffers?: boolean;
  // Nº de reservas COMPLETADAS del cliente (histórico): "(9)" junto al nombre.
  customerCompletedCount?: number;
  // Solicitud de cambio del cliente pendiente de aprobar/rechazar (JSON de
  // Reservation.pendingChangeRequest). Solo los valores que pidió cambiar.
  pendingChangeRequest?: {
    date?: string;
    time?: string;
    partySize?: number;
    requestedAt?: string;
    changes?: string[];
  } | null;
  tableId: string | null;
  tableCombinationId: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  cancelledAt: string | null;
  createdAt: string;
  customer: Customer;
  table: Table | null;
  tableCombination: {
    id: string;
    name: string;
    tables: {
      id: string;
      table: {
        id: string;
        name: string;
      };
    }[];
  } | null;
  menuSelections?: {
    menuId: string;
    quantity: number;
    menu: { name: string };
  }[];
}

// Recuento de menús del día seleccionado (GET /menus/counts?date=), pintado
// como chip junto a "X reservas · Y pax". Solo cuenta reservas activas
// (lo calcula el backend); si no hay ninguno, el chip no se muestra.
interface MenuCounts {
  total: number;
  byMenu: { menuId: string; name: string; quantity: number }[];
  // Desglose por turno (el chip de cada cabecera muestra el suyo, no el día entero)
  byShift?: {
    comida: { total: number; byMenu: MenuCounts["byMenu"] };
    cena: { total: number; byMenu: MenuCounts["byMenu"] };
  };
}

// Menú vigente para una fecha (+comensales) concreta, tal como lo devuelve
// GET /menus?date=&partySize= (filtrado en servidor, ya excluye los menús
// cuyo `minPartySize` no cubre la reserva). `scope: "all_guests"` fuerza
// cantidad = comensales (checkbox único); `"per_guest"` permite un stepper
// 0..comensales. Mismo criterio que `MenuOption` del widget (apps/widget).
interface ReservationMenuOption {
  id: string;
  name: string;
  price: number | null;
  scope: "per_guest" | "all_guests";
  minPartySize?: number;
}

interface ReservationStatusConfig {
  id: string;
  code: string;
  name: string;
  color: string;
  bgColor: string;
  textColor: string;
  icon: string | null;
  sortOrder: number;
  isDefault: boolean;
  isFinal: boolean;
}

type TimeSlot = "lunch" | "dinner" | "all";

type SortOption = "default" | "recent" | "time" | "partySize";

const SORT_OPTIONS: { key: SortOption; label: string }[] = [
  { key: "default", label: "Predeterminado" },
  { key: "recent", label: "Más recientes" },
  { key: "time", label: "Por hora" },
  { key: "partySize", label: "Por nº de personas" },
];

// Icon mapping for dynamic statuses
const ICON_MAP: Record<string, React.ElementType> = {
  Clock,
  Check,
  CheckCircle,
  LogIn,
  UserCheck,
  X,
  XCircle,
  AlertCircle,
};

// Fallback STATUS_CONFIG (used while loading from backend, or for unknown status codes).
// Valores en HEX (coherentes con `colorProps`/`statusBadgeColorProps`) porque `ReservationStatus.bgColor`
// y `.textColor` en BD están migrando de clases Tailwind a HEX — este fallback debe hablar el mismo idioma
// que el destino final. Los hex son la equivalencia aproximada de la paleta Tailwind usada antes.
const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; icon: React.ElementType }
> = {
  pending: {
    label: "Pendiente",
    color: "#A16207", // antes: text-yellow-700
    bgColor: "#FEF9C3", // antes: bg-yellow-100
    icon: Clock,
  },
  confirmed: {
    label: "Confirmada",
    color: "#1D4ED8", // antes: text-blue-700
    bgColor: "#DBEAFE", // antes: bg-blue-100
    icon: Check,
  },
  double_confirmed: {
    label: "Reconfirmada",
    color: "#15803D", // antes: text-green-700
    bgColor: "#DCFCE7", // antes: bg-green-100
    icon: CheckCircle,
  },
  arrived: {
    label: "Llegó",
    color: "#7E22CE", // antes: text-purple-700
    bgColor: "#F3E8FF", // antes: bg-purple-100
    icon: LogIn,
  },
  seated: {
    label: "Sentado",
    color: "#4338CA", // antes: text-indigo-700
    bgColor: "#E0E7FF", // antes: bg-indigo-100
    icon: UserCheck,
  },
  completed: {
    label: "Completada",
    color: "#047857", // antes: text-emerald-700
    bgColor: "#D1FAE5", // antes: bg-emerald-100
    icon: CheckCircle,
  },
  cancelled: {
    label: "Cancelada",
    color: "#B91C1C", // antes: text-red-700
    bgColor: "#FEE2E2", // antes: bg-red-100
    icon: X,
  },
  cancelled_by_restaurant: {
    label: "Cancelado por restaurante",
    color: "#BE123C", // antes: text-rose-700
    bgColor: "#FFE4E6", // antes: bg-rose-100
    icon: X,
  },
  no_show: {
    label: "No Show",
    color: "#374151", // antes: text-gray-700
    bgColor: "#E5E7EB", // antes: bg-gray-200
    icon: XCircle,
  },
};

// Texto comparable para el buscador: sin acentos, minúsculas, espacios
// simples. "gonzalez" encuentra a "González".
const normalizeSearchText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// Chip "a nombre de …": el cliente reservó con un nombre que NO cuadra con el
// de su ficha (el emparejamiento por email/teléfono reutiliza la ficha sin
// renombrarla). El backend decide cuándo difiere (`bookingNameDiffers`); aquí
// solo se pinta. Ej.: ficha "JAVIER TREJO", reserva "Trabajadores LA ROCA".
function BookingNameChip({
  reservation,
}: {
  reservation: Pick<Reservation, "bookingName" | "bookingNameDiffers" | "customer">;
}) {
  if (!reservation.bookingNameDiffers || !reservation.bookingName) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 max-w-[14rem] truncate"
      title={`Reserva hecha a nombre de "${reservation.bookingName}" (ficha del cliente: ${reservation.customer.firstName} ${reservation.customer.lastName})`}
    >
      a nombre de {reservation.bookingName}
    </span>
  );
}

/**
 * Aviso ⚠️ junto al nombre del cliente cuando tiene no-shows en su historial,
 * para que el staff pueda pedir confirmación o depósito.
 */
function NoShowBadge({ count }: { count?: number }) {
  if (!count || count <= 0) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap"
      title={`Este cliente tiene ${count} no-show${count > 1 ? "s" : ""} en su historial`}
    >
      <AlertCircle className="w-3 h-3" />
      {count} no-show{count > 1 ? "s" : ""}
    </span>
  );
}

/**
 * Chip "🍽️ X menús" junto a los chips de "X reservas · Y pax" del día. Solo
 * se pinta si hay recuento (>0); al pasar el ratón (o tocar en móvil) se
 * despliega el desglose por menú ("14× Menú del día · 6× Degustación").
 * No es exclusivo de un turno: `counts` es del DÍA completo, así que se
 * repite igual en las 3 cabeceras (Comida / Cena / vista normal).
 */
function MenuCountChip({ counts }: { counts: MenuCounts | null }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  if (!counts || counts.total <= 0) return null;
  return (
    <span
      className="relative inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-2.5 py-1 font-medium text-orange-800 cursor-default"
      onMouseEnter={() => setShowBreakdown(true)}
      onMouseLeave={() => setShowBreakdown(false)}
      onClick={() => setShowBreakdown((v) => !v)}
    >
      <UtensilsCrossed className="h-3.5 w-3.5" />
      {counts.total} menús
      {showBreakdown && counts.byMenu.length > 0 && (
        <div className="absolute z-20 top-full left-0 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-2 text-xs font-normal normal-case text-gray-700 shadow-lg">
          {counts.byMenu.map((mc) => (
            <div
              key={mc.menuId}
              className="flex items-center justify-between gap-2 py-0.5"
            >
              <span className="truncate">{mc.name}</span>
              <span className="font-semibold whitespace-nowrap">
                {mc.quantity}×
              </span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

/**
 * Sección plegable "Menú" en los modales de Nueva/Editar reserva: lista
 * compacta de los menús vigentes ese día (`GET /menus?date=&partySize=`,
 * filtrado en servidor) con stepper de cantidad (`scope: "per_guest"`) o
 * checkbox "Toda la mesa" (`scope: "all_guests"`, cantidad = comensales) —
 * mismo patrón que el paso "menu" del widget. No pinta nada si ese día no
 * tiene menús (`menus` vacío tras cargar): la sección es invisible, no solo
 * colapsada, para no ensanchar el modal en el caso común (sin menús).
 */
function ReservationMenuSection({
  menus,
  loading,
  open,
  onToggleOpen,
  quantities,
  onIncrement,
  onDecrement,
  onSetAllGuests,
  partySize,
}: {
  menus: ReservationMenuOption[];
  loading: boolean;
  open: boolean;
  onToggleOpen: () => void;
  quantities: Record<string, number>;
  onIncrement: (menuId: string) => void;
  onDecrement: (menuId: string) => void;
  onSetAllGuests: (menuId: string, checked: boolean) => void;
  partySize: number;
}) {
  if (!loading && menus.length === 0) return null;

  const total = Object.values(quantities).reduce((sum, q) => sum + q, 0);
  const exceeds = total > partySize;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggleOpen}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <UtensilsCrossed className="w-4 h-4 text-primary-600" />
          Menú
          {total > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
              {total}
            </span>
          )}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="p-3 space-y-2.5 border-t border-gray-200">
          {loading ? (
            <div className="flex items-center justify-center py-3">
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            menus.map((menu) => {
              const quantity = quantities[menu.id] || 0;
              return (
                <div
                  key={menu.id}
                  className="flex items-center justify-between gap-3 py-1"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {menu.name}
                    </p>
                    {menu.price !== null && (
                      <p className="text-xs text-gray-500">
                        {menu.price.toFixed(2)} €
                      </p>
                    )}
                  </div>
                  {menu.scope === "all_guests" ? (
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={quantity === partySize && quantity > 0}
                        onChange={(e) =>
                          onSetAllGuests(menu.id, e.target.checked)
                        }
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Toda la mesa
                    </label>
                  ) : (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => onDecrement(menu.id)}
                        disabled={quantity <= 0}
                        className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5 text-gray-700" />
                      </button>
                      <span className="w-5 text-center text-sm font-semibold text-gray-900">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => onIncrement(menu.id)}
                        disabled={total >= partySize}
                        className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5 text-gray-700" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}

          {exceeds && (
            <div className="p-2 bg-red-50 text-red-700 text-xs rounded-lg">
              El total de menús ({total}) supera el número de comensales (
              {partySize}).
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Viewport de una superficie de mapa: tamaño de celda dinámico (zoom) +
 * auto-encuadre (fit-to-content) + pan (botón central del ratón, o
 * izquierdo sobre el fondo si `panLeftButtonEnabled`). El scroll nativo
 * (barras/trackpad) ya funciona solo con `overflow-auto` en el contenedor;
 * el pan añade además "arrastrar para desplazar". En el mapa en vivo el
 * botón izquierdo ya está ocupado por el drag de mesas, así que ahí el pan
 * se queda solo en el botón central; en los 3 modales "Seleccionar Mesas"
 * (sin drag de mesas, solo clic) se habilita también el botón izquierdo
 * sobre el fondo — cada mesa hace `stopPropagation` en su `onMouseDown`
 * para que un clic sobre ella nunca arranque un pan ni le robe el click de
 * selección. Mismo patrón que TableMap.tsx.
 */
// Estructura persistida en localStorage por superficie+sala (ver
// `storageKey`, formato `rm-map-view:<superficie>:<roomId>`).
interface SavedMapView {
  cellPx: number;
  scrollLeft: number;
  scrollTop: number;
}

function useMapViewport({
  totalCells,
  recuadroPx,
  defaultCellPx = DEFAULT_CELL_PX,
  minCellPx = MIN_CELL_PX,
  maxCellPx = MAX_CELL_PX,
  marginCells = FIT_MARGIN_CELLS,
  storageKey = null,
  panTouchEnabled = true,
  panLeftButtonEnabled = false,
}: {
  totalCells: number;
  recuadroPx: number;
  defaultCellPx?: number;
  minCellPx?: number;
  maxCellPx?: number;
  marginCells?: number;
  // Clave de localStorage (`rm-map-view:<superficie>:<roomId>`) para
  // persistir/restaurar cellPx+scroll. `null` = no persistir (p.ej. sin
  // sala seleccionada todavía).
  storageKey?: string | null;
  // Pan por arrastre táctil (además del scroll nativo, que SIEMPRE
  // funciona solo con `overflow-auto`, con o sin esto). Se desactiva en
  // superficies editables por touch (mesas arrastrables) para no arriesgar
  // conflicto con ese drag — ahí el pan de fondo se queda solo con scroll
  // nativo, como pide el criterio "en modo edición usa scroll nativo".
  panTouchEnabled?: boolean;
  // Pan por arrastre con el botón IZQUIERDO del ratón (además del botón
  // central, siempre activo). Solo seguro en superficies sin drag de mesas
  // propio (los 3 modales "Seleccionar Mesas": ahí el botón izquierdo está
  // libre salvo sobre una mesa, que hace `stopPropagation` en su
  // `onMouseDown` para no arrancar el pan ni perder el click de selección).
  // Desactivado por defecto (mapa en vivo, donde el botón izquierdo ya lo
  // usa el drag de mesas).
  panLeftButtonEnabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellPx, setCellPxRaw] = useState(defaultCellPx);
  // Ref "espejo" del cellPx actual: los closures de guardado (creados una
  // vez por `storageKey`, no por cada cambio de zoom) necesitan leer el
  // valor MÁS RECIENTE en el momento de guardar, no el que tenían cuando
  // se crearon. Se actualiza en cada render (patrón habitual de ref
  // "última versión conocida", no dispara re-render).
  const cellPxRef = useRef(cellPx);
  cellPxRef.current = cellPx;

  const clampCellPx = useCallback(
    (v: number) => clampNum(v, minCellPx, maxCellPx),
    [minCellPx, maxCellPx],
  );

  const zoomIn = useCallback(() => {
    setCellPxRaw((v) => clampCellPx(v * ZOOM_STEP));
  }, [clampCellPx]);

  const zoomOut = useCallback(() => {
    setCellPxRaw((v) => clampCellPx(v / ZOOM_STEP));
  }, [clampCellPx]);

  // Guardado con debounce (400ms) de la vista actual (cellPx + scroll) bajo
  // `storageKey`. Se dispara en cada cambio de `cellPx` (zoom/fit/restore)
  // y en cada evento `scroll` nativo del contenedor (pan con ratón/touch,
  // scroll con rueda/barras). `localStorage` puede fallar (modo privado,
  // cuota) — nunca debe romper el mapa, solo no persiste.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = useCallback(() => {
    if (!storageKey) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      try {
        const data: SavedMapView = {
          cellPx: cellPxRef.current,
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
        };
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch {
        // localStorage no disponible (privado/cuota) — no persiste, no rompe el mapa
      }
    }, 400);
  }, [storageKey]);

  useEffect(() => {
    scheduleSave();
  }, [cellPx, scheduleSave]);

  const onScroll = useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  // Auto-encuadre: calcula `cellPx` a partir del bounding box del contenido
  // (en celdas) y desplaza el scroll para dejarlo visible. Usa el
  // ancho/alto REALES del contenedor (`clientWidth`/`clientHeight`), NO
  // `recuadroPx` a pelo — en viewports estrechos (móvil) el recuadro real
  // es más estrecho que `recuadroPx` (que ya solo actúa como tope máximo
  // vía `maxWidth`), así que dividir por `recuadroPx` ahí sobrestimaba el
  // espacio disponible y dejaba el lienzo con muy poca área realmente
  // visible ("se ve pequeño"). Con fallback a `recuadroPx` solo si el
  // contenedor aún no ha montado (medida 0). El `rAF` es necesario porque
  // el nuevo tamaño del lienzo (ligado a `cellPx`) tarda un ciclo de
  // render + layout en aplicarse antes de que scrollLeft/scrollTop tengan
  // efecto real.
  const fitToContent = useCallback(
    (bbox: ContentBBox | null) => {
      const viewportWidthPx = containerRef.current?.clientWidth || recuadroPx;
      const viewportHeightPx =
        containerRef.current?.clientHeight || recuadroPx;
      if (!bbox) {
        setCellPxRaw(defaultCellPx);
        requestAnimationFrame(() => {
          const el = containerRef.current;
          if (el) {
            el.scrollLeft = 0;
            el.scrollTop = 0;
          }
        });
        return;
      }
      const minX = Math.max(0, bbox.minX - marginCells);
      const minY = Math.max(0, bbox.minY - marginCells);
      const maxX = Math.min(totalCells, bbox.maxX + marginCells);
      const maxY = Math.min(totalCells, bbox.maxY + marginCells);
      const widthCells = Math.max(1, maxX - minX);
      const heightCells = Math.max(1, maxY - minY);
      const next = clampCellPx(
        Math.min(viewportWidthPx / widthCells, viewportHeightPx / heightCells),
      );
      setCellPxRaw(next);
      requestAnimationFrame(() => {
        const el = containerRef.current;
        if (!el) return;
        el.scrollLeft = minX * next;
        el.scrollTop = minY * next;
      });
    },
    [clampCellPx, defaultCellPx, marginCells, recuadroPx, totalCells],
  );

  // Al cambiar de sala: restaura la vista guardada (cellPx + scroll) si
  // existe una para `storageKey`; si no hay nada guardado (o falla el
  // parseo/localStorage), cae al auto-encuadre de siempre.
  const restoreOrFit = useCallback(
    (bbox: ContentBBox | null) => {
      if (storageKey) {
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) {
            const saved = JSON.parse(raw) as Partial<SavedMapView>;
            if (
              typeof saved.cellPx === "number" &&
              typeof saved.scrollLeft === "number" &&
              typeof saved.scrollTop === "number"
            ) {
              const next = clampCellPx(saved.cellPx);
              setCellPxRaw(next);
              requestAnimationFrame(() => {
                const el = containerRef.current;
                if (!el) return;
                el.scrollLeft = saved.scrollLeft as number;
                el.scrollTop = saved.scrollTop as number;
              });
              return;
            }
          }
        } catch {
          // JSON corrupto / localStorage no disponible — se ignora y se hace fit
        }
      }
      fitToContent(bbox);
    },
    [storageKey, clampCellPx, fitToContent],
  );

  // Pan con botón central del ratón (desktop, SIEMPRE) o con el botón
  // izquierdo cuando `panLeftButtonEnabled` (solo modales de solo lectura)
  // — scroll nativo (barras/trackpad/rueda) siempre funciona vía
  // `overflow-auto`; esto añade además "arrastrar para desplazar" sin
  // interferir con el drag de mesas/objetos (mapa en vivo, botón
  // izquierdo) ni con el clic para seleccionar mesas (modales: cada mesa
  // hace `stopPropagation` en su propio `onMouseDown`).
  const panStateRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const isMiddleButton = e.button === 1;
      const isLeftButtonOnBackground =
        panLeftButtonEnabled && e.button === 0;
      if (!isMiddleButton && !isLeftButtonOnBackground) return;
      const container = containerRef.current;
      if (!container) return;
      e.preventDefault();
      panStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      };
      setIsPanning(true);
    },
    [panLeftButtonEnabled],
  );

  useEffect(() => {
    if (!isPanning) return;
    const handleMove = (e: MouseEvent) => {
      const state = panStateRef.current;
      const container = containerRef.current;
      if (!state || !container) return;
      container.scrollLeft = state.scrollLeft - (e.clientX - state.startX);
      container.scrollTop = state.scrollTop - (e.clientY - state.startY);
    };
    const handleUp = () => {
      panStateRef.current = null;
      setIsPanning(false);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isPanning]);

  // Pan táctil (opcional, `panTouchEnabled`): el scroll nativo por
  // arrastre de un dedo YA funciona en cualquier `overflow-auto` sin nada
  // de esto — esto es solo para las superficies de solo lectura donde
  // queremos control total del gesto (evita que el scroll nativo y este
  // pan compitan). Umbral de 6px antes de considerar que es un arrastre
  // (no un tap) para no robarle el toque a los clics de seleccionar/abrir
  // mesa. NO se activa cuando el modo edición está encendido (mapa en
  // vivo) para que el único gesto de arrastre posible sea el de mesas, sin
  // ambigüedad.
  const touchStateRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    engaged: boolean;
  } | null>(null);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!panTouchEnabled) return;
      const container = containerRef.current;
      if (!container || e.touches.length !== 1) return;
      const touch = e.touches[0];
      touchStateRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
        engaged: false,
      };
    },
    [panTouchEnabled],
  );

  useEffect(() => {
    if (!panTouchEnabled) return;
    const handleTouchMove = (e: TouchEvent) => {
      const state = touchStateRef.current;
      const container = containerRef.current;
      if (!state || !container || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      if (!state.engaged) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return; // aún podría ser un tap
        state.engaged = true;
      }
      e.preventDefault(); // ya lo llevamos nosotros: evita que el scroll nativo compita
      container.scrollLeft = state.scrollLeft - dx;
      container.scrollTop = state.scrollTop - dy;
    };
    const handleTouchEnd = () => {
      touchStateRef.current = null;
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [panTouchEnabled]);

  return {
    containerRef,
    cellPx,
    zoomIn,
    zoomOut,
    fitToContent,
    restoreOrFit,
    onScroll,
    isPanning,
    onMouseDown,
    onTouchStart,
  };
}

// Botonera flotante de zoom (+/-/ajustar), reutilizada en las 4 superficies
// de mapa (mapa en vivo + 3 modales de seleccionar mesa).
function ZoomControls({
  onZoomIn,
  onZoomOut,
  onFit,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
}) {
  // w-11 h-11 = 44px: área táctil mínima recomendada (Apple HIG), holgada
  // también en desktop con el ratón.
  const btnClass =
    "w-11 h-11 flex items-center justify-center bg-white border border-gray-300 rounded-lg shadow-sm text-gray-600 hover:bg-gray-50 active:bg-gray-100 hover:text-gray-900";
  return (
    // z-40: por encima de las mesas del mapa (hover z-10) pero por DEBAJO de
    // cualquier modal (z-50+). Con z-[70] las lupas asomaban sobre los
    // modales de crear/editar reserva.
    <div className="absolute top-2 right-2 z-40 flex flex-col gap-1.5">
      <button
        type="button"
        onClick={onZoomIn}
        className={btnClass}
        title="Acercar"
      >
        <ZoomIn className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onZoomOut}
        className={btnClass}
        title="Alejar"
      >
        <ZoomOut className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onFit}
        className={btnClass}
        title="Ajustar al contenido"
      >
        <Maximize2 className="w-5 h-5" />
      </button>
    </div>
  );
}

export default function Reservations() {
  // Get current location from context
  const { currentLocationId, currentLocation, locationVersion } =
    useLocation();
  // Confirmación/aviso de mesa bloqueada (bloqueo SOFT para staff): nunca
  // window.confirm/alert, siempre estos hooks globales.
  const { confirm } = useConfirm();
  const { showToast: showGlobalToast } = useGlobalToast();
  // FASE 3c: gate del botón "Eliminar" reserva (único punto gateado de la
  // página; el resto de acciones no necesitan permiso propio).
  const { can } = usePermissions();
  const canDeleteReservation = can("reservas:borrar");
  const canEditCustomers = can("clientes:gestionar");
  // Ficha del cliente abierta desde el detalle de la reserva (formulario
  // compartido con la página de Clientes). null = cerrado.
  const [editingCustomer, setEditingCustomer] = useState<CustomerRecord | null>(
    null,
  );

  // Read date from URL query params
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // "Iniciar conversación por WhatsApp" desde el detalle de la reserva
  // (12-sep-2026): envía la plantilla de apertura desde el número de la sede
  // y abre la conversación en el inbox. Solo sedes con WhatsApp IA.
  const [startingWhatsAppChat, setStartingWhatsAppChat] = useState(false);
  const startWhatsAppConversation = async (customerId: string) => {
    if (startingWhatsAppChat) return;
    setStartingWhatsAppChat(true);
    try {
      const res = await api.post("/whatsapp-conversations/start", {
        locationId: currentLocationId,
        customerId,
      });
      const conversationId = res.data?.data?.conversationId as string | undefined;
      showGlobalToast(res.data?.message || "Mensaje de inicio enviado", "success");
      navigate(conversationId ? `/conversations?open=${conversationId}` : "/conversations");
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      showGlobalToast(
        e.response?.data?.message || "No se pudo iniciar la conversación",
        "error",
      );
    } finally {
      setStartingWhatsAppChat(false);
    }
  };

  const [selectedDate, setSelectedDate] = useState(() => {
    // Initialize from URL param if present
    const dateParam = searchParams.get("date");
    if (dateParam) {
      const parsed = parseISO(dateParam);
      if (isValid(parsed)) {
        return parsed;
      }
    }
    return new Date();
  });

  // Effect to update selectedDate when URL param changes
  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      const parsed = parseISO(dateParam);
      if (isValid(parsed)) {
        setSelectedDate(parsed);
        // Clear the param after reading to keep URL clean
        setSearchParams(
          (prev) => {
            prev.delete("date");
            return prev;
          },
          { replace: true },
        );
      }
    }
  }, [searchParams, setSearchParams]);

  const [timeSlot, setTimeSlot] = useState<TimeSlot>(() => {
    // En móvil empezar con "lunch" ya que "all" no está disponible
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      return "lunch";
    }
    return "all";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(() => {
    // Enlaces desde la Home mandan "?status=pending" para abrir la lista
    // ya filtrada (p.ej. el aviso de "reservas pendientes de confirmar").
    return searchParams.get("status");
  });

  // "?status=" se aplica UNA vez y se borra de la URL (mismo patrón que
  // "?date="): si se dejaba, al recargar la página el filtro "resucitaba" y el
  // listado parecía vacío mientras el mapa enseñaba las reservas.
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (!statusParam) return;
    setStatusFilter(statusParam);
    setSearchParams(
      (prev) => {
        prev.delete("status");
        return prev;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [tableCombinations, setTableCombinations] = useState<
    TableCombination[]
  >([]);
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [selectedReservation, setSelectedReservation] =
    useState<Reservation | null>(null);
  const [hoveredReservation, setHoveredReservation] =
    useState<Reservation | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Abre el detalle de una reserva al navegar con "?id=<id>" (enlaces desde
  // la Home: "Próximas reservas de hoy" y "Últimas reservas creadas"). Si ya
  // está en la lista cargada para la fecha/turno actual se usa directamente;
  // si no (p.ej. la reserva es de otro día), se pide por id al backend.
  // Espera a que termine la carga en curso (`loading`) para no lanzar una
  // petición de más cuando la reserva sí va a llegar en la lista del día.
  useEffect(() => {
    const idParam = searchParams.get("id");
    if (!idParam || loading) return;

    const clearIdParam = () => {
      setSearchParams(
        (prev) => {
          prev.delete("id");
          return prev;
        },
        { replace: true },
      );
    };

    const inList = reservations.find((r) => r.id === idParam);
    if (inList) {
      setSelectedReservation(inList);
      setShowDetailModal(true);
      clearIdParam();
      return;
    }

    let cancelled = false;
    api
      .get<{ success: boolean; data?: Reservation }>(
        `/reservations/${idParam}`,
      )
      .then((response) => {
        if (cancelled) return;
        if (response.data.data) {
          setSelectedReservation(response.data.data);
          setShowDetailModal(true);
        } else {
          showToast("No se encontró la reserva solicitada", "error");
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching reservation by id:", error);
        showToast("No se pudo abrir la reserva solicitada", "error");
      })
      .finally(() => {
        if (!cancelled) clearIdParam();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, reservations, loading]);

  const [showNewModal, setShowNewModal] = useState(false);
  // Giro del botón "Actualizar" (refresco manual sin spinner de página)
  const [refreshing, setRefreshing] = useState(false);
  // Ficha 360 del cliente (estadísticas, historial…) abierta desde una tarjeta
  // del listado o desde el detalle de la reserva.
  const [viewCustomerId, setViewCustomerId] = useState<string | null>(null);
  // Candado temporal de reservas nuevas (ISO o null) + menú de duraciones
  const [pausedUntil, setPausedUntil] = useState<string | null>(null);
  const [showPauseMenu, setShowPauseMenu] = useState(false);
  const [selectedTableForReservation, setSelectedTableForReservation] =
    useState<Table | null>(null);
  // States for manual table selection in new reservation
  const [showNewReservationTableMap, setShowNewReservationTableMap] =
    useState(false);
  const [selectedTablesForNewReservation, setSelectedTablesForNewReservation] =
    useState<string[]>([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const calendarRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  // Declarado aquí (en vez de junto al resto del editor de turnos, más
  // abajo) porque `liveMapViewport` necesita su valor para decidir si
  // activa el pan táctil de fondo.
  const [isMapEditMode, setIsMapEditMode] = useState(false);
  // Viewport (zoom + auto-encuadre + pan): uno para el mapa en vivo (siempre
  // montado) y otro compartido por los 3 modales "Seleccionar Mesas"
  // (showNewReservationTableMap / showTableMapModal / showMoveTableModal),
  // que nunca están abiertos a la vez.
  const liveMapViewport = useMapViewport({
    totalCells: CANVAS_CELLS,
    recuadroPx: RECUADRO_PX,
    storageKey: selectedRoom
      ? `rm-map-view:reservations-live:${selectedRoom}`
      : null,
    // Con el candado desbloqueado (isMapEditMode) las mesas se arrastran
    // por touch — el pan de fondo se queda solo con scroll nativo ahí
    // ("en modo edición usa scroll nativo"). Bloqueado (modo lectura,
    // valor por defecto) sí se activa el pan táctil de fondo.
    panTouchEnabled: !isMapEditMode,
  });
  const modalMapViewport = useMapViewport({
    totalCells: CANVAS_CELLS,
    recuadroPx: RECUADRO_PX,
    // `selectedRoom` (string) en vez de `currentRoom.id` porque este último
    // se deriva más abajo en el componente — son equivalentes.
    storageKey: selectedRoom
      ? `rm-map-view:reservations-modal:${selectedRoom}`
      : null,
    // Los 3 modales son de solo lectura (clic para seleccionar, sin
    // drag) — el pan táctil de fondo siempre es seguro aquí.
    panTouchEnabled: true,
    // Igual que el táctil: sin drag de mesas en los modales, el botón
    // izquierdo sobre el fondo también puede arrastrar para desplazar
    // (cada mesa hace `stopPropagation` en su `onMouseDown` para no
    // arrancar el pan al hacer clic sobre ella).
    panLeftButtonEnabled: true,
  });

  // Mobile view toggle: "list" or "map"
  const [mobileView, setMobileView] = useState<"list" | "map">("list");

  // Table popover state
  const [popoverTable, setPopoverTable] = useState<Table | null>(null);
  const [popoverReservation, setPopoverReservation] =
    useState<Reservation | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [popoverTableReservations, setPopoverTableReservations] = useState<
    Reservation[]
  >([]);

  // Hover on map table → highlight list rows
  const [hoveredTableId, setHoveredTableId] = useState<string | null>(null);

  // Table blocks state
  const [tableBlocks, setTableBlocks] = useState<TableBlock[]>([]);

  // Table capacity overrides state (aforo ajustado por día/turno)
  const [tableCapacityOverrides, setTableCapacityOverrides] = useState<
    TableCapacityOverride[]
  >([]);

  // Recuento de menús del día seleccionado (chip junto a "X reservas · Y pax")
  const [menuCounts, setMenuCounts] = useState<MenuCounts | null>(null);

  // Decorative objects state (loaded from localStorage)
  const [decorativeObjects, setDecorativeObjects] = useState<
    DecorativeObject[]
  >([]);

  // --- Shift layout editor: drag tables on the live map ---
  // (`isMapEditMode` se declaró más arriba, junto a `liveMapViewport`)
  const [shiftPositions, setShiftPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [draggingTableId, setDraggingTableId] = useState<string | null>(null);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    tableX: number;
    tableY: number;
  } | null>(null);
  const [shiftGuides, setShiftGuides] = useState<
    { type: "vertical" | "horizontal"; position: number }[]
  >([]);
  const [snapToGrid, setSnapToGrid] = useState(true);

  // Dynamic statuses from backend
  const [availableStatuses, setAvailableStatuses] = useState<
    ReservationStatusConfig[]
  >([]);

  // Schedule config for time slots
  const [scheduleConfig, setScheduleConfig] = useState<{
    shifts: {
      name: string;
      startTime: string;
      endTime: string;
      enabled: boolean;
    }[];
    slotInterval?: number;
    minAdvanceHours?: number;
    defaultDuration?: number;
    singleTurnPerShift?: boolean;
  } | null>(null);

  // Current time in minutes (updated every minute, used for timeline auto-complete)
  const [nowMinutes, setNowMinutes] = useState<number>(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });
  // Ref to track IDs being auto-completed to avoid duplicate API calls
  const autoCompletingRef = useRef<Set<string>>(new Set());

  // Generate available time slots based on schedule config
  const availableTimeSlots = useMemo(() => {
    if (!scheduleConfig?.shifts) {
      // Default slots if no config
      return [
        "13:00",
        "13:30",
        "14:00",
        "14:30",
        "20:00",
        "20:30",
        "21:00",
        "21:30",
        "22:00",
      ];
    }

    const slots: string[] = [];
    const interval = scheduleConfig.slotInterval || 30;

    // Parse shifts
    let shifts = scheduleConfig.shifts;
    if (typeof shifts === "string") {
      shifts = JSON.parse(shifts);
    }

    // Generate slots for each enabled shift
    shifts
      .filter((shift: { enabled: boolean }) => shift.enabled)
      .forEach((shift: { startTime: string; endTime: string }) => {
        const [startH, startM] = shift.startTime.split(":").map(Number);
        const [endH, endM] = shift.endTime.split(":").map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;

        for (let mins = startMinutes; mins < endMinutes; mins += interval) {
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          const timeStr = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
          if (!slots.includes(timeStr)) {
            slots.push(timeStr);
          }
        }
      });

    // Sort slots
    slots.sort((a, b) => {
      const [aH, aM] = a.split(":").map(Number);
      const [bH, bM] = b.split(":").map(Number);
      return aH * 60 + aM - (bH * 60 + bM);
    });

    return slots.length > 0
      ? slots
      : ["13:00", "13:30", "14:00", "20:00", "20:30", "21:00"];
  }, [scheduleConfig]);

  // Filter time slots: hide past times when selected date is today
  const getFilteredTimeSlots = (selectedDate: string) => {
    const today = format(new Date(), "yyyy-MM-dd");
    if (selectedDate !== today) return availableTimeSlots;

    const now = new Date();
    const minAdvance = scheduleConfig?.minAdvanceHours ?? 1;
    const minAllowed = new Date(now.getTime() + minAdvance * 60 * 60 * 1000);
    const minMinutes = minAllowed.getHours() * 60 + minAllowed.getMinutes();

    return availableTimeSlots.filter((slot) => {
      const [h, m] = slot.split(":").map(Number);
      return h * 60 + m >= minMinutes;
    });
  };

  // Catálogo del selector de prefijo telefónico ("Nueva reserva"), ordenado
  // con el de la sede activa primero (ej: "+52" para una sede en México).
  const orderedCountries = useMemo(
    () =>
      getOrderedCountries(
        defaultCountryCodeForTimezone(currentLocation?.timezone),
      ),
    [currentLocation?.timezone],
  );

  // Form states for new reservation
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    customerFirstName: "",
    customerLastName: "",
    customerPhone: "",
    customerCountryCode: "+34",
    customerEmail: "",
    partySize: 2,
    time: "13:00",
    notes: "",
    source: "dashboard" as string,
    notifyByWhatsApp: false,
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Sección "Menú" del modal Nueva Reserva (ver `ReservationMenuSection`).
  // `newMenus` se recarga vía GET /menus?date=&partySize= al cambiar
  // fecha/comensales mientras el modal está abierto. `newMenuTouched`
  // distingue "el usuario no tocó la sección" de "la tocó y la dejó vacía" —
  // en creación no cambia el payload (solo se envían cantidades >0 de todos
  // modos), pero se mantiene por simetría con el modal de editar.
  const [newMenus, setNewMenus] = useState<ReservationMenuOption[]>([]);
  const [newMenusLoading, setNewMenusLoading] = useState(false);
  const [newMenuSectionOpen, setNewMenuSectionOpen] = useState(false);
  const [newMenuQuantities, setNewMenuQuantities] = useState<
    Record<string, number>
  >({});
  const [newMenuTouched, setNewMenuTouched] = useState(false);

  // Customer search states
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState<
    CustomerSearchResult[]
  >([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  );
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerSearchRef = useRef<HTMLDivElement>(null);

  // Edit reservation states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTableMapModal, setShowTableMapModal] = useState(false);
  const [selectedTablesForManual, setSelectedTablesForManual] = useState<
    string[]
  >([]);
  const [editFormData, setEditFormData] = useState({
    date: "",
    time: "",
    partySize: 2,
    tableId: null as string | null,
    tableCombinationId: null as string | null,
    manualTableIds: [] as string[], // For manual multi-table selection
    notes: "",
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Sección "Menú" del modal Editar Reserva (ver `ReservationMenuSection`).
  // `editMenuQuantities` se precarga con `reservation.menuSelections` al
  // abrir el modal (`openEditModal`); `editMenus` (la lista de menús
  // vigentes) se recarga aparte vía GET /menus?date=&partySize= al cambiar
  // fecha/comensales. `editMenuTouched` es la clave del contrato: si el
  // usuario NO tocó la sección, el PUT no envía `menuSelections` en absoluto
  // (las selecciones existentes no se tocan); si la tocó, se envían las
  // cantidades >0 tal cual (pueden ser ninguna, para vaciar el menú).
  const [editMenus, setEditMenus] = useState<ReservationMenuOption[]>([]);
  const [editMenusLoading, setEditMenusLoading] = useState(false);
  const [editMenuSectionOpen, setEditMenuSectionOpen] = useState(false);
  const [editMenuQuantities, setEditMenuQuantities] = useState<
    Record<string, number>
  >({});
  const [editMenuTouched, setEditMenuTouched] = useState(false);

  // Move table modal state (from detail view)
  const [showMoveTableModal, setShowMoveTableModal] = useState(false);
  const [selectedTablesForMove, setSelectedTablesForMove] = useState<string[]>(
    [],
  );
  // Mesa(s) que la reserva ya tenía asignadas ANTES de abrir la modal de
  // Desplazar. Se usa solo para pintar el estado visual "Actual" en la
  // rejilla; la selección real (lo que se envía al backend) siempre arranca
  // vacía en `selectedTablesForMove` para evitar que "Desplazar" amplíe la
  // reserva a ambas mesas en vez de moverla.
  const [moveOriginalTableIds, setMoveOriginalTableIds] = useState<string[]>(
    [],
  );
  const [moveSubmitting, setMoveSubmitting] = useState(false);

  // Cancel reservation modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  // Solicitud de cambio pendiente (bloque del modal de detalle)
  const [changeRequestReason, setChangeRequestReason] = useState("");
  const [changeRequestAction, setChangeRequestAction] = useState<
    "approve" | "reject" | null
  >(null);
  const [notifyCustomerOnCancel, setNotifyCustomerOnCancel] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type?: "danger" | "warning" | "info";
    onConfirm: () => void;
  } | null>(null);

  // Toast/Alert state
  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    subject: "",
    message: "",
  });
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [emailSending, setEmailSending] = useState(false);

  // Show toast helper
  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "info",
  ) => {
    setToast({ isOpen: true, message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Show confirm dialog helper
  const showConfirm = (options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type?: "danger" | "warning" | "info";
    onConfirm: () => void;
  }) => {
    setConfirmDialog({ isOpen: true, ...options });
  };

  // Fetch schedule config and auto-select time slot based on current time
  useEffect(() => {
    const fetchScheduleAndAutoSelectSlot = async () => {
      try {
        const response = await api.get("/schedule/config");
        if (response.data.success && response.data.config) {
          const config = response.data.config;
          setScheduleConfig(config);

          // Parse shifts from config
          let shifts = config.shifts;
          if (typeof shifts === "string") {
            shifts = JSON.parse(shifts);
          }

          if (Array.isArray(shifts) && shifts.length > 0) {
            const now = new Date();
            const currentHour = now.getHours();
            const currentMinutes = now.getMinutes();
            const currentTimeInMinutes = currentHour * 60 + currentMinutes;

            // Find lunch shift (Comida) and dinner shift (Cena)
            const lunchShift = shifts.find(
              (s: { name: string; enabled: boolean }) =>
                s.name.toLowerCase().includes("comida") && s.enabled,
            );
            const dinnerShift = shifts.find(
              (s: { name: string; enabled: boolean }) =>
                s.name.toLowerCase().includes("cena") && s.enabled,
            );

            if (lunchShift && dinnerShift) {
              // Parse end time of lunch shift
              const [lunchEndHour, lunchEndMinutes] = lunchShift.endTime
                .split(":")
                .map(Number);
              const lunchEndInMinutes = lunchEndHour * 60 + lunchEndMinutes;

              // If current time is past the lunch end time, auto-select dinner
              if (currentTimeInMinutes >= lunchEndInMinutes) {
                setTimeSlot("dinner");
                console.log("Auto-selected dinner slot based on current time");
              } else {
                setTimeSlot("lunch");
                console.log("Auto-selected lunch slot based on current time");
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching schedule config:", error);
      }
    };
    fetchScheduleAndAutoSelectSlot();
  }, []);

  // Update nowMinutes every minute for the timeline auto-complete
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setNowMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Timeline: auto-complete "seated" reservations whose time window has passed (today only)
  useEffect(() => {
    if (!isToday(selectedDate)) return;
    const duration = scheduleConfig?.defaultDuration ?? 90;
    reservations.forEach((r) => {
      if (r.status !== "seated") return;
      if (autoCompletingRef.current.has(r.id)) return;
      const [h, m] = r.time.split(":").map(Number);
      if (nowMinutes >= h * 60 + m + duration) {
        autoCompletingRef.current.add(r.id);
        updateReservationStatus(r.id, "completed").finally(() => {
          autoCompletingRef.current.delete(r.id);
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowMinutes]);

  // Fetch available statuses from backend
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await api.get("/reservations/statuses");
        if (response.data.success) {
          setAvailableStatuses(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  // Helper to get status config (from backend or fallback)
  const getStatusConfig = (statusCode: string) => {
    const dynamicStatus = availableStatuses.find((s) => s.code === statusCode);
    if (dynamicStatus) {
      return {
        label: dynamicStatus.name,
        color: dynamicStatus.textColor,
        bgColor: dynamicStatus.bgColor,
        icon: ICON_MAP[dynamicStatus.icon || ""] || Clock,
      };
    }
    return (
      STATUS_CONFIG[statusCode] || {
        label: statusCode,
        color: "#374151", // antes: text-gray-700
        bgColor: "#F3F4F6", // antes: bg-gray-100
        icon: Clock,
      }
    );
  };

  // Helper to get source config
  const getSourceConfig = (source: string) => {
    return (
      RESERVATION_SOURCES[source] || {
        icon: Edit3,
        label: source,
        color: "text-gray-600",
        bgColor: "bg-gray-100",
      }
    );
  };

  // Helper to get next action (quick button)
  const getNextAction = (
    status: string,
  ): {
    nextStatus: string;
    label: string;
    icon: React.ElementType;
    color: string;
  } | null => {
    const actions: Record<
      string,
      {
        nextStatus: string;
        label: string;
        icon: React.ElementType;
        color: string;
      }
    > = {
      pending: {
        nextStatus: "confirmed",
        label: "Confirmar",
        icon: Check,
        color: "text-green-600",
      },
      confirmed: {
        nextStatus: "seated",
        label: "Sentar",
        icon: UserCheck,
        color: "text-indigo-600",
      },
      arrived: {
        nextStatus: "seated",
        label: "Sentar",
        icon: UserCheck,
        color: "text-indigo-600",
      },
      double_confirmed: {
        nextStatus: "seated",
        label: "Sentar",
        icon: UserCheck,
        color: "text-indigo-600",
      },
      seated: {
        nextStatus: "completed",
        label: "Completar",
        icon: CheckCircle,
        color: "text-emerald-600",
      },
    };
    return actions[status] || null;
  };

  // Search customers with debounce
  useEffect(() => {
    const searchCustomers = async () => {
      if (customerSearch.length < 2) {
        setCustomerResults([]);
        setShowCustomerDropdown(false);
        return;
      }

      setIsSearchingCustomers(true);
      try {
        const response = await api.get("/customers/search", {
          params: { q: customerSearch },
        });
        const results = response.data.data || [];
        setCustomerResults(results);
        // Show dropdown if there are results OR if search looks like a phone number
        const digits = customerSearch.replace(/\D/g, "");
        setShowCustomerDropdown(results.length > 0 || digits.length >= 6);
      } catch (error) {
        console.error("Error searching customers:", error);
      } finally {
        setIsSearchingCustomers(false);
      }
    };

    const debounce = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounce);
  }, [customerSearch]);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerSearchRef.current &&
        !customerSearchRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Select a customer from search results
  const selectCustomer = (customer: CustomerSearchResult) => {
    setSelectedCustomerId(customer.id);
    setFormData((prev) => ({
      ...prev,
      customerFirstName: customer.firstName,
      customerLastName: customer.lastName || "",
      customerPhone: customer.phone,
      customerEmail: customer.email || "",
    }));
    setCustomerSearch(
      `${customer.firstName} ${customer.lastName || ""} - ${customer.phone}`,
    );
    setShowCustomerDropdown(false);
  };

  // Clear selected customer
  const clearSelectedCustomer = () => {
    setSelectedCustomerId(null);
    setCustomerSearch("");
    setFormData((prev) => ({
      ...prev,
      customerFirstName: "",
      customerLastName: "",
      customerPhone: "",
      customerEmail: "",
    }));
  };

  // Check if search text looks like a phone number
  const isPhoneNumber = (text: string): boolean => {
    const digits = text.replace(/\D/g, "");
    return digits.length >= 6; // At least 6 digits to be considered a phone
  };

  // Extract clean phone number from search text
  const extractPhoneNumber = (text: string): string => {
    return text.replace(/\D/g, "");
  };

  // Create new customer with the searched phone number
  const createCustomerWithPhone = () => {
    const phone = extractPhoneNumber(customerSearch);
    setFormData((prev) => ({
      ...prev,
      customerPhone: phone,
      customerFirstName: "",
      customerLastName: "",
      customerEmail: "",
    }));
    setCustomerSearch("");
    setShowCustomerDropdown(false);
    setSelectedCustomerId(null);
    // Focus on first name field after a short delay
    setTimeout(() => {
      const firstNameInput = document.querySelector(
        'input[placeholder="Nombre"]',
      ) as HTMLInputElement;
      if (firstNameInput) firstNameInput.focus();
    }, 100);
  };

  // Reset form when modal opens
  const openNewReservationModal = (table?: Table) => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const filteredSlots = getFilteredTimeSlots(dateStr);
    // Hora por defecto DEL TURNO que se está viendo: con "Cena" seleccionado,
    // la primera hora de la cena (y con "Comida", la primera de la comida).
    // Antes salía siempre la primera hora del día, y al pinchar una mesa del
    // mapa de la cena se registraba sin querer una reserva de mediodía.
    // Mismo criterio comida/cena que el resto de la página (hora < 17).
    const inActiveShift = (slot: string) => {
      const hour = parseInt(slot.split(":")[0], 10);
      if (timeSlot === "dinner") return hour >= 17;
      if (timeSlot === "lunch") return hour < 17;
      return true;
    };
    const defaultTime =
      filteredSlots.find(inActiveShift) ??
      availableTimeSlots.find(inActiveShift) ??
      filteredSlots[0] ??
      availableTimeSlots[0] ??
      "13:00";
    setFormData({
      date: dateStr,
      customerFirstName: "",
      customerLastName: "",
      customerPhone: "",
      customerCountryCode: defaultCountryCodeForTimezone(
        currentLocation?.timezone,
      ),
      customerEmail: "",
      partySize: table ? table.minCapacity : 2,
      time: defaultTime,
      notes: "",
      source: "dashboard",
      notifyByWhatsApp: false,
    });
    setFormError(null);
    setSelectedCustomerId(null);
    setCustomerSearch("");
    setCustomerResults([]);
    setSelectedTableForReservation(table || null);
    // Reset de la sección "Menú" — se recarga sola vía el useEffect de
    // `newMenus` en cuanto `showNewModal`+fecha/comensales estén listos.
    setNewMenus([]);
    setNewMenuQuantities({});
    setNewMenuSectionOpen(false);
    setNewMenuTouched(false);
    setShowNewModal(true);
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Generate calendar days
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const calendarDays = generateCalendarDays();

  // Fetch reservations and rooms when location changes
  useEffect(() => {
    if (currentLocationId) {
      // Clear old data first
      setReservations([]);
      setRooms([]);
      setSelectedRoom(null);
      setTableBlocks([]);
      setTableCapacityOverrides([]);
      setMenuCounts(null);
      fetchReservations();
      fetchRooms();
      fetchTableBlocks();
      fetchTableCapacityOverrides();
      fetchMenuCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, timeSlot, currentLocationId, locationVersion]);

  // Asegurar que siempre haya una sala seleccionada cuando hay salas disponibles
  useEffect(() => {
    if (rooms.length > 0 && !selectedRoom) {
      setSelectedRoom(rooms[0].id);
    }
  }, [rooms, selectedRoom]);

  // Load decorative objects from selected room when room changes
  useEffect(() => {
    if (selectedRoom) {
      const room = rooms.find((r) => r.id === selectedRoom);
      setDecorativeObjects(room?.decorativeObjects || []);
    }
  }, [selectedRoom, rooms]);

  // Al cargar/cambiar de sala: restaura la vista guardada (cellPx+scroll)
  // del mapa en vivo si existe, si no auto-encuadra. Deliberadamente keyed
  // solo por `selectedRoom` (el id, no por `rooms`) para NO
  // re-encuadrar/resetear la vista en cada refresco de datos
  // (reservas/mesas se recargan a menudo) — eso resetearía el zoom/pan del
  // usuario mientras mira el mapa. El botón "Ajustar" de ZoomControls
  // permite re-encuadrar a mano (y actualiza lo guardado).
  useEffect(() => {
    if (!selectedRoom) return;
    const room = rooms.find((r) => r.id === selectedRoom);
    const bbox = computeContentBBox(
      room?.tables ?? [],
      room?.decorativeObjects ?? [],
    );
    liveMapViewport.restoreOrFit(bbox);
  }, [selectedRoom, liveMapViewport.restoreOrFit]);

  // --- Shift layout: load/save temporary positions per date+shift+room ---
  const shiftLayoutKey = useMemo(() => {
    if (!selectedRoom) return null;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return `shift_layout_${selectedRoom}_${dateStr}_${timeSlot}`;
  }, [selectedRoom, selectedDate, timeSlot]);

  // Load shift positions when room/date/shift changes
  useEffect(() => {
    if (!shiftLayoutKey) {
      setShiftPositions({});
      return;
    }
    try {
      const saved = localStorage.getItem(shiftLayoutKey);
      setShiftPositions(saved ? JSON.parse(saved) : {});
    } catch {
      setShiftPositions({});
    }
    setIsMapEditMode(false);
  }, [shiftLayoutKey]);

  // Save shift positions to localStorage
  const saveShiftPositions = useCallback(
    (positions: Record<string, { x: number; y: number }>) => {
      if (!shiftLayoutKey) return;
      if (Object.keys(positions).length === 0) {
        localStorage.removeItem(shiftLayoutKey);
      } else {
        localStorage.setItem(shiftLayoutKey, JSON.stringify(positions));
      }
    },
    [shiftLayoutKey],
  );

  // Get effective table position (shift override or original)
  const getTablePosition = useCallback(
    (table: Table) => {
      const override = shiftPositions[table.id];
      return override
        ? { x: override.x, y: override.y }
        : { x: table.positionX, y: table.positionY };
    },
    [shiftPositions],
  );

  // Drag handlers for shift layout editor
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent, table: Table) => {
      if (!isMapEditMode) return;
      e.preventDefault();
      e.stopPropagation();
      const pos = getTablePosition(table);
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      dragStartRef.current = {
        mouseX: clientX,
        mouseY: clientY,
        tableX: pos.x,
        tableY: pos.y,
      };
      setDraggingTableId(table.id);
    },
    [isMapEditMode, getTablePosition],
  );

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!draggingTableId || !dragStartRef.current || !mapContainerRef.current)
        return;
      e.preventDefault();
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const rect = mapContainerRef.current.getBoundingClientRect();
      const currentRoom = rooms.find((r) => r.id === selectedRoom);
      // Lienzo fijo (ver CANVAS_CELLS) — Room.gridSize ya no se usa para pintar.
      const gridSize = CANVAS_CELLS;
      const cellSize = rect.width / gridSize;
      const dx = (clientX - dragStartRef.current.mouseX) / cellSize;
      const dy = (clientY - dragStartRef.current.mouseY) / cellSize;
      let rawX = Math.max(
        0,
        Math.min(gridSize - 1, dragStartRef.current.tableX + dx),
      );
      let rawY = Math.max(
        0,
        Math.min(gridSize - 1, dragStartRef.current.tableY + dy),
      );
      let newX = snapToGrid ? Math.round(rawX) : rawX;
      let newY = snapToGrid ? Math.round(rawY) : rawY;

      // Smart alignment guides
      const cellPct = 100 / gridSize;
      const threshold = snapToGrid ? 0.8 : 0.4;
      const foundGuides: {
        type: "vertical" | "horizontal";
        position: number;
      }[] = [];
      if (currentRoom) {
        for (const t of currentRoom.tables) {
          if (t.id === draggingTableId) continue;
          const pos = shiftPositions[t.id] ?? {
            x: t.positionX,
            y: t.positionY,
          };
          if (Math.abs(newX - pos.x) < threshold) {
            newX = pos.x;
            foundGuides.push({ type: "vertical", position: pos.x * cellPct });
          }
          if (Math.abs(newY - pos.y) < threshold) {
            newY = pos.y;
            foundGuides.push({ type: "horizontal", position: pos.y * cellPct });
          }
          const myCX = newX + 0.5,
            otherCX = pos.x + t.width / 2;
          if (Math.abs(myCX - otherCX) < threshold) {
            // Sin Math.round: con snap desactivado el centrado entre dos
            // mesas puede caer en una posición fraccionaria (p.ej. una
            // mesa de ancho impar) — redondear aquí la desalineaba del
            // centro real igual que hacía el bug de posiciones enteras.
            newX = otherCX - 0.5;
            foundGuides.push({ type: "vertical", position: otherCX * cellPct });
          }
          const myCY = newY + 0.5,
            otherCY = pos.y + t.height / 2;
          if (Math.abs(myCY - otherCY) < threshold) {
            newY = otherCY - 0.5;
            foundGuides.push({
              type: "horizontal",
              position: otherCY * cellPct,
            });
          }
        }
      }
      setShiftGuides(foundGuides);
      setShiftPositions((prev) => ({
        ...prev,
        [draggingTableId]: { x: newX, y: newY },
      }));
    },
    [draggingTableId, rooms, selectedRoom, shiftPositions, snapToGrid],
  );

  const handleDragEnd = useCallback(() => {
    if (draggingTableId) {
      setShiftPositions((prev) => {
        saveShiftPositions(prev);
        return prev;
      });
    }
    setDraggingTableId(null);
    setShiftGuides([]);
    dragStartRef.current = null;
  }, [draggingTableId, saveShiftPositions]);

  // Attach global move/end listeners when dragging
  useEffect(() => {
    if (!draggingTableId) return;
    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
    window.addEventListener("touchmove", handleDragMove, { passive: false });
    window.addEventListener("touchend", handleDragEnd);
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [draggingTableId, handleDragMove, handleDragEnd]);

  // Reset shift positions for current view
  const resetShiftPositions = useCallback(() => {
    setShiftPositions({});
    if (shiftLayoutKey) localStorage.removeItem(shiftLayoutKey);
  }, [shiftLayoutKey]);

  // `silent = true`: recarga sin encender el spinner de página (lo usan el
  // refresco automático y el botón "Actualizar" para no parpadear la vista).
  const fetchReservations = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      // El backend limita a 50 resultados por defecto si no se manda `limit`
      // (paginación pensada para vistas de rango largo). Aquí queremos SIEMPRE
      // el día completo, así que se pide un límite alto para no cortar en
      // silencio los días con más de 50 cubiertos.
      const params: Record<string, string> = { date: dateStr, limit: "1000" };
      if (timeSlot !== "all") params.timeSlot = timeSlot;

      const response = await api.get("/reservations", { params });
      setReservations(response.data.data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Candado de reservas nuevas: estado actual de la sede al entrar/cambiar.
  useEffect(() => {
    if (!currentLocationId) return;
    api
      .get(`/locations/${currentLocationId}`)
      .then((response) => {
        setPausedUntil(response.data.data?.reservationsPausedUntil ?? null);
      })
      .catch(() => {});
  }, [currentLocationId]);

  const pauseActive = pausedUntil ? new Date(pausedUntil) > new Date() : false;

  const handleSetPause = async (minutes: number | null) => {
    if (!currentLocationId) return;
    setShowPauseMenu(false);
    try {
      const response = await api.patch(
        `/locations/${currentLocationId}/reservations-pause`,
        { minutes },
      );
      setPausedUntil(response.data.data?.reservationsPausedUntil ?? null);
      showToast(
        minutes
          ? `Reservas nuevas pausadas ${minutes} minutos`
          : "Reservas nuevas reactivadas",
        "success",
      );
    } catch {
      showToast("No se pudo cambiar la pausa de reservas", "error");
    }
  };

  // Refresco manual (botón "Actualizar"): recarga silenciosa de reservas y
  // bloqueos, con giro del icono mientras dura.
  const handleManualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([fetchReservations(true), fetchTableBlocks()]);
    } finally {
      setRefreshing(false);
    }
  };

  // Refresco automático: cada 30 s con la pestaña visible, y al volver a la
  // ventana. Silencioso (sin spinner) y en pausa mientras haya un modal
  // abierto, para no pisar lo que se esté editando. Cubre el caso real de
  // mover mesas desde el móvil y mirar el ordenador: antes había que recargar.
  const anyModalOpen =
    showDetailModal ||
    showNewModal ||
    showEditModal ||
    showTableMapModal ||
    showMoveTableModal ||
    showCancelModal ||
    showEmailModal;
  useEffect(() => {
    if (anyModalOpen) return;
    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      fetchReservations(true);
      fetchTableBlocks();
    };
    const interval = setInterval(refreshIfVisible, 30000);
    window.addEventListener("focus", refreshIfVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", refreshIfVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyModalOpen, selectedDate, timeSlot]);

  // Editar ficha por id (botón "Editar" de la ficha 360): mismo formulario,
  // pero el cliente viene de la ficha abierta, no de la reserva seleccionada.
  const openCustomerEditById = async (customerId: string) => {
    try {
      const response = await api.get(`/customers/${customerId}`);
      setEditingCustomer(response.data.data as CustomerRecord);
    } catch (error) {
      console.error("Error loading customer for edit:", error);
      showToast("No se pudo cargar la ficha del cliente", "error");
    }
  };

  // "Editar ficha" desde el detalle de la reserva: se carga la ficha completa
  // por id (el `customer` embebido en la reserva es un recorte) y se abre el
  // mismo formulario que usa la página de Clientes, encima del detalle.
  const openCustomerEdit = async () => {
    if (!selectedReservation) return;
    try {
      const response = await api.get(
        `/customers/${selectedReservation.customerId}`,
      );
      setEditingCustomer(response.data.data as CustomerRecord);
    } catch (error) {
      console.error("Error loading customer for edit:", error);
      showToast("No se pudo cargar la ficha del cliente", "error");
    }
  };

  // Tras guardar la ficha: recargar la reserva abierta (nombre, teléfono y
  // email nuevos en el detalle) y el listado del día.
  const handleCustomerSaved = async () => {
    setEditingCustomer(null);
    showToast("Ficha del cliente actualizada", "success");
    fetchReservations();
    if (!selectedReservation) return;
    try {
      const response = await api.get<{ success: boolean; data?: Reservation }>(
        `/reservations/${selectedReservation.id}`,
      );
      if (response.data.data) setSelectedReservation(response.data.data);
    } catch (error) {
      console.error("Error reloading reservation after customer edit:", error);
    }
  };

  // Fetch table blocks for the selected date
  const fetchTableBlocks = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await api.get(`/table-blocks/date/${dateStr}`);
      setTableBlocks(response.data.blocks || []);
    } catch (error) {
      console.error("Error fetching table blocks:", error);
      setTableBlocks([]);
    }
  }, [selectedDate]);

  // Block a table
  const handleBlockTable = async (tableId: string) => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      // Determine shift based on current timeSlot filter
      const shift =
        timeSlot === "all" ? null : timeSlot === "lunch" ? "comida" : "cena";

      await api.post("/table-blocks", {
        tableId,
        date: dateStr,
        shift,
        // reason is optional, don't send null
      });

      showToast("Mesa bloqueada correctamente", "success");
      fetchTableBlocks();
      setPopoverTable(null);
    } catch (error: any) {
      console.error("Error blocking table:", error);
      showToast(
        error.response?.data?.message || "Error al bloquear la mesa",
        "error",
      );
    }
  };

  // Unblock a table
  const handleUnblockTable = async (_tableId: string, blockId: string) => {
    try {
      await api.delete(`/table-blocks/${blockId}`);
      showToast("Mesa desbloqueada correctamente", "success");
      fetchTableBlocks();
      setPopoverTable(null);
    } catch (error: any) {
      console.error("Error unblocking table:", error);
      showToast(
        error.response?.data?.message || "Error al desbloquear la mesa",
        "error",
      );
    }
  };

  // Fetch table capacity overrides for the selected date
  const fetchTableCapacityOverrides = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await api.get(
        `/table-capacity-overrides/date/${dateStr}`,
      );
      setTableCapacityOverrides(response.data.overrides || []);
    } catch (error) {
      console.error("Error fetching table capacity overrides:", error);
      setTableCapacityOverrides([]);
    }
  }, [selectedDate]);

  // Fetch menu counts for the selected date (chip "🍽️ X menús" junto a los
  // chips del día). Solo cuenta reservas activas (lo calcula el backend); si
  // falla o no hay ninguno, el chip simplemente no se muestra.
  const fetchMenuCounts = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await api.get("/menus/counts", {
        params: { date: dateStr },
      });
      setMenuCounts(response.data?.counts ?? null);
    } catch (error) {
      console.error("Error fetching menu counts:", error);
      setMenuCounts(null);
    }
  }, [selectedDate]);

  // Menús vigentes para la sección "Menú" de los modales Nueva/Editar
  // reserva (GET /menus?date=&partySize=, filtrado en servidor por
  // `minPartySize`). Compartido por los dos useEffect de abajo — cada modal
  // mantiene su propia lista/loading, no hay estado cruzado.
  const fetchReservationMenuOptions = useCallback(
    async (date: string, partySize: number): Promise<ReservationMenuOption[]> => {
      try {
        const response = await api.get("/menus", {
          params: { date, partySize },
        });
        return Array.isArray(response.data?.menus) ? response.data.menus : [];
      } catch (error) {
        console.error("Error fetching reservation menus:", error);
        return [];
      }
    },
    [],
  );

  // Recarga `newMenus` (modal Nueva Reserva) al cambiar fecha/comensales
  // mientras el modal está abierto. No toca `newMenuQuantities` — solo
  // actualiza qué menús se pueden elegir.
  useEffect(() => {
    if (!showNewModal || !formData.date) return;
    let cancelled = false;
    setNewMenusLoading(true);
    fetchReservationMenuOptions(formData.date, formData.partySize).then(
      (menus) => {
        if (cancelled) return;
        setNewMenus(menus);
        setNewMenusLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [
    showNewModal,
    formData.date,
    formData.partySize,
    fetchReservationMenuOptions,
  ]);

  // Igual que el anterior pero para el modal Editar Reserva. Las cantidades
  // preexistentes se precargan en `openEditModal`, no aquí.
  useEffect(() => {
    if (!showEditModal || !editFormData.date) return;
    let cancelled = false;
    setEditMenusLoading(true);
    fetchReservationMenuOptions(
      editFormData.date,
      editFormData.partySize,
    ).then((menus) => {
      if (cancelled) return;
      setEditMenus(menus);
      setEditMenusLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    showEditModal,
    editFormData.date,
    editFormData.partySize,
    fetchReservationMenuOptions,
  ]);

  // Callbacks de la sección "Menú" — factory compartida por los dos modales
  // (Nueva/Editar) para no duplicar el clamp per_guest/all_guests. Cada
  // llamada marca `touched=true`: es la señal de "el usuario SÍ intervino en
  // esta sección" que decide si el PUT de editar envía `menuSelections`.
  const makeMenuHandlers = (
    setQuantities: React.Dispatch<React.SetStateAction<Record<string, number>>>,
    setTouched: React.Dispatch<React.SetStateAction<boolean>>,
    partySize: number,
  ) => ({
    onIncrement: (menuId: string) => {
      setQuantities((prev) => {
        const total = Object.values(prev).reduce((sum, q) => sum + q, 0);
        if (total >= partySize) return prev;
        return { ...prev, [menuId]: (prev[menuId] || 0) + 1 };
      });
      setTouched(true);
    },
    onDecrement: (menuId: string) => {
      setQuantities((prev) => {
        const current = prev[menuId] || 0;
        if (current <= 0) return prev;
        return { ...prev, [menuId]: current - 1 };
      });
      setTouched(true);
    },
    onSetAllGuests: (menuId: string, checked: boolean) => {
      setQuantities((prev) => ({
        ...prev,
        [menuId]: checked ? partySize : 0,
      }));
      setTouched(true);
    },
  });

  const newMenuHandlers = makeMenuHandlers(
    setNewMenuQuantities,
    setNewMenuTouched,
    formData.partySize,
  );
  const editMenuHandlers = makeMenuHandlers(
    setEditMenuQuantities,
    setEditMenuTouched,
    editFormData.partySize,
  );

  // Derive the override shift bucket ("comida" | "cena") from a "HH:MM" time,
  // matching the backend's deriveOverrideShift (hour < 17 → comida).
  const deriveShiftFromTime = (time: string): "comida" | "cena" =>
    parseInt(time.split(":")[0], 10) < 17 ? "comida" : "cena";

  // Set (upsert) a capacity override for a table on the selected date
  const handleSetCapacityOverride = async (
    tableId: string,
    data: {
      minCapacity: number;
      maxCapacity: number;
      shift: "comida" | "cena" | null;
      reason?: string;
    },
  ) => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      await api.post("/table-capacity-overrides", {
        tableId,
        date: dateStr,
        shift: data.shift,
        minCapacity: data.minCapacity,
        maxCapacity: data.maxCapacity,
        reason: data.reason || undefined,
      });

      showToast("Aforo de la mesa ajustado correctamente", "success");

      // Aviso si ya hay una reserva ese día/turno en esa mesa que supera el
      // nuevo máximo (no se bloquea el guardado, solo se informa).
      const table = rooms
        .flatMap((r) => r.tables)
        .find((t) => t.id === tableId);
      const overflowing = reservations.find((r) => {
        if (r.tableId !== tableId) return false;
        if (finalStatusCodes.has(r.status)) return false;
        if (data.shift && deriveShiftFromTime(r.time) !== data.shift)
          return false;
        return r.partySize > data.maxCapacity;
      });
      if (overflowing) {
        showToast(
          `Aviso: la reserva de ${overflowing.customer.firstName} ${overflowing.customer.lastName} (${overflowing.partySize} pax) supera el nuevo aforo de ${table?.name || "la mesa"}`,
          "info",
        );
      }

      fetchTableCapacityOverrides();
      setPopoverTable(null);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message || "Error al ajustar el aforo",
        "error",
      );
    }
  };

  // Remove a capacity override (revert to base capacity)
  const handleRemoveCapacityOverride = async (overrideId: string) => {
    try {
      await api.delete(`/table-capacity-overrides/${overrideId}`);
      showToast("Aforo restablecido correctamente", "success");
      fetchTableCapacityOverrides();
      setPopoverTable(null);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message || "Error al restablecer el aforo",
        "error",
      );
    }
  };

  // Create a walk-in (instant reservation without customer data). El nº de
  // personas lo elige el usuario en el mini-formulario del TablePopover; la
  // hora la pone el backend con la hora real (now).
  const handleWalkIn = async (tableId: string, partySize: number) => {
    try {
      const response = await api.post<ReservationApiResponse>(
        "/reservations/walk-in",
        {
          tableId,
          partySize,
        },
      );

      showToast("Walk-in creado correctamente", "success");
      notifyReservationWarnings(response.data.warnings, "Walk-in creado");
      fetchReservations();
      setPopoverTable(null);
    } catch (error: any) {
      console.error("Error creating walk-in:", error);
      showToast(
        error.response?.data?.message || "Error al crear walk-in",
        "error",
      );
    }
  };

  // Get block for a specific table
  const getTableBlock = (tableId: string): TableBlock | null => {
    // Check for full-day block or shift-specific block
    const currentShift =
      timeSlot === "lunch" ? "comida" : timeSlot === "dinner" ? "cena" : null;

    return (
      tableBlocks.find((block) => {
        if (block.tableId !== tableId) return false;
        // Full day block applies to all shifts
        if (block.shift === null) return true;
        // Shift-specific block only applies to that shift
        if (currentShift && block.shift === currentShift) return true;
        // If viewing "all", show any shift-specific blocks
        if (timeSlot === "all") return true;
        return false;
      }) || null
    );
  };

  // Get block for a specific table at a given reservation time (para los
  // grids de selección manual de NUEVA/EDITAR/MOVER: resuelven el turno de
  // la reserva concreta con `deriveShiftFromTime`, no el filtro global
  // `timeSlot` de la página que usa `getTableBlock`).
  const getTableBlockForTime = (
    tableId: string,
    time?: string | null,
  ): TableBlock | null => {
    const shift = time ? deriveShiftFromTime(time) : null;
    return (
      tableBlocks.find((block) => {
        if (block.tableId !== tableId) return false;
        // Bloqueo de todo el día aplica siempre
        if (block.shift === null) return true;
        // Bloqueo de turno concreto solo aplica si coincide con el turno de la reserva
        return shift !== null && block.shift === shift;
      }) || null
    );
  };

  // Aviso (toast informativo, no de error) cuando la respuesta del backend
  // trae warnings de tipo "table_blocked" o "room_disabled" — cubre el caso
  // en que la reserva termina en una mesa bloqueada, o en el salón de una
  // mesa deshabilitado ese día, por otra vía que no pasó por la confirmación
  // de los grids (p.ej. combinación existente, selector <select>, o llegada
  // directa a la API).
  // Aprobar / rechazar la solicitud de cambio desde el modal de detalle de la
  // reserva (mismos endpoints que el modal de la campanita). Así el enlace
  // "Ver reserva" y el historial de notificaciones también permiten decidir.
  const handleApprovePendingChange = async () => {
    if (!selectedReservation?.pendingChangeRequest) return;
    setChangeRequestAction("approve");
    try {
      const response = await api.post<ReservationApiResponse>(
        `/reservations/${selectedReservation.id}/approve-change`,
      );
      showToast("Cambio aprobado", "success");
      notifyReservationWarnings(response.data.warnings, "Cambio aprobado");
      // Recargar la reserva COMPLETA: al aprobar cambian fecha/hora/personas
      // en el servidor y quedarse con la copia local (solo quitando la
      // solicitud) hacía que "Editar" abriera con los datos viejos.
      try {
        const fresh = await api.get<{ success: boolean; data?: Reservation }>(
          `/reservations/${selectedReservation.id}`,
        );
        setSelectedReservation(
          fresh.data.data ?? {
            ...selectedReservation,
            pendingChangeRequest: null,
          },
        );
      } catch {
        setSelectedReservation({
          ...selectedReservation,
          pendingChangeRequest: null,
        });
      }
      fetchReservations();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string; error?: string } };
      };
      showToast(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          "No se pudo aprobar el cambio",
        "error",
      );
    } finally {
      setChangeRequestAction(null);
    }
  };

  const handleRejectPendingChange = async () => {
    if (!selectedReservation?.pendingChangeRequest) return;
    setChangeRequestAction("reject");
    try {
      await api.post(`/reservations/${selectedReservation.id}/reject-change`, {
        reason: changeRequestReason,
      });
      showToast("Cambio rechazado", "success");
      setChangeRequestReason("");
      setSelectedReservation({
        ...selectedReservation,
        pendingChangeRequest: null,
      });
      fetchReservations();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string; error?: string } };
      };
      showToast(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          "No se pudo rechazar el cambio",
        "error",
      );
    } finally {
      setChangeRequestAction(null);
    }
  };

  const notifyReservationWarnings = (
    warnings: ReservationWarning[] | undefined,
    actionLabel: string,
  ) => {
    (warnings || []).forEach((w) => {
      if (w.code === "table_blocked") {
        const tableName = w.tableName || "seleccionada";
        const reasonSuffix = w.reason ? ` (${w.reason})` : "";
        showGlobalToast(
          `${actionLabel} en la mesa ${tableName}, que está bloqueada${reasonSuffix}.`,
          "info",
        );
      } else if (w.code === "room_disabled") {
        const tableName = w.tableName || "seleccionada";
        const roomName = w.roomName || "de esta mesa";
        showGlobalToast(
          `${actionLabel} en la mesa ${tableName}: el salón ${roomName} está deshabilitado ese día.`,
          "info",
        );
      }
    });
  };

  // Get the effective capacity override for a table, resolved for a shift.
  // If `shift` isn't provided, it's derived from the current timeSlot filter
  // (same convention as getTableBlock). Priority: override específico del
  // turno → override de "todo el día" → ninguno (capacidad base).
  const getTableCapacityOverride = useCallback(
    (
      tableId: string,
      shift?: "comida" | "cena" | null,
    ): TableCapacityOverride | null => {
      const targetShift =
        shift !== undefined
          ? shift
          : timeSlot === "lunch"
            ? "comida"
            : timeSlot === "dinner"
              ? "cena"
              : null;

      const forTable = tableCapacityOverrides.filter(
        (o) => o.tableId === tableId,
      );
      if (forTable.length === 0) return null;

      if (targetShift) {
        const specific = forTable.find((o) => o.shift === targetShift);
        if (specific) return specific;
      }
      const fullDay = forTable.find((o) => o.shift === null);
      if (fullDay) return fullDay;
      // Viendo "Todo el día" sin turno concreto: si no hay override de todo
      // el día, muestra el primero disponible como referencia (igual que
      // getTableBlock con timeSlot === "all").
      if (!targetShift) return forTable[0];
      return null;
    },
    [tableCapacityOverrides, timeSlot],
  );

  const fetchRooms = async () => {
    try {
      const [roomsResponse, combinationsResponse] = await Promise.all([
        // channel=dashboard: salas ocultas para el canal dashboard no se
        // muestran en el mapa ni en el selector de reservas manuales
        api.get("/tables/rooms?channel=dashboard"),
        api.get("/table-combinations"),
      ]);

      const roomsData =
        roomsResponse.data.rooms || roomsResponse.data.data || [];
      setRooms(roomsData);

      // Siempre seleccionar la primera sala si hay salas disponibles
      // y la sala actualmente seleccionada no existe en las nuevas salas
      if (roomsData.length > 0) {
        const currentRoomExists = roomsData.some(
          (r: { id: string }) => r.id === selectedRoom,
        );
        if (!selectedRoom || !currentRoomExists) {
          setSelectedRoom(roomsData[0].id);
        }
      }

      // Load table combinations
      const combosData = combinationsResponse.data || [];
      setTableCombinations(combosData);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  const updateReservationStatus = async (id: string, status: string) => {
    if (status === "cancelled" || status === "cancelled_by_restaurant") {
      // Open dedicated modal to capture reason and notification preference
      setCancelTargetId(id);
      setCancelReason("");
      setNotifyCustomerOnCancel(true);
      setShowCancelModal(true);
      return;
    }
    try {
      await api.patch(`/reservations/${id}/status`, { status });
      fetchReservations();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const confirmCancelReservation = async () => {
    if (!cancelTargetId) return;
    setCancelling(true);
    try {
      await api.patch(`/reservations/${cancelTargetId}/cancel`, {
        reason: cancelReason.trim() || undefined,
        notifyCustomer: notifyCustomerOnCancel,
      });
      const cancelledId = cancelTargetId;
      setShowCancelModal(false);
      setCancelTargetId(null);
      setCancelReason("");
      setNotifyCustomerOnCancel(true);
      if (selectedReservation?.id === cancelledId) {
        setSelectedReservation({
          ...selectedReservation,
          status: "cancelled_by_restaurant",
        });
      }
      fetchReservations();
      showToast("Reserva cancelada correctamente", "success");
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error("Error cancelling reservation:", error);
      showToast(
        err?.response?.data?.message || "Error al cancelar la reserva",
        "error",
      );
    } finally {
      setCancelling(false);
    }
  };

  const closeCancelModal = () => {
    if (cancelling) return;
    setShowCancelModal(false);
    setCancelTargetId(null);
    setCancelReason("");
    setNotifyCustomerOnCancel(true);
    // Revert any optimistic UI updates that may have happened on click
    fetchReservations();
  };

  // Open edit modal with reservation data
  const openEditModal = (overrideReservation?: Reservation) => {
    // La reserva puede venir por parámetro (popup de la mesa): leerla del
    // estado justo tras un setSelectedReservation abría el formulario con los
    // datos de la reserva ANTERIOR (el set aún no se había asentado).
    const source = overrideReservation ?? selectedReservation;
    if (!source) return;
    // Cast to access tableCombinationId if it exists
    const reservation = source as Reservation & {
      tableCombinationId?: string | null;
    };

    // Initialize manualTableIds based on current assignment
    let manualIds: string[] = [];
    if (reservation.tableCombination) {
      manualIds = reservation.tableCombination.tables.map((t) => t.table.id);
    } else if (source.tableId) {
      manualIds = [source.tableId];
    }

    setEditFormData({
      date: source.date.split("T")[0],
      time: source.time.slice(0, 5),
      partySize: source.partySize,
      tableId: source.tableId,
      tableCombinationId: reservation.tableCombinationId || null,
      manualTableIds: manualIds,
      notes: source.notes || "",
    });

    // Precargar la sección "Menú" con las selecciones ACTUALES de la
    // reserva. `editMenus` (la lista de menús vigentes ese día) se recarga
    // aparte vía el useEffect correspondiente; esta precarga es
    // independiente para que las cantidades existentes sobrevivan aunque el
    // fetch tarde o falle. Arranca abierta solo si ya había menú elegido.
    const initialMenuQuantities: Record<string, number> = {};
    (source.menuSelections || []).forEach((ms) => {
      initialMenuQuantities[ms.menuId] = ms.quantity;
    });
    setEditMenus([]);
    setEditMenuQuantities(initialMenuQuantities);
    setEditMenuSectionOpen((source.menuSelections?.length ?? 0) > 0);
    setEditMenuTouched(false);

    setEditError(null);
    setShowEditModal(true);
  };

  // Save edited reservation
  const handleEditReservation = async () => {
    if (!selectedReservation) return;

    setEditSubmitting(true);
    setEditError(null);

    try {
      // Build update payload
      const updatePayload: Record<string, unknown> = {
        date: editFormData.date,
        time: editFormData.time,
        partySize: editFormData.partySize,
        notes: editFormData.notes || null,
      };

      // Handle table assignment
      if (editFormData.manualTableIds.length > 1) {
        // Multiple tables selected manually - send array for backend to create combination
        updatePayload.manualTableIds = editFormData.manualTableIds;
        updatePayload.tableId = editFormData.manualTableIds[0];
        updatePayload.tableCombinationId = null;
      } else if (editFormData.manualTableIds.length === 1) {
        // Single table
        updatePayload.tableId = editFormData.manualTableIds[0];
        updatePayload.tableCombinationId = null;
      } else if (editFormData.tableCombinationId) {
        // Existing combination selected from dropdown
        updatePayload.tableCombinationId = editFormData.tableCombinationId;
        updatePayload.tableId = editFormData.tableId;
      } else {
        // No table / auto
        updatePayload.tableId = editFormData.tableId;
        updatePayload.tableCombinationId = editFormData.tableCombinationId;
      }

      // `menuSelections` solo se envía si el usuario tocó la sección "Menú"
      // (`editMenuTouched`) — si no la tocó, el campo se omite y el backend
      // conserva las selecciones existentes tal cual (contrato del PUT).
      if (editMenuTouched) {
        updatePayload.menuSelections = Object.entries(editMenuQuantities)
          .filter(([, quantity]) => quantity > 0)
          .map(([menuId, quantity]) => ({ menuId, quantity }));
      }

      const response = await api.put<ReservationApiResponse>(
        `/reservations/${selectedReservation.id}`,
        updatePayload,
      );

      setShowEditModal(false);
      setShowDetailModal(false);
      showToast("Reserva actualizada correctamente", "success");
      notifyReservationWarnings(response.data.warnings, "Reserva actualizada");
      fetchReservations();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setEditError(
        axiosError.response?.data?.message || "Error al actualizar la reserva",
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  // Release table from reservation
  const handleReleaseTable = async () => {
    if (!selectedReservation) return;

    try {
      await api.put(`/reservations/${selectedReservation.id}`, {
        tableId: null,
      });
      setSelectedReservation({
        ...selectedReservation,
        tableId: null,
        table: null,
      });
      showToast("Mesa liberada correctamente", "success");
      fetchReservations();
    } catch (error) {
      console.error("Error releasing table:", error);
      showToast("Error al liberar la mesa", "error");
    }
  };

  // Move reservation to different table(s)
  const handleMoveTable = async () => {
    if (!selectedReservation || selectedTablesForMove.length === 0) return;

    setMoveSubmitting(true);
    try {
      const response = await api.put<ReservationApiResponse>(
        `/reservations/${selectedReservation.id}`,
        {
          manualTableIds: selectedTablesForMove,
        },
      );

      // Update local state
      const newTable = rooms
        .flatMap((r) => r.tables)
        .find((t) => t.id === selectedTablesForMove[0]);

      setSelectedReservation({
        ...selectedReservation,
        tableId: selectedTablesForMove[0],
        table: newTable || null,
      });

      setShowMoveTableModal(false);
      setSelectedTablesForMove([]);
      setMoveOriginalTableIds([]);
      showToast("Mesa actualizada correctamente", "success");
      notifyReservationWarnings(response.data.warnings, "Reserva movida");
      fetchReservations();
    } catch (error: unknown) {
      console.error("Error moving table:", error);
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message || "Error al cambiar la mesa",
        "error",
      );
    } finally {
      setMoveSubmitting(false);
    }
  };

  // Delete reservation
  const handleDeleteReservation = async () => {
    if (!selectedReservation) return;

    try {
      await api.delete(`/reservations/${selectedReservation.id}`);
      setShowDetailModal(false);
      setSelectedReservation(null);
      showToast("Reserva eliminada", "success");
      fetchReservations();
    } catch (error) {
      console.error("Error deleting:", error);
      showToast("Error al eliminar la reserva", "error");
    }
  };

  // Send email to customer
  const handleSendEmail = async () => {
    if (!selectedReservation) return;

    if (!emailFormData.subject.trim()) {
      showToast("El asunto es obligatorio", "error");
      return;
    }
    if (!emailFormData.message.trim()) {
      showToast("El mensaje es obligatorio", "error");
      return;
    }

    setEmailSending(true);

    try {
      // Convert files to base64 for the API
      const attachmentsData = await Promise.all(
        emailAttachments.map(async (file) => {
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              // Remove the data:mime/type;base64, prefix
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.readAsDataURL(file);
          });
          return {
            filename: file.name,
            content: base64,
            contentType: file.type,
          };
        }),
      );

      await api.post(`/reservations/${selectedReservation.id}/send-email`, {
        subject: emailFormData.subject,
        message: emailFormData.message,
        attachments: attachmentsData,
      });
      setShowEmailModal(false);
      setEmailFormData({ subject: "", message: "" });
      setEmailAttachments([]);
      showToast("Email enviado correctamente", "success");
    } catch (error: unknown) {
      console.error("Error sending email:", error);
      const apiError = error as { response?: { data?: { error?: string } } };
      showToast(
        apiError.response?.data?.error || "Error al enviar el email",
        "error",
      );
    } finally {
      setEmailSending(false);
    }
  };

  // Open email modal with pre-filled data
  const openEmailModal = () => {
    if (!selectedReservation) return;

    const reservationDate =
      typeof selectedReservation.date === "string"
        ? new Date(selectedReservation.date)
        : selectedReservation.date;
    const dateStr = format(reservationDate, "d 'de' MMMM", { locale: es });

    setEmailFormData({
      subject: `Información sobre su reserva del ${dateStr}`,
      message: `Estimado/a ${selectedReservation.customer.firstName},\n\n`,
    });
    setEmailAttachments([]);
    setShowEmailModal(true);
  };

  // Create new reservation
  const handleCreateReservation = async () => {
    if (!formData.customerFirstName.trim()) {
      setFormError("El nombre del cliente es obligatorio");
      return;
    }
    if (!formData.customerLastName.trim()) {
      setFormError("Los apellidos del cliente son obligatorios");
      return;
    }
    if (!formData.customerPhone.trim()) {
      setFormError("El teléfono es obligatorio");
      return;
    }
    if (!currentLocationId) {
      setFormError(
        "No se ha encontrado la ubicación. Por favor recarga la página.",
      );
      return;
    }

    setFormSubmitting(true);
    setFormError(null);

    try {
      // Map source values to backend expected values
      const sourceMap: Record<string, string> = {
        manual: "dashboard",
        phone: "phone",
        walk_in: "walk-in",
        "walk-in": "walk-in",
        dashboard: "dashboard",
      };

      const response = await api.post<ReservationApiResponse>(
        "/reservations",
        {
          locationId: currentLocationId,
          date: formData.date,
          time: formData.time,
          partySize: formData.partySize,
          tableId:
            selectedTablesForNewReservation.length === 1
              ? selectedTablesForNewReservation[0]
              : selectedTableForReservation?.id || null,
          manualTableIds:
            selectedTablesForNewReservation.length > 0
              ? selectedTablesForNewReservation
              : undefined,
          notes: formData.notes || null,
          source: sourceMap[formData.source] || "dashboard",
          notifyByWhatsApp: formData.notifyByWhatsApp,
          customerId: selectedCustomerId || undefined,
          customer: selectedCustomerId
            ? undefined
            : {
                firstName: formData.customerFirstName.trim(),
                lastName: formData.customerLastName.trim(),
                phone: formData.customerPhone.trim(),
                email: formData.customerEmail?.trim() || undefined,
                countryCode: formData.customerCountryCode,
                language: "es",
              },
          // Solo se envía si el usuario tocó la sección "Menú" (mismo
          // criterio que el PUT de editar, por simetría del contrato).
          ...(newMenuTouched
            ? {
                menuSelections: Object.entries(newMenuQuantities)
                  .filter(([, quantity]) => quantity > 0)
                  .map(([menuId, quantity]) => ({ menuId, quantity })),
              }
            : {}),
        },
      );

      setShowNewModal(false);
      setSelectedTableForReservation(null);
      setSelectedTablesForNewReservation([]);
      setSelectedCustomerId(null);
      setCustomerResults([]);
      notifyReservationWarnings(response.data.warnings, "Reserva creada");
      fetchReservations();
    } catch (error) {
      console.error("Error creating reservation:", error);
      const axiosError = error as {
        response?: {
          data?: { message?: string; error?: string; errors?: unknown };
        };
      };
      console.error("Error response data:", axiosError.response?.data);
      setFormError(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          "Error al crear la reserva",
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const navigateDate = (direction: "prev" | "next") => {
    setSelectedDate((current) =>
      direction === "prev" ? subDays(current, 1) : addDays(current, 1),
    );
  };

  // Helper compartido: búsqueda + filtro de origen + filtro de estado. Se
  // aplica igual en la vista normal (`filteredReservations`) y en la vista
  // "Todo el día" (`lunchReservations`/`dinnerReservations`) para que un
  // filtro de estado/origen (p.ej. el enlace `?status=pending` de la Home)
  // tenga efecto sin importar en qué vista caiga el usuario.
  const matchesSearchAndFilters = (r: Reservation): boolean => {
    // Filtrar por término de búsqueda
    if (searchTerm) {
      // POR PALABRAS y SIN ACENTOS: "pablo pascual" encuentra a Pablo Pascual y
      // "gonzalez" a González. Cada palabra debe casar con algún campo: nombre
      // de la ficha, teléfono, email o nombre con el que se hizo la reserva.
      const haystack = [
        `${r.customer.firstName || ""} ${r.customer.lastName || ""}`,
        r.customer.phone || "",
        r.customer.email || "",
        r.bookingName || "",
      ].map(normalizeSearchText);
      const terms = normalizeSearchText(searchTerm).split(" ").filter(Boolean);
      const matchesSearch = terms.every((term) =>
        haystack.some((field) => field.includes(term)),
      );
      if (!matchesSearch) return false;
    }

    // Filtrar por origen
    if (sourceFilter && r.source !== sourceFilter) {
      return false;
    }

    // Filtrar por estado
    if (statusFilter && r.status !== statusFilter) {
      return false;
    }

    return true;
  };

  const filteredReservations = reservations.filter((r) => {
    if (!matchesSearchAndFilters(r)) return false;

    // Filtrar por turno (comida/cena)
    if (timeSlot !== "all") {
      const hour = parseInt(r.time.split(":")[0], 10);
      // Comida: antes de las 17:00 (12:00 - 16:59)
      // Cena: desde las 17:00 en adelante (17:00 - 23:59)
      if (timeSlot === "lunch" && hour >= 17) return false;
      if (timeSlot === "dinner" && hour < 17) return false;
    }

    return true;
  });

  // Reservas filtradas por turno para vista "Todo el día" (mismos criterios
  // de búsqueda/origen/estado que `filteredReservations`, ver
  // `matchesSearchAndFilters`)
  const lunchReservations = reservations.filter((r) => {
    if (!matchesSearchAndFilters(r)) return false;
    const hour = parseInt(r.time.split(":")[0], 10);
    return hour < 17;
  });

  const dinnerReservations = reservations.filter((r) => {
    if (!matchesSearchAndFilters(r)) return false;
    const hour = parseInt(r.time.split(":")[0], 10);
    return hour >= 17;
  });

  // Estados que NO cuentan para el total de personas: la gente no viene.
  const NON_COUNTING_STATUSES = ["cancelled", "no_show"];
  const countsForPax = (reservation: Reservation) =>
    !NON_COUNTING_STATUSES.includes(reservation.status);

  const filteredReservationsPax = filteredReservations.reduce(
    (total, reservation) =>
      total + (countsForPax(reservation) ? reservation.partySize : 0),
    0,
  );

  const lunchReservationsPax = lunchReservations.reduce(
    (total, reservation) =>
      total + (countsForPax(reservation) ? reservation.partySize : 0),
    0,
  );

  const dinnerReservationsPax = dinnerReservations.reduce(
    (total, reservation) =>
      total + (countsForPax(reservation) ? reservation.partySize : 0),
    0,
  );

  // Aplica el criterio de ordenación seleccionado ("Predeterminado" no reordena:
  // el backend ya devuelve las reservas ordenadas por date+time asc)
  const sortReservations = useCallback(
    (list: Reservation[]): Reservation[] => {
      switch (sortBy) {
        case "recent":
          return [...list].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
        case "time":
          return [...list].sort((a, b) => a.time.localeCompare(b.time));
        case "partySize":
          return [...list].sort((a, b) => b.partySize - a.partySize);
        case "default":
        default:
          return list;
      }
    },
    [sortBy],
  );

  const sortedReservations = useMemo(
    () => sortReservations(filteredReservations),
    [filteredReservations, sortReservations],
  );

  const sortedLunchReservations = useMemo(
    () => sortReservations(lunchReservations),
    [lunchReservations, sortReservations],
  );

  const sortedDinnerReservations = useMemo(
    () => sortReservations(dinnerReservations),
    [dinnerReservations, sortReservations],
  );

  const currentRoom = rooms.find((r) => r.id === selectedRoom);
  // Lienzo fijo del mapa en vivo (ver CANVAS_CELLS) — Room.gridSize sigue
  // llegando de la API pero ya no se usa para pintar el mapa.
  const gridSize = CANVAS_CELLS;

  // Restaura/auto-encuadra el viewport compartido por los 3 modales
  // "Seleccionar Mesas" al abrirse (o al cambiar de sala CON el modal ya
  // abierto, vía las pestañas de sala del propio modal). Un único efecto
  // porque nunca hay más de un modal abierto a la vez.
  const anyMapModalOpen =
    showNewReservationTableMap || showTableMapModal || showMoveTableModal;
  useEffect(() => {
    if (!anyMapModalOpen) return;
    const bbox = computeContentBBox(
      currentRoom?.tables ?? [],
      currentRoom?.decorativeObjects ?? [],
    );
    modalMapViewport.restoreOrFit(bbox);
  }, [anyMapModalOpen, currentRoom?.id, modalMapViewport.restoreOrFit]);

  // Helper to check if a reservation matches the current time slot filter
  const matchesTimeSlot = (reservation: Reservation): boolean => {
    if (timeSlot === "all") return true;
    const hour = parseInt(reservation.time.split(":")[0], 10);
    if (timeSlot === "lunch" && hour >= 17) return false;
    if (timeSlot === "dinner" && hour < 17) return false;
    return true;
  };

  const getTableReservations = (tableId: string): Reservation[] => {
    return reservations.filter((r) => {
      const isActiveStatus = !finalStatusCodes.has(r.status);
      const isDirectTable = r.tableId === tableId;
      const isInCombination =
        r.tableCombination?.tables.some((t) => t.table.id === tableId) ?? false;
      const isInTimeSlot = matchesTimeSlot(r);
      return (
        isActiveStatus && (isDirectTable || isInCombination) && isInTimeSlot
      );
    });
  };

  const getTableReservation = (tableId: string): Reservation | undefined => {
    return getTableReservations(tableId)[0];
  };

  // Get icon for decorative object type
  const getDecorativeIcon = (
    type: DecorativeType,
    size: string = "w-4 h-4",
  ) => {
    switch (type) {
      case "bar":
        return <Coffee className={size} />;
      case "bathroom":
        return <Bath className={size} />;
      case "door":
        return <DoorOpen className={size} />;
      case "wall":
        return <RectangleHorizontal className={size} />;
      case "plant":
        return <Flower2 className={size} />;
      case "column":
        return <Columns className={size} />;
      default:
        return null;
    }
  };

  // Lookup code -> {bgColor,textColor} de los estados configurados por el superadmin, para pintar
  // mesas del mapa con los colores REALES (Tailwind hoy, HEX cuando migre la BD). Sustituye al mapa
  // hardcodeado que había antes en getTableColor/getStatusBgColor.
  const statusColorLookup = useMemo(() => {
    const lookup: Record<string, { bgColor: string; textColor: string }> = {};
    availableStatuses.forEach((s) => {
      lookup[s.code] = { bgColor: s.bgColor, textColor: s.textColor };
    });
    return lookup;
  }, [availableStatuses]);

  // Estados a mostrar en la leyenda del mapa: los mismos "activos" (no finales) que colorea
  // getTableColor, con nombre y colores reales configurados por el superadmin. Mientras carga
  // el backend, usa un fallback con los estados clásicos coloreables en el mapa.
  const legendStatuses = useMemo(() => {
    const nonFinal = availableStatuses
      .filter((s) => !s.isFinal)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((s) => ({
        code: s.code,
        name: s.name,
        bgColor: s.bgColor,
        textColor: s.textColor,
      }));
    if (nonFinal.length > 0) return nonFinal;
    return (
      ["pending", "confirmed", "double_confirmed", "arrived", "seated"] as const
    ).map((code) => ({
      code,
      name: STATUS_CONFIG[code]?.label ?? code,
      bgColor: STATUS_CONFIG[code]?.bgColor ?? "#F3F4F6",
      textColor: STATUS_CONFIG[code]?.color ?? "#374151",
    }));
  }, [availableStatuses]);

  // Codes de estado "final" que NO ocupan la mesa (liberan el hueco en el mapa): cancelaciones,
  // no-show y completada. Deriva de isFinal (config real de estados, superadmin puede añadir
  // codes personalizados), no de una lista literal — evita que un estado final nuevo (p.ej. un
  // "cancelled_by_customer" añadido a mano) siga pintando la mesa como ocupada. Fallback mientras
  // availableStatuses aún no cargó desde /reservations/statuses (mismo patrón que legendStatuses).
  const finalStatusCodes = useMemo(() => {
    const fromApi = availableStatuses
      .filter((s) => s.isFinal)
      .map((s) => s.code);
    if (fromApi.length > 0) return new Set(fromApi);
    return new Set([
      "completed",
      "cancelled",
      "cancelled_by_restaurant",
      "no_show",
    ]);
  }, [availableStatuses]);

  const getTableColor = (table: Table) => {
    const reservation = getTableReservation(table.id);
    if (!reservation)
      return { className: "bg-gray-100 border-gray-300 text-gray-600", style: {} };
    const statusInfo = statusColorLookup[reservation.status];
    if (!statusInfo) {
      return { className: "bg-gray-100 border-gray-300", style: {} };
    }
    return tableStatusColorProps(statusInfo.bgColor, statusInfo.textColor);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-gray-50 overflow-hidden -m-4 lg:-m-8">
      {/* Barra superior - FIJA */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-2 md:px-5 py-1.5 md:py-2.5">
        <div className="flex items-center justify-between gap-3">
          {/* Navegación de fecha */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => navigateDate("prev")}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            {/* Date selector with calendar dropdown */}
            <div className="relative" ref={calendarRef}>
              <button
                onClick={() => {
                  setShowCalendar(!showCalendar);
                  setCalendarMonth(selectedDate);
                }}
                className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-100 rounded-lg transition-colors min-w-[140px] sm:min-w-[180px]"
              >
                <Calendar className="w-5 h-5 text-primary-600 flex-shrink-0" />
                <div className="text-left">
                  <span className="text-sm font-semibold text-gray-900">
                    <span className="hidden sm:inline">
                      {format(selectedDate, "EEE d 'de' MMM", { locale: es })}
                    </span>
                    <span className="sm:hidden">
                      {format(selectedDate, "d MMM", { locale: es })}
                    </span>
                  </span>
                  {isToday(selectedDate) && (
                    <span className="ml-1.5 text-xs text-primary-600 font-medium">
                      (Hoy)
                    </span>
                  )}
                </div>
              </button>

              {/* Mini Calendar Dropdown */}
              {showCalendar && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50 w-72 md:w-80">
                  {/* Calendar Header */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() =>
                        setCalendarMonth(subMonths(calendarMonth, 1))
                      }
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <span className="font-semibold text-gray-900">
                      {format(calendarMonth, "MMMM yyyy", { locale: es })}
                    </span>
                    <button
                      onClick={() =>
                        setCalendarMonth(addMonths(calendarMonth, 1))
                      }
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {["L", "M", "X", "J", "V", "S", "D"].map((day) => (
                      <div
                        key={day}
                        className="text-center text-xs font-medium text-gray-500 py-1"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar days */}
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day, index) => {
                      const isCurrentMonth = isSameMonth(day, calendarMonth);
                      const isSelected = isSameDay(day, selectedDate);
                      const isTodayDate = isToday(day);

                      return (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedDate(day);
                            setShowCalendar(false);
                          }}
                          className={`
                            w-9 h-9 rounded-lg text-sm font-medium transition-colors
                            ${!isCurrentMonth ? "text-gray-300" : "text-gray-700 hover:bg-gray-100"}
                            ${isSelected ? "bg-primary-600 text-white hover:bg-primary-700" : ""}
                            ${isTodayDate && !isSelected ? "border-2 border-primary-600" : ""}
                          `}
                        >
                          {format(day, "d")}
                        </button>
                      );
                    })}
                  </div>

                  {/* Quick actions */}
                  <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedDate(new Date());
                        setShowCalendar(false);
                      }}
                      className="flex-1 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      Hoy
                    </button>
                    <button
                      onClick={() => setShowCalendar(false)}
                      className="flex-1 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => navigateDate("next")}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => setSelectedDate(new Date())}
              className="hidden sm:block px-2.5 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md"
            >
              Hoy
            </button>
            <button
              onClick={() =>
                navigate(
                  `/reservations/calendar?month=${format(selectedDate, "yyyy-MM")}`,
                )
              }
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 text-sm bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-md"
            >
              <CalendarDays className="w-4 h-4" />
              Mes
            </button>
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600 disabled:opacity-60"
              title="Actualizar reservas"
            >
              <RefreshCw
                className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
              />
            </button>
            {/* Candado: pausa temporal de reservas nuevas de clientes (para
                recolocar el mapa sin que entre nadie en una mesa liberada) */}
            <div className="relative">
              <button
                onClick={() =>
                  pauseActive
                    ? handleSetPause(null)
                    : setShowPauseMenu(!showPauseMenu)
                }
                className={`flex items-center gap-1.5 p-1.5 rounded-lg ${
                  pauseActive
                    ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
                title={
                  pauseActive
                    ? `Reservas nuevas pausadas hasta las ${format(new Date(pausedUntil!), "HH:mm")} — pulsa para reactivar`
                    : "Pausar reservas nuevas temporalmente"
                }
              >
                {pauseActive ? (
                  <>
                    <Lock className="w-5 h-5" />
                    <span className="hidden md:inline text-xs font-semibold">
                      hasta {format(new Date(pausedUntil!), "HH:mm")}
                    </span>
                  </>
                ) : (
                  <Unlock className="w-5 h-5" />
                )}
              </button>
              {showPauseMenu && !pauseActive && (
                <div className="absolute right-0 mt-2 w-60 bg-white border border-gray-200 rounded-xl shadow-lg z-[60] p-2">
                  <p className="px-2 py-1 text-xs font-semibold text-gray-500">
                    Pausar reservas nuevas
                  </p>
                  <p className="px-2 pb-1.5 text-[11px] text-gray-400 leading-snug">
                    Widget y app no aceptarán reservas y ninguna mesa se
                    asignará sola. Tú puedes seguir trabajando.
                  </p>
                  {[15, 30, 60].map((m) => (
                    <button
                      key={m}
                      onClick={() => handleSetPause(m)}
                      className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-amber-50 rounded-lg"
                    >
                      ⏸️ {m} minutos
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Selector de turno - Desktop */}
          <div className="hidden md:flex items-center gap-2">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              {[
                { key: "all", label: "Todo" },
                { key: "lunch", label: "Comida" },
                { key: "dinner", label: "Cena" },
              ].map((slot) => (
                <button
                  key={slot.key}
                  onClick={() => setTimeSlot(slot.key as TimeSlot)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${timeSlot === slot.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          </div>

          {/* Botón Nueva Reserva */}
          <button
            onClick={() => openNewReservationModal()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nueva Reserva</span>
          </button>
        </div>
      </div>

      {/* Mobile Controls: Turno + Vista en una sola barra compacta */}
      <div className="flex-shrink-0 md:hidden bg-white border-b border-gray-200 px-3 py-1.5">
        <div className="flex items-center gap-2">
          {/* Selector de turno */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 flex-1">
            {[
              { key: "lunch", label: "Comida" },
              { key: "dinner", label: "Cena" },
            ].map((slot) => (
              <button
                key={slot.key}
                onClick={() => setTimeSlot(slot.key as TimeSlot)}
                className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${timeSlot === slot.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
              >
                {slot.label}
              </button>
            ))}
          </div>
          {/* Toggle vista */}
          {timeSlot !== "all" && (
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setMobileView("list")}
                className={`p-1.5 rounded-md transition-all ${mobileView === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-400"}`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setMobileView("map")}
                className={`p-1.5 rounded-md transition-all ${mobileView === "map" ? "bg-white text-gray-900 shadow-sm" : "text-gray-400"}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filtros y ordenación - Desktop, vista "Todo el día" (en la vista
          normal estos mismos controles viven dentro del panel de lista, ver
          más abajo el bloque "Filtros y ordenación - Desktop") */}
      {timeSlot === "all" && (
        <div className="hidden md:flex flex-shrink-0 items-center gap-2 bg-white border-b border-gray-200 px-4 py-2">
          <select
            value={statusFilter || ""}
            onChange={(e) => setStatusFilter(e.target.value || null)}
            className="px-3 py-1.5 text-sm bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-primary-500 cursor-pointer text-gray-600"
            title="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            {availableStatuses.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            value={sourceFilter || ""}
            onChange={(e) => setSourceFilter(e.target.value || null)}
            className="px-3 py-1.5 text-sm bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-primary-500 cursor-pointer text-gray-600"
            title="Filtrar por origen"
          >
            <option value="">Todos los orígenes</option>
            {Object.entries(RESERVATION_SOURCES).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-3 py-1.5 text-sm bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-primary-500 cursor-pointer text-gray-600"
            title="Ordenar por"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>

          {(statusFilter || sourceFilter) && (
            <button
              onClick={() => {
                setStatusFilter(null);
                setSourceFilter(null);
              }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Quitar filtros"
            >
              <X className="w-3.5 h-3.5" />
              Quitar filtros
            </button>
          )}
        </div>
      )}

      {/* Contenedor principal con scroll - FLEX-1 para ocupar el resto */}
      <div
        className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden"
        key={timeSlot}
      >
        {/* Vista "Todo el día" en desktop: dos listas lado a lado */}
        {timeSlot === "all" ? (
          <>
            {/* Lista Comida */}
            <div className="hidden lg:flex w-full lg:w-1/2 border-r border-gray-200 bg-white flex-col min-h-0 overflow-hidden">
              <div className="p-3 border-b border-gray-100 bg-amber-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                    <span className="text-lg">☀️</span> Comida
                    <span className="text-sm font-normal text-amber-600">
                      ({lunchReservations.length})
                    </span>
                  </h3>
                </div>
              </div>
              <div className="p-4 border-b border-gray-100">
                <div className="mb-3 flex items-center gap-3 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-800">
                    <Calendar className="h-3.5 w-3.5" />
                    {lunchReservations.length} reservas
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 font-medium text-blue-800">
                    <Users className="h-3.5 w-3.5" />
                    {lunchReservationsPax} pax
                  </span>
                  <MenuCountChip counts={menuCounts?.byShift?.comida ?? menuCounts} />
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : lunchReservations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-500 px-4 text-center">
                    <Calendar className="w-10 h-10 mb-2 text-gray-300" />
                    {(searchTerm || statusFilter || sourceFilter) &&
                    reservations.some(
                      (r) => parseInt(r.time.split(":")[0], 10) < 17,
                    ) ? (
                      <>
                        <p className="text-sm">
                          Hay reservas de comida ocultas por los filtros activos.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm("");
                            setStatusFilter(null);
                            setSourceFilter(null);
                          }}
                          className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg"
                        >
                          <X className="w-3 h-3" />
                          Quitar filtros
                        </button>
                      </>
                    ) : (
                      <p className="text-sm">Sin reservas de comida</p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {sortedLunchReservations.map((reservation) => {
                      const statusConfig = getStatusConfig(reservation.status);
                      const statusColor = statusBadgeColorProps(
                        statusConfig?.bgColor,
                        statusConfig?.color,
                      );
                      const sourceConfig = getSourceConfig(reservation.source);
                      const IconComponent = sourceConfig.icon;
                      const isHighlightedByMap =
                        hoveredTableId != null &&
                        (reservation.tableId === hoveredTableId ||
                          (reservation.tableCombination?.tables.some(
                            (t) => t.table.id === hoveredTableId,
                          ) ??
                            false));
                      return (
                        <div
                          key={reservation.id}
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowDetailModal(true);
                          }}
                          onMouseEnter={() =>
                            setHoveredReservation(reservation)
                          }
                          onMouseLeave={() => setHoveredReservation(null)}
                          className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                            selectedReservation?.id === reservation.id
                              ? "bg-primary-50 border-l-4 border-l-primary-600"
                              : isHighlightedByMap
                                ? "bg-amber-50 border-l-4 border-l-amber-400"
                                : ""
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-base font-semibold text-gray-900">
                                  {reservation.time.slice(0, 5)}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewCustomerId(reservation.customerId);
                                  }}
                                  className="font-medium text-gray-900 truncate text-left hover:text-primary-700 hover:underline"
                                  title="Ver ficha del cliente"
                                >
                                  {reservation.customer.firstName}{" "}
                                  {reservation.customer.lastName}{" "}
                                  <span
                                    className="text-gray-400 font-normal"
                                    title="Reservas completadas del cliente"
                                  >
                                    ({reservation.customerCompletedCount ?? 0})
                                  </span>
                                </button>
                                <BookingNameChip reservation={reservation} />
                                <NoShowBadge
                                  count={reservation.customerNoShowCount}
                                />
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor.className}`}
                                  style={statusColor.style}
                                >
                                  {statusConfig?.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {reservation.partySize} pax
                                </span>
                                {reservation.table && (
                                  <span className="text-gray-600">
                                    {reservation.table.name}
                                  </span>
                                )}
                              </div>
                              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${sourceConfig.bgColor} ${sourceConfig.color}`}
                                  title={sourceConfig.label}
                                >
                                  <IconComponent className="w-3 h-3" />
                                  {sourceConfig.label}
                                </span>
                                {(reservation.menuSelections?.length ?? 0) >
                                  0 && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-50 text-amber-700"
                                    title="Menú elegido por el cliente"
                                  >
                                    🍽️{" "}
                                    {reservation
                                      .menuSelections!.map(
                                        (s) => `${s.quantity}× ${s.menu.name}`,
                                      )
                                      .join(" · ")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {(() => {
                                const action = getNextAction(
                                  reservation.status,
                                );
                                if (!action) return null;
                                const ActionIcon = action.icon;
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateReservationStatus(
                                        reservation.id,
                                        action.nextStatus,
                                      );
                                    }}
                                    className={`p-1 hover:bg-opacity-20 rounded ${action.color.replace("text-", "hover:bg-").replace("-600", "-100")}`}
                                    title={action.label}
                                  >
                                    <ActionIcon className="w-4 h-4" />
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Lista Cena */}
            <div className="hidden lg:flex w-full lg:w-1/2 bg-white flex-col min-h-0 overflow-hidden">
              <div className="p-3 border-b border-gray-100 bg-indigo-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
                    <span className="text-lg">🌙</span> Cena
                    <span className="text-sm font-normal text-indigo-600">
                      ({dinnerReservations.length})
                    </span>
                  </h3>
                </div>
              </div>
              <div className="p-4 border-b border-gray-100">
                <div className="mb-3 flex items-center gap-3 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 px-2.5 py-1 font-medium text-indigo-800">
                    <Calendar className="h-3.5 w-3.5" />
                    {dinnerReservations.length} reservas
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 font-medium text-blue-800">
                    <Users className="h-3.5 w-3.5" />
                    {dinnerReservationsPax} pax
                  </span>
                  <MenuCountChip counts={menuCounts?.byShift?.cena ?? menuCounts} />
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : dinnerReservations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-500 px-4 text-center">
                    <Calendar className="w-10 h-10 mb-2 text-gray-300" />
                    {(searchTerm || statusFilter || sourceFilter) &&
                    reservations.some(
                      (r) => parseInt(r.time.split(":")[0], 10) >= 17,
                    ) ? (
                      <>
                        <p className="text-sm">
                          Hay reservas de cena ocultas por los filtros activos.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm("");
                            setStatusFilter(null);
                            setSourceFilter(null);
                          }}
                          className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg"
                        >
                          <X className="w-3 h-3" />
                          Quitar filtros
                        </button>
                      </>
                    ) : (
                      <p className="text-sm">Sin reservas de cena</p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {sortedDinnerReservations.map((reservation) => {
                      const statusConfig = getStatusConfig(reservation.status);
                      const statusColor = statusBadgeColorProps(
                        statusConfig?.bgColor,
                        statusConfig?.color,
                      );
                      const sourceConfig = getSourceConfig(reservation.source);
                      const IconComponent = sourceConfig.icon;
                      const isHighlightedByMap =
                        hoveredTableId != null &&
                        (reservation.tableId === hoveredTableId ||
                          (reservation.tableCombination?.tables.some(
                            (t) => t.table.id === hoveredTableId,
                          ) ??
                            false));
                      return (
                        <div
                          key={reservation.id}
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowDetailModal(true);
                          }}
                          onMouseEnter={() =>
                            setHoveredReservation(reservation)
                          }
                          onMouseLeave={() => setHoveredReservation(null)}
                          className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                            selectedReservation?.id === reservation.id
                              ? "bg-primary-50 border-l-4 border-l-primary-600"
                              : isHighlightedByMap
                                ? "bg-amber-50 border-l-4 border-l-amber-400"
                                : ""
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-base font-semibold text-gray-900">
                                  {reservation.time.slice(0, 5)}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewCustomerId(reservation.customerId);
                                  }}
                                  className="font-medium text-gray-900 truncate text-left hover:text-primary-700 hover:underline"
                                  title="Ver ficha del cliente"
                                >
                                  {reservation.customer.firstName}{" "}
                                  {reservation.customer.lastName}{" "}
                                  <span
                                    className="text-gray-400 font-normal"
                                    title="Reservas completadas del cliente"
                                  >
                                    ({reservation.customerCompletedCount ?? 0})
                                  </span>
                                </button>
                                <BookingNameChip reservation={reservation} />
                                <NoShowBadge
                                  count={reservation.customerNoShowCount}
                                />
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor.className}`}
                                  style={statusColor.style}
                                >
                                  {statusConfig?.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {reservation.partySize} pax
                                </span>
                                {reservation.table && (
                                  <span className="text-gray-600">
                                    {reservation.table.name}
                                  </span>
                                )}
                              </div>
                              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${sourceConfig.bgColor} ${sourceConfig.color}`}
                                  title={sourceConfig.label}
                                >
                                  <IconComponent className="w-3 h-3" />
                                  {sourceConfig.label}
                                </span>
                                {(reservation.menuSelections?.length ?? 0) >
                                  0 && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-50 text-amber-700"
                                    title="Menú elegido por el cliente"
                                  >
                                    🍽️{" "}
                                    {reservation
                                      .menuSelections!.map(
                                        (s) => `${s.quantity}× ${s.menu.name}`,
                                      )
                                      .join(" · ")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {(() => {
                                const action = getNextAction(
                                  reservation.status,
                                );
                                if (!action) return null;
                                const ActionIcon = action.icon;
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateReservationStatus(
                                        reservation.id,
                                        action.nextStatus,
                                      );
                                    }}
                                    className={`p-1 hover:bg-opacity-20 rounded ${action.color.replace("text-", "hover:bg-").replace("-600", "-100")}`}
                                    title={action.label}
                                  >
                                    <ActionIcon className="w-4 h-4" />
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Vista normal: Lista + Mapa */}
            {/* Lista de Reservas - oculto en móvil cuando mobileView es "map" */}
            <div
              className={`${mobileView === "map" ? "hidden" : "flex"} lg:flex w-full lg:w-1/2 border-r border-gray-200 bg-white flex-col min-h-0 overflow-hidden`}
            >
              <div className="flex-shrink-0 p-2.5 md:p-4 border-b border-gray-100">
                <div className="mb-2 md:mb-3 flex items-center gap-2 md:gap-3 text-xs md:text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 font-medium text-primary-700">
                    <Calendar className="h-3.5 w-3.5" />
                    {filteredReservations.length} reservas
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 font-medium text-blue-800">
                    <Users className="h-3.5 w-3.5" />
                    {filteredReservationsPax} pax
                  </span>
                  <MenuCountChip counts={timeSlot === "lunch" ? (menuCounts?.byShift?.comida ?? menuCounts) : timeSlot === "dinner" ? (menuCounts?.byShift?.cena ?? menuCounts) : menuCounts} />
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, teléfono o email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Filtros y ordenación - Desktop */}
                <div className="hidden md:flex items-center gap-2 mt-2">
                  {/* Filtro de estado */}
                  <select
                    value={statusFilter || ""}
                    onChange={(e) => setStatusFilter(e.target.value || null)}
                    className="px-3 py-1.5 text-sm bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-primary-500 cursor-pointer text-gray-600"
                    title="Filtrar por estado"
                  >
                    <option value="">Todos los estados</option>
                    {availableStatuses.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>

                  {/* Filtro de origen */}
                  <select
                    value={sourceFilter || ""}
                    onChange={(e) => setSourceFilter(e.target.value || null)}
                    className="px-3 py-1.5 text-sm bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-primary-500 cursor-pointer text-gray-600"
                    title="Filtrar por origen"
                  >
                    <option value="">Todos los orígenes</option>
                    {Object.entries(RESERVATION_SOURCES).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>

                  {/* Ordenar por */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="px-3 py-1.5 text-sm bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-primary-500 cursor-pointer text-gray-600"
                    title="Ordenar por"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : filteredReservations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-500 px-4 text-center">
                    <Calendar className="w-12 h-12 mb-2 text-gray-300" />
                    {reservations.length > 0 &&
                    (searchTerm || statusFilter || sourceFilter) ? (
                      // Hay reservas cargadas (el mapa las enseña) pero un
                      // filtro de la lista las oculta — p.ej. "?status=" en la
                      // URL o un texto autorrellenado en el buscador. Decirlo
                      // evita creer que "no hay reservas".
                      <>
                        <p>
                          Hay {reservations.length}{" "}
                          {reservations.length === 1 ? "reserva" : "reservas"}{" "}
                          este día, pero ninguna coincide con los filtros
                          activos.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm("");
                            setStatusFilter(null);
                            setSourceFilter(null);
                          }}
                          className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg"
                        >
                          <X className="w-3.5 h-3.5" />
                          Quitar filtros
                        </button>
                      </>
                    ) : (
                      <p>No hay reservas para este día</p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {sortedReservations.map((reservation) => {
                      const statusConfig = getStatusConfig(reservation.status);
                      const statusColor = statusBadgeColorProps(
                        statusConfig?.bgColor,
                        statusConfig?.color,
                      );
                      const sourceConfig = getSourceConfig(reservation.source);
                      const IconComponent = sourceConfig.icon;
                      const isHighlightedByMap =
                        hoveredTableId != null &&
                        (reservation.tableId === hoveredTableId ||
                          (reservation.tableCombination?.tables.some(
                            (t) => t.table.id === hoveredTableId,
                          ) ??
                            false));
                      return (
                        <div
                          key={reservation.id}
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowDetailModal(true);
                          }}
                          onMouseEnter={() =>
                            setHoveredReservation(reservation)
                          }
                          onMouseLeave={() => setHoveredReservation(null)}
                          className={`p-2.5 md:p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors ${
                            selectedReservation?.id === reservation.id
                              ? "bg-primary-50 border-l-4 border-l-primary-600"
                              : isHighlightedByMap
                                ? "bg-amber-50 border-l-4 border-l-amber-400"
                                : ""
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 md:gap-3 mb-0.5 md:mb-1">
                                <span className="text-sm md:text-lg font-bold text-gray-900 tabular-nums">
                                  {reservation.time.slice(0, 5)}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewCustomerId(reservation.customerId);
                                  }}
                                  className="font-medium text-gray-900 text-sm md:text-base truncate text-left hover:text-primary-700 hover:underline"
                                  title="Ver ficha del cliente"
                                >
                                  {reservation.customer.firstName}{" "}
                                  {reservation.customer.lastName}{" "}
                                  <span
                                    className="text-gray-400 font-normal"
                                    title="Reservas completadas del cliente"
                                  >
                                    ({reservation.customerCompletedCount ?? 0})
                                  </span>
                                </button>
                                <BookingNameChip reservation={reservation} />
                                <NoShowBadge
                                  count={reservation.customerNoShowCount}
                                />
                                <span
                                  className={`px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium whitespace-nowrap ${statusColor.className}`}
                                  style={statusColor.style}
                                >
                                  {statusConfig?.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Users className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                  {reservation.partySize} pax
                                </span>
                                {reservation.tableCombination ? (
                                  <span className="text-purple-600 font-medium">
                                    {reservation.tableCombination.tables
                                      .map((t) => t.table.name)
                                      .join(" + ")}
                                  </span>
                                ) : reservation.table ? (
                                  <span className="text-gray-600 font-medium">
                                    {reservation.table.name}
                                  </span>
                                ) : null}
                                {reservation.customer.phone && (
                                  <span className="hidden md:flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {reservation.customer.phone}
                                  </span>
                                )}
                              </div>
                              {reservation.notes && (
                                <p className="mt-1 text-xs md:text-sm text-gray-500 flex items-center gap-1 truncate">
                                  <MessageSquare className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {reservation.notes}
                                  </span>
                                </p>
                              )}
                              <div className="mt-1 md:mt-2 flex items-center gap-1.5 flex-wrap">
                                <span
                                  className={`inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 md:py-1 rounded text-[10px] md:text-xs font-medium ${sourceConfig.bgColor} ${sourceConfig.color}`}
                                  title={sourceConfig.label}
                                >
                                  <IconComponent className="w-3 h-3" />
                                  {sourceConfig.label}
                                </span>
                                {(reservation.menuSelections?.length ?? 0) >
                                  0 && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 md:py-1 rounded text-[10px] md:text-xs font-medium bg-amber-50 text-amber-700"
                                    title="Menú elegido por el cliente"
                                  >
                                    🍽️{" "}
                                    {reservation
                                      .menuSelections!.map(
                                        (s) => `${s.quantity}× ${s.menu.name}`,
                                      )
                                      .join(" · ")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {(() => {
                                const action = getNextAction(
                                  reservation.status,
                                );
                                if (!action) return null;
                                const ActionIcon = action.icon;
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateReservationStatus(
                                        reservation.id,
                                        action.nextStatus,
                                      );
                                    }}
                                    className={`p-1.5 hover:bg-opacity-20 rounded ${action.color.replace("text-", "hover:bg-").replace("-600", "-100")}`}
                                    title={action.label}
                                  >
                                    <ActionIcon className="w-4 h-4" />
                                  </button>
                                );
                              })()}
                              {/* Legacy code below - can be removed once getNextAction handles all cases */}
                              {reservation.status === "seated" && false && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateReservationStatus(
                                      reservation.id,
                                      "completed",
                                    );
                                  }}
                                  className="p-1.5 hover:bg-emerald-100 rounded text-emerald-600"
                                  title="Completar"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Mapa de Mesas - oculto en móvil cuando mobileView es "list" */}
            <div
              className={`${mobileView === "list" ? "hidden" : "flex"} lg:flex w-full lg:w-1/2 bg-gray-50 flex-col min-h-0 overflow-hidden`}
            >
              {rooms.length > 0 && (
                <div className="bg-white border-b border-gray-200 px-2 md:px-4 py-1.5 md:py-2 flex-shrink-0">
                  <div className="flex items-center gap-1.5 md:gap-2">
                    <div className="flex gap-1.5 md:gap-2 overflow-x-auto flex-1">
                      {rooms.map((room) => (
                        <button
                          key={room.id}
                          onClick={() => setSelectedRoom(room.id)}
                          className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${selectedRoom === room.id ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                        >
                          {room.name}
                        </button>
                      ))}
                    </div>
                    {/* Shift layout edit toggle */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isMapEditMode &&
                        Object.keys(shiftPositions).length > 0 && (
                          <button
                            onClick={resetShiftPositions}
                            className="p-1.5 md:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Restaurar posiciones originales"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      <button
                        onClick={() => {
                          setIsMapEditMode(!isMapEditMode);
                          setPopoverTable(null);
                        }}
                        className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                          isMapEditMode
                            ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        }`}
                        title={
                          isMapEditMode
                            ? "Bloquear mesas"
                            : "Desbloquear para mover mesas"
                        }
                      >
                        {isMapEditMode ? (
                          <Unlock className="w-4 h-4" />
                        ) : (
                          <Lock className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  {isMapEditMode && (
                    <div className="mt-1 flex items-center gap-1.5 text-[10px] md:text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                      <Move className="w-3 h-3 flex-shrink-0" />
                      <span className="flex-1">
                        Modo edición: arrastra las mesas para reorganizar el
                        turno
                      </span>
                      <button
                        onClick={() => setSnapToGrid((v) => !v)}
                        className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] md:text-xs font-medium rounded border transition-colors ${
                          snapToGrid
                            ? "bg-cyan-50 border-cyan-300 text-cyan-700"
                            : "bg-white/60 border-amber-300 text-amber-600"
                        }`}
                        title={
                          snapToGrid
                            ? "Snap al grid activado"
                            : "Posicionamiento libre"
                        }
                      >
                        <Grid3X3 className="w-3 h-3" />
                        Snap
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Recuadro visible: tamaño FIJO en px (RECUADRO_PX), NUNCA
                  flex-1/vh (altura determinista, no depende del resto del
                  layout). Dentro se panea el lienzo real de 50x50 celdas a
                  `liveMapViewport.cellPx` px/celda (zoom dinámico:
                  auto-encuadre + lupa manual). El ancho se adapta hacia
                  abajo en pantallas estrechas. */}
              <div
                className="relative w-full flex-shrink-0"
                style={{ maxWidth: RECUADRO_PX }}
              >
                <div
                  ref={liveMapViewport.containerRef}
                  onMouseDown={liveMapViewport.onMouseDown}
                  onTouchStart={liveMapViewport.onTouchStart}
                  onScroll={liveMapViewport.onScroll}
                  className={`w-full overflow-auto p-2 md:p-4 ${liveMapViewport.isPanning ? "cursor-grabbing" : ""}`}
                  style={{ height: "min(540px, 65vh)" }}
                >
                {currentRoom ? (
                  <div
                    ref={mapContainerRef}
                    className="relative bg-white rounded-xl border border-gray-200 shadow-sm"
                    style={{
                      width: CANVAS_CELLS * liveMapViewport.cellPx,
                      height: CANVAS_CELLS * liveMapViewport.cellPx,
                    }}
                  >
                    {/* Grid Background */}
                    <div
                      className="absolute inset-0 opacity-10"
                      style={{
                        backgroundImage:
                          "linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)",
                        backgroundSize: `calc(100% / ${gridSize}) calc(100% / ${gridSize})`,
                      }}
                    />

                    {/* Alignment guides */}
                    {shiftGuides.map((guide, i) => (
                      <div
                        key={`sg-${i}`}
                        className="absolute pointer-events-none z-40"
                        style={
                          guide.type === "vertical"
                            ? {
                                left: `${guide.position}%`,
                                top: 0,
                                bottom: 0,
                                width: 1,
                                borderLeft: "1px dashed #06b6d4",
                              }
                            : {
                                top: `${guide.position}%`,
                                left: 0,
                                right: 0,
                                height: 1,
                                borderTop: "1px dashed #06b6d4",
                              }
                        }
                      />
                    ))}

                    {/* Decorative Objects */}
                    {decorativeObjects.map((obj) => {
                      const typeConfig = DECORATIVE_TYPES.find(
                        (t) => t.type === obj.type,
                      );
                      const bgColor = typeConfig?.color || "bg-gray-500";
                      const cellSize = 100 / gridSize;
                      return (
                        <div
                          key={obj.id}
                          className={`absolute ${bgColor} text-white rounded-md flex flex-col items-center justify-center overflow-hidden shadow-md border-2 border-white/30 pointer-events-none`}
                          style={{
                            left: `${obj.positionX * cellSize}%`,
                            top: `${obj.positionY * cellSize}%`,
                            width: `${obj.width * cellSize}%`,
                            height: `${obj.height * cellSize}%`,
                            transform: `rotate(${obj.rotation}deg)`,
                          }}
                        >
                          {getDecorativeIcon(obj.type, "w-4 h-4 md:w-5 md:h-5")}
                          {obj.label && (
                            <span
                              className="text-[8px] md:text-xs font-medium mt-0.5 truncate px-1"
                              style={
                                obj.height > obj.width
                                  ? {
                                      writingMode: "vertical-rl",
                                      textOrientation: "mixed",
                                    }
                                  : undefined
                              }
                            >
                              {obj.label}
                            </span>
                          )}
                        </div>
                      );
                    })}

                    {/* Tables */}
                    {currentRoom.tables.map((table) => {
                      const tableReservations = getTableReservations(table.id);
                      const reservation = tableReservations[0];
                      const hasMultipleReservations =
                        tableReservations.length > 1;
                      const block = getTableBlock(table.id);
                      const capacityOverride = getTableCapacityOverride(
                        table.id,
                      );
                      const hasCapacityOverride = !!capacityOverride;
                      // Reservas en estado final (cancelada, no-show, completada) no ocupan
                      // mesa realmente: aunque conserven tableId histórico, no se resaltan en
                      // el mapa (ni al seleccionarlas en la lista ni al pasar el cursor).
                      const isSelected =
                        selectedReservation != null &&
                        !finalStatusCodes.has(selectedReservation.status) &&
                        (selectedReservation.tableId === table.id ||
                          (selectedReservation.tableCombination?.tables.some(
                            (t) => t.table.id === table.id,
                          ) ??
                            false));

                      // Detectar si esta mesa pertenece a la reserva que se está hovereando
                      const isHovered =
                        hoveredReservation != null &&
                        !finalStatusCodes.has(hoveredReservation.status) &&
                        (hoveredReservation.tableId === table.id ||
                          (hoveredReservation.tableCombination?.tables.some(
                            (t) => t.table.id === table.id,
                          ) ??
                            false));

                      const isBlocked = !!block;
                      const cellSize = 100 / gridSize; // tamaño real de la sala
                      const tablePos = getTablePosition(table);
                      const isDragging = draggingTableId === table.id;

                      // Determine table color based on state.
                      // Bloqueada/hover son estados especiales fijos (no vienen de la BD de estados
                      // de reserva) y tienen prioridad visual sobre el color del estado de la reserva.
                      const getTableDisplayColor = (): {
                        className: string;
                        style: React.CSSProperties;
                      } => {
                        if (isBlocked)
                          return {
                            className: "bg-red-200 border-red-400 text-red-800",
                            style: {},
                          };
                        if (isHovered)
                          return {
                            className:
                              "bg-red-500 border-red-600 text-white ring-2 ring-red-400 ring-offset-1 z-30 scale-110 shadow-xl",
                            style: {},
                          };
                        return getTableColor(table);
                      };

                      // Color de fondo de un estado de reserva (para las franjas de una mesa con
                      // varias reservas). Usa el lookup dinámico en vez del mapa hardcodeado.
                      const getStatusBgColor = (status: string) => {
                        const statusInfo = statusColorLookup[status];
                        if (!statusInfo) return { className: "bg-gray-100", style: {} };
                        return colorProps(statusInfo.bgColor, "backgroundColor");
                      };

                      const openPopover = (
                        e: React.MouseEvent,
                        res?: Reservation,
                      ) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const containerRect =
                          mapContainerRef.current?.getBoundingClientRect();
                        if (containerRect) {
                          const x =
                            rect.left - containerRect.left + rect.width + 5;
                          const y = rect.top - containerRect.top;
                          setPopoverPosition({ x, y });
                        }
                        setPopoverReservation(res || null);
                        setPopoverTable(table);
                        setPopoverTableReservations(
                          getTableReservations(table.id),
                        );
                      };

                      // --- SPLIT VIEW: mesa con múltiples reservas ---
                      if (hasMultipleReservations && !isBlocked) {
                        const count = tableReservations.length;
                        const topStripColor = getStatusBgColor(
                          tableReservations[0].status,
                        );
                        const bottomStripColor = getStatusBgColor(
                          tableReservations[1].status,
                        );
                        // Tipografía escalada con el zoom (cellPx). Esta
                        // vista "split" no oculta líneas por tamaño (cada
                        // franja es flex-1 y su única línea se centra en el
                        // espacio disponible), solo escala el tamaño.
                        const splitNameFontPx = clampNum(
                          liveMapViewport.cellPx * 0.45,
                          8,
                          11,
                        );
                        const splitInfoFontPx = clampNum(
                          liveMapViewport.cellPx * 0.38,
                          7,
                          9,
                        );
                        return (
                          <div
                            key={table.id}
                            onClick={(e) => !isMapEditMode && openPopover(e)}
                            onMouseDown={(e) =>
                              isMapEditMode && handleDragStart(e, table)
                            }
                            onTouchStart={(e) =>
                              isMapEditMode && handleDragStart(e, table)
                            }
                            onMouseEnter={() => setHoveredTableId(table.id)}
                            onMouseLeave={() => setHoveredTableId(null)}
                            className={`absolute flex flex-col overflow-hidden border ${isMapEditMode ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}
                              transition-all ${isDragging ? "duration-0 z-50 shadow-2xl opacity-80" : "duration-150 hover:scale-105 hover:shadow-lg hover:z-10"}
                              ${isHovered ? "bg-red-500 border-red-600 ring-2 ring-red-400 ring-offset-1 z-30 scale-110 shadow-xl" : "border-orange-400 ring-2 ring-orange-300"}
                              ${isSelected ? "ring-2 ring-primary-500 ring-offset-1 z-20" : !isHovered && hasCapacityOverride ? "ring-2 ring-amber-400 ring-offset-1" : ""}
                              ${table.shape === "circle" ? "rounded-full" : "rounded-md"}`}
                            style={{
                              left: `${tablePos.x * cellSize}%`,
                              top: `${tablePos.y * cellSize}%`,
                              width: `${(table.width || 1) * cellSize}%`,
                              height: `${(table.height || 1) * cellSize}%`,
                            }}
                            title={`${table.name} - ${count} reservas - Click para ver todas${hasCapacityOverride ? " - Aforo ajustado hoy" : ""}`}
                          >
                            {/* Nombre de la mesa centrado arriba — sin
                                truncar con "…", envuelve hasta 2 líneas */}
                            <div className="absolute top-0 left-0 right-0 text-center z-10 pointer-events-none">
                              <span
                                className="font-normal leading-tight text-gray-800 bg-white/70 px-0.5 rounded-b break-words line-clamp-2"
                                style={{ fontSize: splitNameFontPx }}
                              >
                                {table.name.replace(/^[Mm]esa\s*/i, "")}
                              </span>
                            </div>

                            {/* Badge de conteo en esquina superior derecha */}
                            <div className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] bg-orange-500 rounded-full flex items-center justify-center z-20 px-0.5">
                              <span className="text-[8px] font-bold text-white leading-none">
                                {count}
                              </span>
                            </div>

                            {/* Indicador de aforo ajustado en esquina superior izquierda */}
                            {hasCapacityOverride && (
                              <div className="absolute top-0.5 left-0.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center z-20">
                                <Settings className="w-2 h-2 text-white" />
                              </div>
                            )}

                            {/* Franja superior - primera reserva */}
                            <div
                              className={`flex-1 flex items-center justify-center pointer-events-none ${isHovered ? "" : topStripColor.className}`}
                              style={isHovered ? {} : topStripColor.style}
                            >
                              <span
                                className="font-normal leading-tight text-gray-800 truncate max-w-full px-0.5"
                                style={{ fontSize: splitInfoFontPx }}
                              >
                                {tableReservations[0].partySize}p{" "}
                                {tableReservations[0].time.slice(0, 5)}
                              </span>
                            </div>

                            {/* Línea divisoria */}
                            <div className="h-px bg-orange-400 flex-shrink-0 pointer-events-none" />

                            {/* Franja inferior - segunda reserva o indicador "+N más" */}
                            <div
                              className={`flex-1 flex items-center justify-center pointer-events-none ${isHovered ? "" : count > 2 ? "bg-orange-100" : bottomStripColor.className}`}
                              style={isHovered || count > 2 ? {} : bottomStripColor.style}
                            >
                              {count > 2 ? (
                                <span
                                  className="font-normal leading-tight text-orange-700 truncate max-w-full px-0.5"
                                  style={{ fontSize: splitInfoFontPx }}
                                >
                                  +{count - 1} más
                                </span>
                              ) : (
                                <span
                                  className="font-normal leading-tight text-gray-800 truncate max-w-full px-0.5"
                                  style={{ fontSize: splitInfoFontPx }}
                                >
                                  {tableReservations[1].partySize}p{" "}
                                  {tableReservations[1].time.slice(0, 5)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // --- VISTA NORMAL: mesa con 0 o 1 reserva ---
                      const tableDisplayColor = getTableDisplayColor();
                      return (
                        <div
                          key={table.id}
                          onClick={(e) =>
                            !isMapEditMode && openPopover(e, reservation)
                          }
                          onMouseDown={(e) =>
                            isMapEditMode && handleDragStart(e, table)
                          }
                          onTouchStart={(e) =>
                            isMapEditMode && handleDragStart(e, table)
                          }
                          onMouseEnter={() => setHoveredTableId(table.id)}
                          onMouseLeave={() => setHoveredTableId(null)}
                          className={`
                        absolute flex flex-col items-center justify-center overflow-hidden border ${isMapEditMode ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}
                        transition-all ${isDragging ? "duration-0 z-50 shadow-2xl opacity-80" : "duration-150 hover:scale-105 hover:shadow-lg hover:z-10"}
                        ${tableDisplayColor.className}
                        ${isSelected ? "ring-2 ring-primary-500 ring-offset-1 z-20" : !isBlocked && !isHovered && hasCapacityOverride ? "ring-2 ring-amber-400 ring-offset-1" : ""}
                        ${table.shape === "circle" ? "rounded-full" : "rounded-md"}
                      `}
                          style={{
                            left: `${tablePos.x * cellSize}%`,
                            top: `${tablePos.y * cellSize}%`,
                            width: `${(table.width || 1) * cellSize}%`,
                            height: `${(table.height || 1) * cellSize}%`,
                            ...tableDisplayColor.style,
                          }}
                          title={
                            isBlocked
                              ? `${table.name} - Bloqueada${block.reason ? `: ${block.reason}` : ""}`
                              : reservation
                                ? `${reservation.customer.firstName} ${reservation.customer.lastName} - ${reservation.partySize} pax - ${reservation.time.slice(0, 5)}${hasCapacityOverride ? " - Aforo ajustado hoy" : ""}`
                                : `${table.name} - Click para reservar${hasCapacityOverride ? ` (aforo ajustado hoy: ${capacityOverride?.minCapacity}-${capacityOverride?.maxCapacity})` : ""}`
                          }
                        >
                          {/* Indicador de aforo ajustado en esquina superior izquierda */}
                          {hasCapacityOverride && !isBlocked && (
                            <div className="absolute top-0.5 left-0.5 w-3 h-3 md:w-3.5 md:h-3.5 bg-amber-500 rounded-full flex items-center justify-center z-20">
                              <Settings className="w-1.5 h-1.5 md:w-2 md:h-2 text-white" />
                            </div>
                          )}

                          {(() => {
                            // Tipografía escalada con el zoom (cellPx). El
                            // NOMBRE nunca se recorta con "…": se envuelve
                            // en hasta 2 líneas (line-clamp-2) en vez de
                            // truncarse a una. Las líneas secundarias
                            // siguen con "cabe entera o no se pinta".
                            const displayName = table.name.replace(
                              /^[Mm]esa\s*/i,
                              "",
                            );
                            const boxWidthPx =
                              (table.width || 1) * liveMapViewport.cellPx;
                            const boxHeightPx =
                              (table.height || 1) * liveMapViewport.cellPx;
                            const { fontPx: nameFontPx, lines: nameLines } =
                              fitNameToTwoLines(
                                displayName,
                                boxWidthPx,
                                clampNum(liveMapViewport.cellPx * 0.5, 8, 14),
                                8,
                              );
                            const infoFontPx = clampNum(
                              liveMapViewport.cellPx * 0.42,
                              7,
                              11,
                            );
                            const customerFontPx = clampNum(
                              liveMapViewport.cellPx * 0.35,
                              7,
                              9,
                            );
                            const candidateFonts = isBlocked
                              ? Array(nameLines).fill(nameFontPx)
                              : reservation
                                ? [
                                    ...Array(nameLines).fill(nameFontPx),
                                    infoFontPx,
                                    customerFontPx,
                                  ]
                                : [
                                    ...Array(nameLines).fill(nameFontPx),
                                    infoFontPx,
                                  ];
                            const lineCount = countFittingLines(
                              boxHeightPx,
                              candidateFonts,
                            );
                            const infoThreshold = nameLines + 1;
                            const customerThreshold = nameLines + 2;
                            return (
                              <>
                                {lineCount >= 1 && (
                                  <span
                                    className="font-normal leading-tight break-words line-clamp-2 max-w-full px-0.5"
                                    style={{ fontSize: nameFontPx }}
                                  >
                                    {displayName}
                                  </span>
                                )}

                                {isBlocked ? (
                                  <Lock className="w-3 h-3 md:w-4 md:h-4 opacity-75" />
                                ) : reservation ? (
                                  <>
                                    {lineCount >= infoThreshold && (
                                      <span
                                        className="font-normal leading-tight truncate max-w-full px-0.5"
                                        style={{ fontSize: infoFontPx }}
                                      >
                                        {reservation.partySize}p{" "}
                                        {reservation.time.slice(0, 5)}
                                      </span>
                                    )}
                                    {lineCount >= customerThreshold && (
                                      <span
                                        className="font-normal leading-tight truncate max-w-full px-0.5 opacity-90"
                                        style={{ fontSize: customerFontPx }}
                                      >
                                        {reservation.customer.firstName}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  lineCount >= infoThreshold && (
                                    <span
                                      className={`font-normal leading-tight truncate max-w-full px-0.5 ${hasCapacityOverride ? "text-amber-800" : "opacity-75"}`}
                                      style={{ fontSize: infoFontPx }}
                                    >
                                      {hasCapacityOverride
                                        ? `${capacityOverride?.minCapacity}-${capacityOverride?.maxCapacity}`
                                        : `${table.minCapacity}-${table.maxCapacity}`}
                                    </span>
                                  )
                                )}
                              </>
                            );
                          })()}
                        </div>
                      );
                    })}

                    {/* Table Popover */}
                    {popoverTable && (
                      <TablePopover
                        table={popoverTable}
                        reservation={popoverReservation}
                        tableReservations={popoverTableReservations}
                        block={getTableBlock(popoverTable.id)}
                        capacityOverride={getTableCapacityOverride(
                          popoverTable.id,
                        )}
                        position={popoverPosition}
                        containerRef={mapContainerRef}
                        availableStatuses={availableStatuses}
                        onClose={() => setPopoverTable(null)}
                        onEditReservation={(res) => {
                          setSelectedReservation(res);
                          setPopoverTable(null);
                          // Pasar la reserva directa: el estado recién seteado
                          // aún no está disponible dentro de openEditModal.
                          openEditModal(res);
                        }}
                        onReleaseTable={(res) => {
                          setPopoverTable(null);
                          showConfirm({
                            title: "Liberar mesa",
                            message:
                              "¿Liberar la mesa de esta reserva? La reserva se mantendrá pero sin mesa asignada.",
                            confirmText: "Liberar",
                            type: "warning",
                            onConfirm: async () => {
                              try {
                                await api.put(`/reservations/${res.id}`, {
                                  tableId: null,
                                });
                                showToast(
                                  "Mesa liberada correctamente",
                                  "success",
                                );
                                fetchReservations(true);
                              } catch {
                                showToast("Error al liberar la mesa", "error");
                              }
                            },
                          });
                        }}
                        onMoveTable={(res) => {
                          setSelectedReservation(res);
                          // Guardar las mesas ACTUALES solo para pintarlas
                          // como "Actual" en la rejilla; la selección para
                          // guardar arranca vacía (ver moveOriginalTableIds).
                          if (res.tableCombination?.tables?.length) {
                            setMoveOriginalTableIds(
                              res.tableCombination.tables.map(
                                (t: { table: { id: string } }) => t.table.id,
                              ),
                            );
                          } else if (res.tableId) {
                            setMoveOriginalTableIds([res.tableId]);
                          } else {
                            setMoveOriginalTableIds([]);
                          }
                          setSelectedTablesForMove([]);
                          setPopoverTable(null);
                          setShowMoveTableModal(true);
                        }}
                        onChangeStatus={(resId, status) => {
                          updateReservationStatus(resId, status);
                          setPopoverTable(null);
                        }}
                        onBlockTable={(tableId) => {
                          handleBlockTable(tableId);
                        }}
                        onUnblockTable={(tableId, blockId) => {
                          handleUnblockTable(tableId, blockId);
                        }}
                        onSetCapacityOverride={handleSetCapacityOverride}
                        onRemoveCapacityOverride={handleRemoveCapacityOverride}
                        onNewReservation={(tbl) => {
                          setPopoverTable(null);
                          openNewReservationModal(tbl);
                        }}
                        onWalkIn={(tableId, partySize) => {
                          handleWalkIn(tableId, partySize);
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <AlertCircle className="w-12 h-12 mb-2 text-gray-300" />
                    <p>No hay salas configuradas</p>
                    <a
                      href="/tables"
                      className="mt-2 text-primary-600 hover:underline text-sm"
                    >
                      Configurar mesas
                    </a>
                  </div>
                )}
                </div>
                {currentRoom && (
                  <ZoomControls
                    onZoomIn={liveMapViewport.zoomIn}
                    onZoomOut={liveMapViewport.zoomOut}
                    onFit={() =>
                      liveMapViewport.fitToContent(
                        computeContentBBox(
                          currentRoom.tables ?? [],
                          currentRoom.decorativeObjects ?? [],
                        ),
                      )
                    }
                  />
                )}
              </div>
              <div className="bg-white border-t border-gray-200 px-2 py-1.5 md:px-4 md:py-3">
                <div className="flex items-center justify-center gap-2 md:gap-4 flex-wrap text-[10px] md:text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
                    <span className="text-gray-600">Libre</span>
                  </div>
                  {legendStatuses.map((s) => {
                    const swatch = tableStatusColorProps(s.bgColor, s.textColor);
                    return (
                      <div key={s.code} className="flex items-center gap-1.5">
                        <div
                          className={`w-3 h-3 rounded border ${swatch.className}`}
                          style={swatch.style}
                        />
                        <span className="text-gray-600">{s.name}</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-red-200 border border-red-400" />
                    <span className="text-gray-600">Bloqueada</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg shadow-2xl max-h-[95dvh] md:max-h-[90dvh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Nueva Reserva
              </h2>
              <button
                onClick={() => {
                  setShowNewModal(false);
                  setSelectedTableForReservation(null);
                  setSelectedTablesForNewReservation([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Error message */}
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{formError}</span>
                </div>
              )}

              {/* Date & Time Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, date: e.target.value }))
                    }
                    className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Hora *
                  </label>
                  <select
                    value={formData.time}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, time: e.target.value }))
                    }
                    className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {getFilteredTimeSlots(formData.date).map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Party Size & Table Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Personas *
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          partySize: Math.max(1, prev.partySize - 1),
                        }))
                      }
                      className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-l-lg transition-colors"
                    >
                      <span className="text-lg font-medium">−</span>
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.partySize}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        setFormData((prev) => ({
                          ...prev,
                          partySize: Math.max(1, Math.min(100, value)),
                        }));
                      }}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 w-16 py-2.5 bg-white border-y border-gray-300 text-center font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          partySize: Math.min(100, prev.partySize + 1),
                        }))
                      }
                      className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-r-lg transition-colors"
                    >
                      <span className="text-lg font-medium">+</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Mesa
                  </label>
                  {selectedTablesForNewReservation.length > 0 ? (
                    <div className="flex items-center justify-between px-3 py-2.5 bg-primary-50 border border-primary-200 rounded-lg">
                      <span className="font-medium text-primary-900 truncate">
                        {selectedTablesForNewReservation.length === 1
                          ? rooms
                              .flatMap((r) => r.tables)
                              .find(
                                (t) =>
                                  t.id === selectedTablesForNewReservation[0],
                              )?.name || "Mesa seleccionada"
                          : `${selectedTablesForNewReservation.length} mesas: ${selectedTablesForNewReservation
                              .map(
                                (id) =>
                                  rooms
                                    .flatMap((r) => r.tables)
                                    .find((t) => t.id === id)?.name,
                              )
                              .filter(Boolean)
                              .join(" + ")}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowNewReservationTableMap(true)}
                        className="text-primary-600 hover:text-primary-800 text-sm flex-shrink-0 ml-2"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : selectedTableForReservation ? (
                    <div className="flex items-center justify-between px-3 py-2.5 bg-primary-50 border border-primary-200 rounded-lg">
                      <span className="font-medium text-primary-900">
                        {selectedTableForReservation.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowNewReservationTableMap(true)}
                        className="text-primary-600 hover:text-primary-800 text-sm"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowNewReservationTableMap(true)}
                      className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-sm hover:bg-gray-100 hover:border-gray-300 transition-colors text-left flex items-center justify-between"
                    >
                      <span>Asignación automática</span>
                      <span className="text-primary-600 text-xs">
                        Seleccionar mesa
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Datos del cliente
                </h3>
              </div>

              {/* Customer Search */}
              <div className="relative" ref={customerSearchRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Buscar cliente existente
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={customerSearch}
                    placeholder="Buscar por nombre, teléfono o email..."
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onFocus={() =>
                      customerResults.length > 0 &&
                      setShowCustomerDropdown(true)
                    }
                    className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {isSearchingCustomers && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>

                {/* Customer Results Dropdown */}
                {showCustomerDropdown && !selectedCustomerId && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {customerResults.length > 0 ? (
                      <>
                        {customerResults.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => selectCustomer(customer)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-700 font-medium text-sm">
                                {customer.firstName?.[0]}
                                {customer.lastName?.[0]}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {customer.firstName} {customer.lastName}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {customer.phone}{" "}
                                {customer.email && `• ${customer.email}`}
                              </p>
                            </div>
                          </button>
                        ))}
                      </>
                    ) : (
                      /* No results - show create option if phone number */
                      customerSearch.length >= 2 &&
                      !isSearchingCustomers &&
                      isPhoneNumber(customerSearch) && (
                        <button
                          type="button"
                          onClick={createCustomerWithPhone}
                          className="w-full px-4 py-3 text-left hover:bg-green-50 flex items-center gap-3 text-green-700"
                        >
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <UserPlus className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">
                              Crear nuevo cliente
                            </p>
                            <p className="text-xs text-green-600">
                              con teléfono {extractPhoneNumber(customerSearch)}
                            </p>
                          </div>
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Selected Customer Badge or New Customer Form */}
              {selectedCustomerId ? (
                <div className="flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary-700" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primary-900">
                      {formData.customerFirstName} {formData.customerLastName}
                    </p>
                    <p className="text-xs text-primary-600">
                      {formData.customerPhone}{" "}
                      {formData.customerEmail && `• ${formData.customerEmail}`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelectedCustomer}
                    className="p-1 hover:bg-primary-100 rounded"
                  >
                    <X className="w-4 h-4 text-primary-600" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span>o crear nuevo cliente</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* First Name & Last Name Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        value={formData.customerFirstName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            customerFirstName: e.target.value,
                          }))
                        }
                        placeholder="Nombre"
                        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Apellidos *
                      </label>
                      <input
                        type="text"
                        value={formData.customerLastName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            customerLastName: e.target.value,
                          }))
                        }
                        placeholder="Apellidos"
                        className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Phone & Email Row - Show when no customer selected or always editable */}
              {!selectedCustomerId && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Teléfono *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.customerCountryCode}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            customerCountryCode: e.target.value,
                          }))
                        }
                        className="w-[4.75rem] flex-shrink-0 px-1.5 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        aria-label="Prefijo telefónico"
                      >
                        {orderedCountries.map((country) => (
                          <option key={country.iso} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <div className="relative flex-1 min-w-0">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          value={formData.customerPhone}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              customerPhone: e.target.value,
                            }))
                          }
                          placeholder="600 000 000"
                          className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customerEmail: e.target.value,
                        }))
                      }
                      placeholder="email@ejemplo.com"
                      className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              )}

              {/* Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Origen
                </label>
                <div className="flex gap-2">
                  {[
                    { value: "phone", label: "Teléfono", icon: Phone },
                    { value: "walk_in", label: "Walk-in", icon: LogIn },
                    { value: "manual", label: "Manual", icon: Calendar },
                  ].map((source) => (
                    <button
                      key={source.value}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          source: source.value,
                        }))
                      }
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        formData.source === source.value
                          ? "bg-primary-50 border-primary-300 text-primary-700"
                          : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <source.icon className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {source.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notas
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Alergias, celebraciones, peticiones especiales..."
                    rows={3}
                    className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  />
                </div>
              </div>

              {/* Menú (solo visible si el día elegido tiene menús vigentes) */}
              <ReservationMenuSection
                menus={newMenus}
                loading={newMenusLoading}
                open={newMenuSectionOpen}
                onToggleOpen={() => setNewMenuSectionOpen((prev) => !prev)}
                quantities={newMenuQuantities}
                onIncrement={newMenuHandlers.onIncrement}
                onDecrement={newMenuHandlers.onDecrement}
                onSetAllGuests={newMenuHandlers.onSetAllGuests}
                partySize={formData.partySize}
              />
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowNewModal(false);
                  setSelectedTableForReservation(null);
                  setSelectedTablesForNewReservation([]);
                }}
                className="flex-1 py-3 bg-white hover:bg-gray-100 text-gray-700 font-medium rounded-lg border border-gray-300 transition-colors"
                disabled={formSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateReservation}
                disabled={formSubmitting}
                className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Crear Reserva
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Map Selection Modal for New Reservation */}
      {showNewReservationTableMap && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[60] md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-4xl shadow-2xl h-[95vh] md:h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 md:p-5 border-b border-gray-200">
              <div>
                <h2 className="text-base md:text-xl font-semibold text-gray-900">
                  Seleccionar Mesas
                </h2>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 md:mt-1">
                  Toca las mesas para seleccionarlas.
                </p>
              </div>
              <button
                onClick={() => setShowNewReservationTableMap(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Room Tabs */}
            <div className="flex gap-2 p-4 border-b border-gray-200 overflow-x-auto">
              {rooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedRoom === room.id
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {room.name}
                </button>
              ))}
            </div>

            {/* Map Container: recuadro fijo (RECUADRO_PX) con zoom dinámico
                (auto-encuadre + lupa) dentro. */}
            <div className="relative w-full mx-auto flex-1 min-h-0 overflow-hidden" style={{ maxWidth: RECUADRO_PX }}>
              <div
                ref={modalMapViewport.containerRef}
                onMouseDown={modalMapViewport.onMouseDown}
                onTouchStart={modalMapViewport.onTouchStart}
                onScroll={modalMapViewport.onScroll}
                className={`w-full h-full overflow-auto p-4 bg-gray-50 ${modalMapViewport.isPanning ? "cursor-grabbing" : "cursor-grab"}`}
              >
              {currentRoom ? (
                <div
                  className="relative bg-white rounded-lg border-2 border-gray-200"
                  style={{
                    width: CANVAS_CELLS * modalMapViewport.cellPx,
                    height: CANVAS_CELLS * modalMapViewport.cellPx,
                  }}
                >
                  {/* Grid Background */}
                  <div
                    className="absolute inset-0 opacity-10 rounded-lg"
                    style={{
                      backgroundImage:
                        "linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)",
                      backgroundSize: `calc(100% / ${gridSize}) calc(100% / ${gridSize})`,
                    }}
                  />

                  {/* Decorative Objects */}
                  {decorativeObjects.map((obj) => {
                    const typeConfig = DECORATIVE_TYPES.find(
                      (t) => t.type === obj.type,
                    );
                    const bgColor = typeConfig?.color || "bg-gray-500";
                    const cellSize = 100 / gridSize;
                    return (
                      <div
                        key={obj.id}
                        className={`absolute ${bgColor} text-white rounded-md flex flex-col items-center justify-center shadow-md border-2 border-white/30 pointer-events-none`}
                        style={{
                          left: `${obj.positionX * cellSize}%`,
                          top: `${obj.positionY * cellSize}%`,
                          width: `${obj.width * cellSize}%`,
                          height: `${obj.height * cellSize}%`,
                          transform: `rotate(${obj.rotation}deg)`,
                        }}
                      >
                        {getDecorativeIcon(obj.type, "w-4 h-4 md:w-5 md:h-5")}
                        {obj.label && (
                          <span
                            className="text-[8px] md:text-xs font-medium mt-0.5 truncate px-1"
                            style={
                              obj.height > obj.width
                                ? {
                                    writingMode: "vertical-rl",
                                    textOrientation: "mixed",
                                  }
                                : undefined
                            }
                          >
                            {obj.label}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Tables */}
                  {currentRoom.tables.map((table) => {
                    const isSelected = selectedTablesForNewReservation.includes(
                      table.id,
                    );
                    const cellSize = 100 / gridSize;

                    // Helper to convert "HH:MM" to minutes from midnight
                    const timeToMinutes = (t: string) => {
                      const [h, m] = t.split(":").map(Number);
                      return h * 60 + m;
                    };

                    const singleTurnPerShift =
                      scheduleConfig?.singleTurnPerShift ?? false;
                    const duration = scheduleConfig?.defaultDuration ?? 90;

                    // Get shift bounds for a time (used when singleTurnPerShift = true)
                    const getShiftBoundsNew = (t: string) => {
                      const shifts = scheduleConfig?.shifts ?? [];
                      const tMin = timeToMinutes(t);
                      for (const sh of shifts as {
                        startTime: string;
                        endTime: string;
                        enabled: boolean;
                      }[]) {
                        if (!sh.enabled) continue;
                        const s = timeToMinutes(sh.startTime);
                        const e = timeToMinutes(sh.endTime);
                        if (tMin >= s && tMin < e) return { start: s, end: e };
                      }
                      return null;
                    };

                    // Get occupied window for a reservation time
                    const getWindowNew = (t: string) => {
                      if (singleTurnPerShift) {
                        const bounds = getShiftBoundsNew(t);
                        if (bounds) return bounds;
                      }
                      const start = timeToMinutes(t);
                      return { start, end: start + duration };
                    };

                    // Check if table is already reserved
                    const isReservedByOther = formData.time
                      ? reservations.some((r) => {
                          // Skip cancelled/no-show/completed
                          if (finalStatusCodes.has(r.status)) return false;
                          // Check same date
                          const reservationDate = format(
                            new Date(r.date),
                            "yyyy-MM-dd",
                          );
                          if (reservationDate !== formData.date) return false;
                          // Check overlap using shift-aware windows
                          const newW = getWindowNew(formData.time);
                          const rW = getWindowNew(r.time);
                          if (!(newW.start < rW.end && newW.end > rW.start))
                            return false;
                          // Check if this table is assigned
                          const isDirectMatch = r.tableId === table.id;
                          const isInCombination =
                            r.tableCombination?.tables.some(
                              (t) => t.table.id === table.id,
                            ) ?? false;
                          return isDirectMatch || isInCombination;
                        })
                      : false;

                    // Capacidad efectiva: solo se resuelve si la fecha del
                    // formulario coincide con la fecha del mapa (los overrides
                    // solo se cargan para `selectedDate`). Si el usuario cambia
                    // la fecha en el formulario, se muestra la capacidad base.
                    const newReservationOverride =
                      formData.date === format(selectedDate, "yyyy-MM-dd")
                        ? getTableCapacityOverride(
                            table.id,
                            formData.time
                              ? deriveShiftFromTime(formData.time)
                              : null,
                          )
                        : null;
                    const effectiveMinNew =
                      newReservationOverride?.minCapacity ??
                      table.minCapacity;
                    const effectiveMaxNew =
                      newReservationOverride?.maxCapacity ??
                      table.maxCapacity;

                    // Bloqueo SOFT para staff: se muestra y se pide
                    // confirmación, pero no se impide la selección.
                    const blockForNew = getTableBlockForTime(
                      table.id,
                      formData.time,
                    );
                    const isBlockedTable = !!blockForNew;

                    return (
                      <div
                        key={table.id}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={async () => {
                          if (isReservedByOther) return;
                          if (isSelected) {
                            setSelectedTablesForNewReservation((prev) =>
                              prev.filter((id) => id !== table.id),
                            );
                            return;
                          }
                          if (isBlockedTable) {
                            const proceed = await confirm({
                              title: "Mesa bloqueada",
                              message: `La mesa ${table.name} está bloqueada${
                                blockForNew?.reason
                                  ? ` (${blockForNew.reason})`
                                  : ""
                              }. ¿Asignarla de todos modos?`,
                              confirmText: "Asignar de todos modos",
                              cancelText: "Cancelar",
                              danger: true,
                            });
                            if (!proceed) return;
                          }
                          setSelectedTablesForNewReservation((prev) => [
                            ...prev,
                            table.id,
                          ]);
                        }}
                        className={`absolute flex flex-col items-center justify-center cursor-pointer transition-all duration-200 border md:border-2 shadow-sm overflow-hidden ${
                          table.shape === "circle"
                            ? "rounded-full"
                            : "rounded md:rounded-lg"
                        } ${
                          isReservedByOther
                            ? "bg-red-100 border-red-300 text-red-600 cursor-not-allowed opacity-60"
                            : isSelected
                              ? isBlockedTable
                                ? "bg-red-200 border-red-600 text-red-900 ring-1 md:ring-2 ring-red-400 z-10"
                                : "bg-green-100 border-green-500 text-green-800 ring-1 md:ring-2 ring-green-400 z-10"
                              : isBlockedTable
                                ? "bg-red-50 border-red-500 border-dashed text-red-700 hover:bg-red-100"
                                : newReservationOverride
                                  ? "bg-white border-amber-400 text-gray-700 ring-1 ring-amber-300 hover:border-primary-400 hover:bg-primary-50"
                                  : "bg-white border-gray-300 text-gray-700 hover:border-primary-400 hover:bg-primary-50"
                        }`}
                        style={{
                          left: `${table.positionX * cellSize}%`,
                          top: `${table.positionY * cellSize}%`,
                          width: `${(table.width || 1) * cellSize}%`,
                          height: `${(table.height || 1) * cellSize}%`,
                        }}
                        title={
                          isReservedByOther
                            ? `${table.name} - Reservada`
                            : isBlockedTable
                              ? `${table.name} - Bloqueada${blockForNew?.reason ? `: ${blockForNew.reason}` : ""}`
                              : `${table.name} (${effectiveMinNew}-${effectiveMaxNew} personas${newReservationOverride ? ", aforo ajustado" : ""})`
                        }
                      >
                        {isBlockedTable && !isReservedByOther && (
                          <div className="absolute -top-0.5 -left-0.5 md:-top-1 md:-left-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-red-600 rounded-full flex items-center justify-center z-20">
                            <Lock className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                        {(() => {
                          const boxWidthPx =
                            (table.width || 1) * modalMapViewport.cellPx;
                          const boxHeightPx =
                            (table.height || 1) * modalMapViewport.cellPx;
                          const { fontPx: nameFontPx, lines: nameLines } =
                            fitNameToTwoLines(
                              table.name,
                              boxWidthPx,
                              clampNum(modalMapViewport.cellPx * 0.5, 8, 14),
                              8,
                            );
                          const infoFontPx = clampNum(
                            modalMapViewport.cellPx * 0.4,
                            7,
                            11,
                          );
                          const lineCount = countFittingLines(
                            boxHeightPx,
                            isReservedByOther
                              ? [
                                  ...Array(nameLines).fill(nameFontPx),
                                  infoFontPx,
                                  infoFontPx,
                                ]
                              : [...Array(nameLines).fill(nameFontPx), infoFontPx],
                          );
                          const infoThreshold = nameLines + 1;
                          const occupiedThreshold = nameLines + 2;
                          return (
                            <>
                              {lineCount >= 1 && (
                                <span
                                  className="font-normal break-words line-clamp-2 px-0.5 leading-tight max-w-full"
                                  style={{ fontSize: nameFontPx }}
                                >
                                  {table.name}
                                </span>
                              )}
                              {lineCount >= infoThreshold && (
                                <span
                                  className={`font-normal leading-tight truncate px-0.5 max-w-full ${
                                    isBlockedTable
                                      ? "text-red-700"
                                      : newReservationOverride
                                        ? "text-amber-700"
                                        : "opacity-70"
                                  }`}
                                  style={{ fontSize: infoFontPx }}
                                >
                                  {isBlockedTable
                                    ? "Bloqueada"
                                    : `${effectiveMinNew}-${effectiveMaxNew}`}
                                </span>
                              )}
                              {lineCount >= occupiedThreshold &&
                                isReservedByOther && (
                                  <span
                                    className="text-red-600 font-normal leading-tight truncate px-0.5 max-w-full"
                                    style={{ fontSize: infoFontPx }}
                                  >
                                    Ocupada
                                  </span>
                                )}
                            </>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <p>No hay salas configuradas</p>
                </div>
              )}
              </div>
              {currentRoom && (
                <ZoomControls
                  onZoomIn={modalMapViewport.zoomIn}
                  onZoomOut={modalMapViewport.zoomOut}
                  onFit={() =>
                    modalMapViewport.fitToContent(
                      computeContentBBox(
                        currentRoom.tables ?? [],
                        currentRoom.decorativeObjects ?? [],
                      ),
                    )
                  }
                />
              )}
            </div>

            {/* Footer */}
            <div className="p-3 md:p-4 border-t border-gray-200 bg-white">
              <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2">
                <div className="text-xs md:text-sm">
                  {selectedTablesForNewReservation.length === 0 ? (
                    <span className="text-gray-500">
                      Ninguna mesa seleccionada (asignación automática)
                    </span>
                  ) : (
                    <span className="text-gray-700">
                      <span className="font-medium">
                        {selectedTablesForNewReservation.length} mesa(s):
                      </span>{" "}
                      {selectedTablesForNewReservation
                        .map((id) => {
                          const table = rooms
                            .flatMap((r) => r.tables)
                            .find((t) => t.id === id);
                          return table?.name;
                        })
                        .filter(Boolean)
                        .join(" + ")}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTablesForNewReservation([])}
                    className="px-3 md:px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors text-xs md:text-sm"
                    disabled={selectedTablesForNewReservation.length === 0}
                  >
                    Limpiar
                  </button>
                  <button
                    onClick={() => setShowNewReservationTableMap(false)}
                    className="px-3 md:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs md:text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      // Apply selection and close
                      if (selectedTablesForNewReservation.length === 1) {
                        const table = rooms
                          .flatMap((r) => r.tables)
                          .find(
                            (t) => t.id === selectedTablesForNewReservation[0],
                          );
                        setSelectedTableForReservation(table || null);
                      } else {
                        setSelectedTableForReservation(null);
                      }
                      setShowNewReservationTableMap(false);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    Confirmar selección
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reservation Detail Modal */}
      {showDetailModal && selectedReservation && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg shadow-2xl max-h-[95dvh] md:max-h-[90dvh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700">
              <div className="text-white min-w-0 flex-1">
                <h2 className="text-lg md:text-xl font-bold truncate">
                  {selectedReservation.customer.firstName}{" "}
                  {selectedReservation.customer.lastName}
                </h2>
                {selectedReservation.bookingNameDiffers &&
                  selectedReservation.bookingName && (
                    <p className="text-xs md:text-sm text-amber-100 truncate">
                      Reserva a nombre de{" "}
                      <span className="font-semibold text-white">
                        {selectedReservation.bookingName}
                      </span>
                    </p>
                  )}
                <p className="text-primary-100 text-xs md:text-sm flex items-center gap-1.5 md:gap-2 mt-0.5 md:mt-1">
                  <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  {selectedReservation.time.slice(0, 5)} -{" "}
                  {format(new Date(selectedReservation.date), "d MMM yyyy", {
                    locale: es,
                  })}
                  <span className="mx-1">•</span>
                  <Users className="w-4 h-4" />
                  {selectedReservation.partySize} pax
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 md:space-y-5">
              {/* Solicitud de cambio pendiente: se decide aquí mismo (antes
                  solo era posible desde el modal de la campanita, así que al
                  llegar por "Ver reserva" o desde el historial no se veía). */}
              {selectedReservation.pendingChangeRequest && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 md:p-4 space-y-3">
                  <p className="text-sm font-semibold text-amber-800">
                    ⚠️ Solicitud de cambio pendiente
                  </p>
                  <div className="text-sm text-amber-900 space-y-1">
                    {selectedReservation.pendingChangeRequest.date && (
                      <p>
                        📅 Fecha:{" "}
                        <span className="line-through text-amber-700/70">
                          {format(
                            new Date(selectedReservation.date),
                            "dd/MM/yyyy",
                          )}
                        </span>{" "}
                        →{" "}
                        <strong>
                          {format(
                            new Date(
                              selectedReservation.pendingChangeRequest.date,
                            ),
                            "dd/MM/yyyy",
                          )}
                        </strong>
                      </p>
                    )}
                    {selectedReservation.pendingChangeRequest.time && (
                      <p>
                        🕐 Hora:{" "}
                        <span className="line-through text-amber-700/70">
                          {selectedReservation.time.slice(0, 5)}
                        </span>{" "}
                        →{" "}
                        <strong>
                          {selectedReservation.pendingChangeRequest.time}
                        </strong>
                      </p>
                    )}
                    {selectedReservation.pendingChangeRequest.partySize && (
                      <p>
                        👥 Personas:{" "}
                        <span className="line-through text-amber-700/70">
                          {selectedReservation.partySize}
                        </span>{" "}
                        →{" "}
                        <strong>
                          {selectedReservation.pendingChangeRequest.partySize}
                        </strong>
                      </p>
                    )}
                  </div>
                  <input
                    type="text"
                    value={changeRequestReason}
                    onChange={(e) => setChangeRequestReason(e.target.value)}
                    placeholder="Motivo si lo rechazas (opcional)"
                    className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleRejectPendingChange}
                      disabled={changeRequestAction !== null}
                      className="flex-1 py-2 px-3 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {changeRequestAction === "reject"
                        ? "Rechazando…"
                        : "Rechazar"}
                    </button>
                    <button
                      type="button"
                      onClick={handleApprovePendingChange}
                      disabled={changeRequestAction !== null}
                      className="flex-1 py-2 px-3 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {changeRequestAction === "approve"
                        ? "Aprobando…"
                        : "Aprobar cambio"}
                    </button>
                  </div>
                </div>
              )}

              {/* Reservation Info */}
              <div className="bg-gray-50 rounded-xl p-3 md:p-4 space-y-2 md:space-y-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Reserva hecha:</span>{" "}
                  {format(new Date(selectedReservation.date), "dd-MM-yyyy", {
                    locale: es,
                  })}{" "}
                  {selectedReservation.time.slice(0, 5)}, Tel:{" "}
                  {selectedReservation.customer.phone || "N/A"}
                </p>
                <p className="text-sm text-gray-600 break-all">
                  <span className="font-medium">Email:</span>{" "}
                  {selectedReservation.customer.email ? (
                    <a
                      href={`mailto:${selectedReservation.customer.email}`}
                      className="text-primary-600 hover:underline"
                    >
                      {selectedReservation.customer.email}
                    </a>
                  ) : (
                    "Sin email"
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Mesa:</span>{" "}
                  {selectedReservation.table?.name || "Sin asignar"}
                </p>
                {selectedReservation.notes && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Notas:</span>{" "}
                    {selectedReservation.notes}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {(() => {
                    const selectedStatusConfig = getStatusConfig(
                      selectedReservation.status,
                    );
                    const selectedStatusColor = statusBadgeColorProps(
                      selectedStatusConfig?.bgColor,
                      selectedStatusConfig?.color,
                    );
                    return (
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${selectedStatusColor.className}`}
                        style={selectedStatusColor.style}
                      >
                        {selectedStatusConfig?.label}
                      </span>
                    );
                  })()}
                  {(selectedReservation.status === "cancelled" ||
                    selectedReservation.status ===
                      "cancelled_by_restaurant") && (
                    <span className="text-xs text-gray-500">
                      {selectedReservation.status === "cancelled_by_restaurant"
                        ? "por el restaurante"
                        : selectedReservation.cancelledBy === "customer"
                          ? "por el cliente"
                          : ""}
                    </span>
                  )}
                </div>
                {selectedReservation.cancellationReason && (
                  <p className="text-xs text-gray-500 mt-1">
                    <span className="font-medium">Motivo:</span>{" "}
                    {selectedReservation.cancellationReason}
                  </p>
                )}
              </div>

              {/* Menú (solo lectura) */}
              {!!selectedReservation.menuSelections?.length && (
                <div className="bg-orange-50 rounded-xl p-3 md:p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <UtensilsCrossed className="w-4 h-4 text-orange-600" />
                    Menú
                  </h4>
                  <ul className="space-y-1">
                    {selectedReservation.menuSelections.map((ms, idx) => (
                      <li
                        key={ms.menuId || idx}
                        className="text-sm text-gray-700"
                      >
                        {ms.quantity}× {ms.menu.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-1.5 md:gap-2">
                <button
                  onClick={() => openEditModal()}
                  className="flex flex-col items-center justify-center gap-0.5 md:gap-1 p-2 md:p-3 bg-white border-2 border-primary-200 rounded-xl hover:bg-primary-50 active:bg-primary-100 transition-colors"
                >
                  <Edit3 className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />
                  <span className="text-[10px] md:text-xs font-medium text-gray-700">
                    EDITAR
                  </span>
                </button>
                <button
                  onClick={() => {
                    showConfirm({
                      title: "Liberar mesa",
                      message:
                        "¿Estás seguro de liberar la mesa de esta reserva? La reserva se mantendrá pero sin mesa asignada.",
                      confirmText: "Liberar",
                      type: "warning",
                      onConfirm: handleReleaseTable,
                    });
                  }}
                  disabled={!selectedReservation.tableId}
                  className="flex flex-col items-center justify-center gap-0.5 md:gap-1 p-2 md:p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Unlock className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  <span className="text-xs font-medium text-gray-700">
                    LIBERAR
                  </span>
                </button>
                <button
                  onClick={() => {
                    // Guardar las mesas ACTUALES solo para pintarlas como
                    // "Actual" en la rejilla; la selección para guardar
                    // arranca vacía (ver moveOriginalTableIds) para que
                    // "Desplazar" mueva en vez de ampliar la reserva.
                    if (selectedReservation.tableCombination?.tables?.length) {
                      setMoveOriginalTableIds(
                        selectedReservation.tableCombination.tables.map(
                          (t: { table: { id: string } }) => t.table.id,
                        ),
                      );
                    } else if (selectedReservation.tableId) {
                      setMoveOriginalTableIds([selectedReservation.tableId]);
                    } else {
                      setMoveOriginalTableIds([]);
                    }
                    setSelectedTablesForMove([]);
                    setShowMoveTableModal(true);
                  }}
                  className="flex flex-col items-center justify-center gap-0.5 md:gap-1 p-2 md:p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <ArrowRightLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  <span className="text-[10px] md:text-xs font-medium text-gray-700">
                    DESPLAZAR
                  </span>
                </button>
              </div>

              {/* Status Buttons */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Cambiar estado:
                </h4>

                {/* Dynamic status buttons from backend */}
                <div className="flex flex-wrap gap-2">
                  {availableStatuses.length > 0 ? (
                    availableStatuses
                      .filter(
                        (status) =>
                          !status.isFinal ||
                          status.code === selectedReservation.status,
                      )
                      .map((status) => {
                        const isActive =
                          selectedReservation.status === status.code;
                        const IconComponent =
                          ICON_MAP[status.icon || ""] || Clock;
                        const buttonColor = statusButtonColorProps(
                          status.bgColor,
                          status.textColor,
                          isActive,
                        );

                        return (
                          <button
                            key={status.id}
                            onClick={() => {
                              updateReservationStatus(
                                selectedReservation.id,
                                status.code,
                              );
                              setSelectedReservation({
                                ...selectedReservation,
                                status: status.code,
                              });
                            }}
                            className={`py-2 px-3 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${buttonColor.className}`}
                            style={buttonColor.style}
                          >
                            <IconComponent className="w-3.5 h-3.5" />
                            {status.name.toUpperCase()}
                          </button>
                        );
                      })
                  ) : (
                    // Fallback to hardcoded buttons while loading
                    <>
                      <button
                        onClick={() => {
                          updateReservationStatus(
                            selectedReservation.id,
                            "pending",
                          );
                          setSelectedReservation({
                            ...selectedReservation,
                            status: "pending",
                          });
                        }}
                        className={`py-2 px-3 rounded-lg text-xs font-bold transition-colors ${
                          selectedReservation.status === "pending"
                            ? "bg-yellow-500 text-white"
                            : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                        }`}
                      >
                        PENDIENTE
                      </button>
                      <button
                        onClick={() => {
                          updateReservationStatus(
                            selectedReservation.id,
                            "confirmed",
                          );
                          setSelectedReservation({
                            ...selectedReservation,
                            status: "confirmed",
                          });
                        }}
                        className={`py-2 px-3 rounded-lg text-xs font-bold transition-colors ${
                          selectedReservation.status === "confirmed"
                            ? "bg-blue-500 text-white"
                            : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                        }`}
                      >
                        CONFIRMADA
                      </button>
                    </>
                  )}
                </div>

                {/* Final statuses row (cancelled, no_show, completed) */}
                {availableStatuses.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                    {availableStatuses
                      .filter((status) => status.isFinal)
                      .map((status) => {
                        const isActive =
                          selectedReservation.status === status.code;
                        const IconComponent =
                          ICON_MAP[status.icon || ""] || XCircle;
                        const buttonColor = statusButtonColorProps(
                          status.bgColor,
                          status.textColor,
                          isActive,
                        );

                        return (
                          <button
                            key={status.id}
                            onClick={() => {
                              updateReservationStatus(
                                selectedReservation.id,
                                status.code,
                              );
                              setSelectedReservation({
                                ...selectedReservation,
                                status: status.code,
                              });
                            }}
                            className={`py-2 px-3 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${buttonColor.className}`}
                            style={buttonColor.style}
                          >
                            <IconComponent className="w-3.5 h-3.5" />
                            {status.name.toUpperCase()}
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Customer Contact */}
              <div className="flex gap-2">
                {selectedReservation.customer.phone && (
                  <a
                    href={`tel:${selectedReservation.customer.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm font-medium">Llamar</span>
                  </a>
                )}
                {selectedReservation.customer.phone &&
                  currentLocation?.whatsappPhoneNumberId && (
                    <button
                      onClick={() =>
                        startWhatsAppConversation(selectedReservation.customer.id)
                      }
                      disabled={startingWhatsAppChat}
                      title="Enviarle un WhatsApp desde el número del negocio y seguir en el inbox"
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {startingWhatsAppChat ? "Enviando..." : "WhatsApp"}
                      </span>
                    </button>
                  )}
                {selectedReservation.customer.email && (
                  <button
                    onClick={openEmailModal}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">Email</span>
                  </button>
                )}
                <button
                  onClick={() =>
                    setViewCustomerId(selectedReservation.customerId)
                  }
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Ver la ficha completa del cliente (estadísticas, historial…)"
                >
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Ver ficha</span>
                </button>
                {canEditCustomers && (
                  <button
                    onClick={openCustomerEdit}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Editar la ficha del cliente (nombre, teléfono, email…)"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Editar ficha</span>
                  </button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t border-gray-200 bg-gray-50">
              {canDeleteReservation && (
                <button
                  onClick={() => {
                    showConfirm({
                      title: "Eliminar reserva",
                      message:
                        "¿Estás seguro de eliminar esta reserva? Esta acción no se puede deshacer.",
                      confirmText: "Eliminar",
                      type: "danger",
                      onConfirm: handleDeleteReservation,
                    });
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Eliminar</span>
                </button>
              )}
              <div className="flex-1" />
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reservation Modal */}
      {showEditModal && selectedReservation && (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/50 md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-md shadow-2xl max-h-[95dvh] md:max-h-auto overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                Editar Reserva
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
              {editError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {editError}
                </div>
              )}

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, date: e.target.value })
                  }
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hora
                </label>
                <input
                  type="time"
                  value={editFormData.time}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, time: e.target.value })
                  }
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Party Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comensales
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setEditFormData({
                        ...editFormData,
                        partySize: Math.max(1, editFormData.partySize - 1),
                      })
                    }
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-bold transition-colors"
                  >
                    -
                  </button>
                  <span className="w-16 text-center text-xl font-semibold text-gray-900">
                    {editFormData.partySize}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setEditFormData({
                        ...editFormData,
                        partySize: editFormData.partySize + 1,
                      })
                    }
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-bold transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Table Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mesa / Combinación
                </label>
                <div className="flex gap-2 items-center">
                  {editFormData.manualTableIds.length > 1 ? (
                    // Show read-only display for manual multi-selection
                    <div className="flex-1 min-w-0 px-3 py-2.5 border border-purple-300 bg-purple-50 rounded-lg text-purple-700 text-sm truncate">
                      {editFormData.manualTableIds.length} mesas seleccionadas
                    </div>
                  ) : (
                    <select
                      value={
                        editFormData.tableCombinationId
                          ? `combo_${editFormData.tableCombinationId}`
                          : editFormData.tableId || ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.startsWith("combo_")) {
                          const comboId = value.replace("combo_", "");
                          const combo = tableCombinations.find(
                            (c) => c.id === comboId,
                          );
                          setEditFormData({
                            ...editFormData,
                            tableCombinationId: comboId,
                            tableId: combo?.tables[0]?.id || null,
                            manualTableIds: [],
                          });
                        } else {
                          setEditFormData({
                            ...editFormData,
                            tableId: value || null,
                            tableCombinationId: null,
                            manualTableIds: value ? [value] : [],
                          });
                        }
                      }}
                      className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="">Sin asignar (automático)</option>
                      <optgroup label="Mesas individuales">
                        {rooms.flatMap((room) =>
                          room.tables.map((table) => (
                            <option key={table.id} value={table.id}>
                              {room.name} - {table.name} ({table.minCapacity}-
                              {table.maxCapacity} pax)
                            </option>
                          )),
                        )}
                      </optgroup>
                      {tableCombinations.length > 0 && (
                        <optgroup label="Combinaciones">
                          {tableCombinations.map((combo) => (
                            <option key={combo.id} value={`combo_${combo.id}`}>
                              {combo.name} ({combo.minCapacity}-
                              {combo.maxCapacity} pax)
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  )}

                  {/* Button to open table map for manual selection */}
                  <button
                    type="button"
                    onClick={() => {
                      // Initialize with current selection
                      if (editFormData.manualTableIds.length > 0) {
                        setSelectedTablesForManual(editFormData.manualTableIds);
                      } else if (editFormData.tableId) {
                        setSelectedTablesForManual([editFormData.tableId]);
                      } else {
                        setSelectedTablesForManual([]);
                      }
                      setShowTableMapModal(true);
                    }}
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-gray-600 transition-colors"
                    title="Seleccionar mesas en el mapa"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                </div>

                {/* Show selected combination details */}
                {editFormData.tableCombinationId && (
                  <div className="mt-2 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <span className="font-medium">
                      Combinación seleccionada:
                    </span>{" "}
                    {tableCombinations
                      .find((c) => c.id === editFormData.tableCombinationId)
                      ?.tables.map((t) => t.name)
                      .filter(Boolean)
                      .join(" + ") || "Cargando..."}
                  </div>
                )}

                {/* Show manual selection details */}
                {editFormData.manualTableIds.length > 1 && (
                  <div className="mt-2 p-2 bg-purple-50 rounded-lg text-sm text-purple-700">
                    <span className="font-medium">Selección manual:</span>{" "}
                    {editFormData.manualTableIds
                      .map((id) => {
                        const table = rooms
                          .flatMap((r) => r.tables)
                          .find((t) => t.id === id);
                        return table?.name;
                      })
                      .filter(Boolean)
                      .join(" + ")}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, notes: e.target.value })
                  }
                  rows={3}
                  placeholder="Notas internas, preferencias..."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                />
              </div>

              {/* Menú (solo visible si el día elegido tiene menús vigentes;
                  precargado con las selecciones actuales de la reserva) */}
              <ReservationMenuSection
                menus={editMenus}
                loading={editMenusLoading}
                open={editMenuSectionOpen}
                onToggleOpen={() => setEditMenuSectionOpen((prev) => !prev)}
                quantities={editMenuQuantities}
                onIncrement={editMenuHandlers.onIncrement}
                onDecrement={editMenuHandlers.onDecrement}
                onSetAllGuests={editMenuHandlers.onSetAllGuests}
                partySize={editFormData.partySize}
              />
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditReservation}
                disabled={editSubmitting}
                className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {editSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar cambios"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog?.isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="p-6 text-center">
              {/* Icon */}
              <div
                className={`w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  confirmDialog.type === "danger"
                    ? "bg-red-100"
                    : confirmDialog.type === "warning"
                      ? "bg-amber-100"
                      : "bg-blue-100"
                }`}
              >
                <AlertCircle
                  className={`w-7 h-7 ${
                    confirmDialog.type === "danger"
                      ? "text-red-600"
                      : confirmDialog.type === "warning"
                        ? "text-amber-600"
                        : "text-blue-600"
                  }`}
                />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmDialog.title}
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                {confirmDialog.message}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {confirmDialog.cancelText || "Cancelar"}
                </button>
                <button
                  onClick={() => {
                    confirmDialog.onConfirm();
                    setConfirmDialog(null);
                  }}
                  className={`flex-1 py-2.5 text-white font-medium rounded-lg transition-colors ${
                    confirmDialog.type === "danger"
                      ? "bg-red-600 hover:bg-red-700"
                      : confirmDialog.type === "warning"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-primary-600 hover:bg-primary-700"
                  }`}
                >
                  {confirmDialog.confirmText || "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast?.isOpen && (
        <div className="fixed bottom-6 right-6 z-[80] animate-slide-up">
          <div
            className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg ${
              toast.type === "success"
                ? "bg-green-600"
                : toast.type === "error"
                  ? "bg-red-600"
                  : "bg-gray-800"
            } text-white`}
          >
            {toast.type === "success" && <CheckCircle className="w-5 h-5" />}
            {toast.type === "error" && <XCircle className="w-5 h-5" />}
            {toast.type === "info" && <AlertCircle className="w-5 h-5" />}
            <span className="font-medium">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-2 hover:bg-white/20 p-1 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Table Map Selection Modal */}
      {showTableMapModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[60] md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-4xl shadow-2xl h-[95vh] md:h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 md:p-5 border-b border-gray-200">
              <div>
                <h2 className="text-base md:text-xl font-semibold text-gray-900">
                  Seleccionar Mesas
                </h2>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 md:mt-1">
                  Toca las mesas para seleccionarlas.
                </p>
              </div>
              <button
                onClick={() => setShowTableMapModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Room Tabs */}
            <div className="flex gap-2 p-4 border-b border-gray-200 overflow-x-auto">
              {rooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedRoom === room.id
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {room.name}
                </button>
              ))}
            </div>

            {/* Map Container: recuadro fijo (RECUADRO_PX) con zoom dinámico
                (auto-encuadre + lupa) dentro. */}
            <div className="relative w-full mx-auto flex-1 min-h-0 overflow-hidden" style={{ maxWidth: RECUADRO_PX }}>
              <div
                ref={modalMapViewport.containerRef}
                onMouseDown={modalMapViewport.onMouseDown}
                onTouchStart={modalMapViewport.onTouchStart}
                onScroll={modalMapViewport.onScroll}
                className={`w-full h-full overflow-auto p-4 bg-gray-50 ${modalMapViewport.isPanning ? "cursor-grabbing" : "cursor-grab"}`}
              >
              {currentRoom ? (
                <div
                  className="relative bg-white rounded-lg border-2 border-gray-200"
                  style={{
                    width: CANVAS_CELLS * modalMapViewport.cellPx,
                    height: CANVAS_CELLS * modalMapViewport.cellPx,
                  }}
                >
                  {/* Grid Background */}
                  <div
                    className="absolute inset-0 opacity-10 rounded-lg"
                    style={{
                      backgroundImage:
                        "linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)",
                      backgroundSize: `calc(100% / ${gridSize}) calc(100% / ${gridSize})`,
                    }}
                  />

                  {/* Decorative Objects */}
                  {decorativeObjects.map((obj) => {
                    const typeConfig = DECORATIVE_TYPES.find(
                      (t) => t.type === obj.type,
                    );
                    const bgColor = typeConfig?.color || "bg-gray-500";
                    const cellSize = 100 / gridSize;
                    return (
                      <div
                        key={obj.id}
                        className={`absolute ${bgColor} text-white rounded-md flex flex-col items-center justify-center shadow-md border-2 border-white/30 pointer-events-none`}
                        style={{
                          left: `${obj.positionX * cellSize}%`,
                          top: `${obj.positionY * cellSize}%`,
                          width: `${obj.width * cellSize}%`,
                          height: `${obj.height * cellSize}%`,
                          transform: `rotate(${obj.rotation}deg)`,
                        }}
                      >
                        {getDecorativeIcon(obj.type, "w-4 h-4 md:w-5 md:h-5")}
                        {obj.label && (
                          <span
                            className="text-[8px] md:text-xs font-medium mt-0.5 truncate px-1"
                            style={
                              obj.height > obj.width
                                ? {
                                    writingMode: "vertical-rl",
                                    textOrientation: "mixed",
                                  }
                                : undefined
                            }
                          >
                            {obj.label}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Tables */}
                  {currentRoom.tables.map((table) => {
                    const isSelected = selectedTablesForManual.includes(
                      table.id,
                    );
                    const cellSize = 100 / gridSize;

                    // Helper to convert "HH:MM" to minutes from midnight
                    const timeToMinutes = (t: string) => {
                      const [h, m] = t.split(":").map(Number);
                      return h * 60 + m;
                    };

                    const singleTurnPerShift =
                      scheduleConfig?.singleTurnPerShift ?? false;
                    const duration = scheduleConfig?.defaultDuration ?? 90;

                    // Get shift bounds for a time (used when singleTurnPerShift = true)
                    const getShiftBoundsEdit = (t: string) => {
                      const shifts = scheduleConfig?.shifts ?? [];
                      const tMin = timeToMinutes(t);
                      for (const sh of shifts as {
                        startTime: string;
                        endTime: string;
                        enabled: boolean;
                      }[]) {
                        if (!sh.enabled) continue;
                        const s = timeToMinutes(sh.startTime);
                        const e = timeToMinutes(sh.endTime);
                        if (tMin >= s && tMin < e) return { start: s, end: e };
                      }
                      return null;
                    };

                    // Get occupied window for a reservation time
                    const getWindowEdit = (t: string) => {
                      if (singleTurnPerShift) {
                        const bounds = getShiftBoundsEdit(t);
                        if (bounds) return bounds;
                      }
                      const start = timeToMinutes(t);
                      return { start, end: start + duration };
                    };

                    // Get all reservations that overlap with the editing reservation's window
                    const editingTime = selectedReservation?.time;
                    const modalTableReservations = editingTime
                      ? reservations.filter((r) => {
                          // Exclude the reservation being edited
                          if (
                            selectedReservation &&
                            r.id === selectedReservation.id
                          )
                            return false;
                          if (finalStatusCodes.has(r.status)) return false;
                          // `reservations` solo contiene las de `selectedDate`
                          // (el día visible en pantalla). Si la reserva que se
                          // edita es de OTRO día (abierta por `?id=`), ninguna
                          // de estas ocupa esa fecha real: solo cuenta como
                          // ocupante si su fecha coincide con la de la
                          // reserva que se está editando.
                          if (
                            selectedReservation &&
                            format(new Date(r.date), "yyyy-MM-dd") !==
                              format(
                                new Date(selectedReservation.date),
                                "yyyy-MM-dd",
                              )
                          )
                            return false;
                          // Check overlap using shift-aware windows
                          const editW = getWindowEdit(editingTime);
                          const rW = getWindowEdit(r.time);
                          if (!(editW.start < rW.end && editW.end > rW.start))
                            return false;
                          const isDirectMatch = r.tableId === table.id;
                          const isInCombination =
                            r.tableCombination?.tables.some(
                              (t) => t.table.id === table.id,
                            ) ?? false;
                          return isDirectMatch || isInCombination;
                        })
                      : [];

                    const isReservedByOther = modalTableReservations.length > 0;
                    const hasMultipleInModal =
                      modalTableReservations.length > 1;

                    // Bloqueo SOFT para staff: se muestra y se pide
                    // confirmación, pero no se impide la selección. Usa el
                    // mismo `editingTime` (hora de la reserva que se edita)
                    // que ya resuelve el turno para el check de solapes.
                    const blockForEdit = getTableBlockForTime(
                      table.id,
                      editingTime,
                    );
                    const isBlockedTable = !!blockForEdit;

                    // Helper to get bg color for a reservation status (for split halves in modal).
                    // Reutiliza el lookup dinámico de colores de estado (BD), no un mapa hardcodeado.
                    const getModalStatusBgColor = (status: string) => {
                      const statusInfo = statusColorLookup[status];
                      if (!statusInfo) return { className: "bg-gray-100", style: {} };
                      return colorProps(statusInfo.bgColor, "backgroundColor");
                    };

                    // --- SPLIT VIEW for modal: table with multiple reservations ---
                    if (hasMultipleInModal) {
                      const modalTopStripColor = getModalStatusBgColor(
                        modalTableReservations[0].status,
                      );
                      const modalBottomStripColor = getModalStatusBgColor(
                        modalTableReservations[1].status,
                      );
                      // Tipografía escalada con el zoom (cellPx). Igual que
                      // en el mapa en vivo, esta vista "split" no oculta
                      // líneas por tamaño (cada franja es flex-1 y centra
                      // su única línea en el espacio disponible).
                      const modalSplitNameFontPx = clampNum(
                        modalMapViewport.cellPx * 0.45,
                        7,
                        10,
                      );
                      const modalSplitInfoFontPx = clampNum(
                        modalMapViewport.cellPx * 0.38,
                        7,
                        9,
                      );
                      return (
                        <div
                          key={table.id}
                          onMouseDown={(e) => e.stopPropagation()}
                          className={`absolute flex flex-col overflow-hidden border-2 cursor-not-allowed
                            transition-all duration-150 border-orange-400 ring-1 md:ring-2 ring-orange-300 opacity-80
                            ${table.shape === "circle" ? "rounded-full" : "rounded md:rounded-lg"}`}
                          style={{
                            left: `${table.positionX * cellSize}%`,
                            top: `${table.positionY * cellSize}%`,
                            width: `${(table.width || 1) * cellSize}%`,
                            height: `${(table.height || 1) * cellSize}%`,
                          }}
                          title={`${table.name} - ${modalTableReservations.length} reservas`}
                        >
                          {/* Table name — sin truncar con "…", envuelve
                              hasta 2 líneas */}
                          <div className="absolute top-0 left-0 right-0 text-center z-10 pointer-events-none">
                            <span
                              className="font-normal leading-tight text-gray-800 bg-white/70 px-0.5 rounded-b break-words line-clamp-2"
                              style={{ fontSize: modalSplitNameFontPx }}
                            >
                              {table.name}
                            </span>
                          </div>
                          {/* Top half - first reservation */}
                          <div
                            className={`flex-1 flex items-center justify-center ${modalTopStripColor.className}`}
                            style={modalTopStripColor.style}
                            title={`${modalTableReservations[0].customer.firstName} - ${modalTableReservations[0].partySize} pax - ${modalTableReservations[0].time.slice(0, 5)}`}
                          >
                            <span
                              className="font-normal leading-tight text-gray-800 truncate max-w-full px-0.5"
                              style={{ fontSize: modalSplitInfoFontPx }}
                            >
                              {modalTableReservations[0].partySize}p{" "}
                              {modalTableReservations[0].time.slice(0, 5)}
                            </span>
                          </div>
                          {/* Divider */}
                          <div className="h-px bg-orange-400 flex-shrink-0" />
                          {/* Bottom half - second reservation */}
                          <div
                            className={`flex-1 flex items-center justify-center ${modalBottomStripColor.className}`}
                            style={modalBottomStripColor.style}
                            title={`${modalTableReservations[1].customer.firstName} - ${modalTableReservations[1].partySize} pax - ${modalTableReservations[1].time.slice(0, 5)}`}
                          >
                            <span
                              className="font-normal leading-tight text-gray-800 truncate max-w-full px-0.5"
                              style={{ fontSize: modalSplitInfoFontPx }}
                            >
                              {modalTableReservations[1].partySize}p{" "}
                              {modalTableReservations[1].time.slice(0, 5)}
                            </span>
                          </div>
                          {/* Blocked icon */}
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                            <Ban className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={table.id}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={async () => {
                          // Don't allow selecting reserved tables
                          if (isReservedByOther) return;

                          if (isSelected) {
                            setSelectedTablesForManual((prev) =>
                              prev.filter((id) => id !== table.id),
                            );
                            return;
                          }
                          if (isBlockedTable) {
                            const proceed = await confirm({
                              title: "Mesa bloqueada",
                              message: `La mesa ${table.name} está bloqueada${
                                blockForEdit?.reason
                                  ? ` (${blockForEdit.reason})`
                                  : ""
                              }. ¿Asignarla de todos modos?`,
                              confirmText: "Asignar de todos modos",
                              cancelText: "Cancelar",
                              danger: true,
                            });
                            if (!proceed) return;
                          }
                          setSelectedTablesForManual((prev) => [
                            ...prev,
                            table.id,
                          ]);
                        }}
                        className={`
                          absolute flex flex-col items-center justify-center border md:border-2 overflow-hidden
                          transition-all duration-150
                          ${
                            isReservedByOther
                              ? "bg-red-100 border-red-300 text-red-400 cursor-not-allowed opacity-60"
                              : isSelected
                                ? isBlockedTable
                                  ? "bg-red-300 border-red-600 text-red-900 ring-1 md:ring-2 ring-red-500 md:ring-offset-2 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                  : "bg-primary-500 border-primary-600 text-white ring-1 md:ring-2 ring-primary-300 md:ring-offset-2 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                : isBlockedTable
                                  ? "bg-red-50 border-red-500 border-dashed text-red-700 hover:bg-red-100 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                  : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                          }
                          ${table.shape === "circle" ? "rounded-full" : "rounded md:rounded-lg"}
                        `}
                        style={{
                          left: `${table.positionX * cellSize}%`,
                          top: `${table.positionY * cellSize}%`,
                          width: `${(table.width || 1) * cellSize}%`,
                          height: `${(table.height || 1) * cellSize}%`,
                        }}
                        title={
                          isReservedByOther
                            ? `${table.name} - Reservada`
                            : isBlockedTable
                              ? `${table.name} - Bloqueada${blockForEdit?.reason ? `: ${blockForEdit.reason}` : ""}`
                              : `${table.name} (${table.minCapacity}-${table.maxCapacity} personas)`
                        }
                      >
                        {isBlockedTable && !isReservedByOther && (
                          <div className="absolute -top-0.5 -left-0.5 md:-top-1 md:-left-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-red-600 rounded-full flex items-center justify-center z-20">
                            <Lock className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                        {(() => {
                          const boxWidthPx =
                            (table.width || 1) * modalMapViewport.cellPx;
                          const boxHeightPx =
                            (table.height || 1) * modalMapViewport.cellPx;
                          const { fontPx: nameFontPx, lines: nameLines } =
                            fitNameToTwoLines(
                              table.name,
                              boxWidthPx,
                              clampNum(modalMapViewport.cellPx * 0.5, 8, 14),
                              8,
                            );
                          const infoFontPx = clampNum(
                            modalMapViewport.cellPx * 0.4,
                            7,
                            11,
                          );
                          const lineCount = countFittingLines(boxHeightPx, [
                            ...Array(nameLines).fill(nameFontPx),
                            infoFontPx,
                          ]);
                          const infoThreshold = nameLines + 1;
                          return (
                            <>
                              {lineCount >= 1 && (
                                <span
                                  className="font-normal leading-tight break-words line-clamp-2 max-w-full px-0.5"
                                  style={{ fontSize: nameFontPx }}
                                >
                                  {table.name}
                                </span>
                              )}
                              {lineCount >= infoThreshold && (
                                <span
                                  className={`font-normal leading-tight truncate max-w-full px-0.5 ${isBlockedTable && !isReservedByOther ? "text-red-700" : "opacity-75"}`}
                                  style={{ fontSize: infoFontPx }}
                                >
                                  {isReservedByOther
                                    ? "Ocupada"
                                    : isBlockedTable
                                      ? "Bloqueada"
                                      : `${table.minCapacity}-${table.maxCapacity}`}
                                </span>
                              )}
                            </>
                          );
                        })()}
                        {isSelected && !isReservedByOther && (
                          <div className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-green-500 rounded-full flex items-center justify-center">
                            <Check className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                        {isReservedByOther && (
                          <div className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-red-500 rounded-full flex items-center justify-center">
                            <Ban className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <AlertCircle className="w-12 h-12 mb-2 text-gray-300" />
                  <p>No hay salas configuradas</p>
                </div>
              )}
              </div>
              {currentRoom && (
                <ZoomControls
                  onZoomIn={modalMapViewport.zoomIn}
                  onZoomOut={modalMapViewport.zoomOut}
                  onFit={() =>
                    modalMapViewport.fitToContent(
                      computeContentBBox(
                        currentRoom.tables ?? [],
                        currentRoom.decorativeObjects ?? [],
                      ),
                    )
                  }
                />
              )}
            </div>

            {/* Selection Summary */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              {/* Legend */}
              <div className="flex items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded bg-gray-100 border border-gray-300" />
                  <span className="text-gray-600">Disponible</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded bg-primary-500 border border-primary-600" />
                  <span className="text-gray-600">Seleccionada</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded bg-red-100 border border-red-300 opacity-60" />
                  <span className="text-gray-600">Ocupada</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded bg-red-50 border border-dashed border-red-500" />
                  <span className="text-gray-600">Bloqueada</span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2">
                <div className="text-xs md:text-sm">
                  {selectedTablesForManual.length === 0 ? (
                    <span className="text-gray-500">
                      Ninguna mesa seleccionada
                    </span>
                  ) : (
                    <span className="text-gray-700">
                      <span className="font-medium">
                        {selectedTablesForManual.length} mesa(s):
                      </span>{" "}
                      {selectedTablesForManual
                        .map((id) => {
                          const table = rooms
                            .flatMap((r) => r.tables)
                            .find((t) => t.id === id);
                          return table?.name;
                        })
                        .filter(Boolean)
                        .join(" + ")}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTablesForManual([])}
                    className="px-3 md:px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors text-xs md:text-sm"
                    disabled={selectedTablesForManual.length === 0}
                  >
                    Limpiar
                  </button>
                  <button
                    onClick={() => setShowTableMapModal(false)}
                    className="px-3 md:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs md:text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      // Apply selection to form
                      if (selectedTablesForManual.length === 0) {
                        setEditFormData({
                          ...editFormData,
                          tableId: null,
                          tableCombinationId: null,
                          manualTableIds: [],
                        });
                      } else if (selectedTablesForManual.length === 1) {
                        setEditFormData({
                          ...editFormData,
                          tableId: selectedTablesForManual[0],
                          tableCombinationId: null,
                          manualTableIds: selectedTablesForManual,
                        });
                      } else {
                        // Multiple tables selected
                        setEditFormData({
                          ...editFormData,
                          tableId: selectedTablesForManual[0], // Primary table
                          tableCombinationId: null, // Will need to create combination on save
                          manualTableIds: selectedTablesForManual,
                        });
                      }
                      setShowTableMapModal(false);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    Confirmar selección
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Desplazar Mesa (desde detalle de reserva) */}
      {showMoveTableModal && selectedReservation && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[60] md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-4xl h-[95vh] md:h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900">
                  Seleccionar Mesas
                </h3>
                <p className="text-xs md:text-sm text-gray-500">
                  Toca la(s) mesa(s) de destino. La mesa sombreada es la
                  actual; si no seleccionas ninguna nueva, la reserva no se
                  moverá.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowMoveTableModal(false);
                  setSelectedTablesForMove([]);
                  setMoveOriginalTableIds([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Room tabs */}
            <div className="px-6 py-3 border-b border-gray-100 flex gap-2 flex-wrap">
              {rooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedRoom === room.id
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {room.name}
                </button>
              ))}
            </div>

            {/* Table map - exactly like the edit modal. Recuadro fijo
                (RECUADRO_PX) con zoom dinámico (auto-encuadre + lupa)
                dentro. */}
            <div className="relative w-full mx-auto flex-1 min-h-0 overflow-hidden" style={{ maxWidth: RECUADRO_PX }}>
              <div
                ref={modalMapViewport.containerRef}
                onMouseDown={modalMapViewport.onMouseDown}
                onTouchStart={modalMapViewport.onTouchStart}
                onScroll={modalMapViewport.onScroll}
                className={`w-full h-full overflow-auto p-4 bg-gray-50 ${modalMapViewport.isPanning ? "cursor-grabbing" : "cursor-grab"}`}
              >
              {currentRoom ? (
                <div
                  className="relative bg-white rounded-lg border-2 border-gray-200"
                  style={{
                    width: CANVAS_CELLS * modalMapViewport.cellPx,
                    height: CANVAS_CELLS * modalMapViewport.cellPx,
                  }}
                >
                  {/* Grid Background */}
                  <div
                    className="absolute inset-0 opacity-10 rounded-lg"
                    style={{
                      backgroundImage:
                        "linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)",
                      backgroundSize: `calc(100% / ${gridSize}) calc(100% / ${gridSize})`,
                    }}
                  />

                  {/* Decorative Objects */}
                  {decorativeObjects.map((obj) => {
                    const typeConfig = DECORATIVE_TYPES.find(
                      (t) => t.type === obj.type,
                    );
                    const bgColor = typeConfig?.color || "bg-gray-500";
                    const cellSize = 100 / gridSize;
                    return (
                      <div
                        key={obj.id}
                        className={`absolute ${bgColor} text-white rounded-md flex flex-col items-center justify-center shadow-md border-2 border-white/30 pointer-events-none`}
                        style={{
                          left: `${obj.positionX * cellSize}%`,
                          top: `${obj.positionY * cellSize}%`,
                          width: `${obj.width * cellSize}%`,
                          height: `${obj.height * cellSize}%`,
                          transform: `rotate(${obj.rotation}deg)`,
                        }}
                      >
                        {getDecorativeIcon(obj.type, "w-4 h-4 md:w-5 md:h-5")}
                        {obj.label && (
                          <span
                            className="text-[8px] md:text-xs font-medium mt-0.5 truncate px-1"
                            style={
                              obj.height > obj.width
                                ? {
                                    writingMode: "vertical-rl",
                                    textOrientation: "mixed",
                                  }
                                : undefined
                            }
                          >
                            {obj.label}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Tables */}
                  {currentRoom.tables.map((table) => {
                    const isSelected = selectedTablesForMove.includes(table.id);
                    // Mesa que la reserva ya ocupaba antes de abrir esta
                    // modal. Se pinta sombreada ("Actual") de forma
                    // INDEPENDIENTE de si está seleccionada o no; sigue
                    // siendo clicable como cualquier otra mesa.
                    const isOriginal = moveOriginalTableIds.includes(
                      table.id,
                    );
                    const cellSize = 100 / gridSize;

                    // Helper to convert "HH:MM" to minutes from midnight
                    const timeToMinutesMove = (t: string) => {
                      const [h, m] = t.split(":").map(Number);
                      return h * 60 + m;
                    };

                    const singleTurnPerShiftMove =
                      scheduleConfig?.singleTurnPerShift ?? false;
                    const durationMove = scheduleConfig?.defaultDuration ?? 90;

                    // Get shift bounds for a time (used when singleTurnPerShift = true)
                    const getShiftBoundsMove = (t: string) => {
                      const shifts = scheduleConfig?.shifts ?? [];
                      const tMin = timeToMinutesMove(t);
                      for (const sh of shifts as {
                        startTime: string;
                        endTime: string;
                        enabled: boolean;
                      }[]) {
                        if (!sh.enabled) continue;
                        const s = timeToMinutesMove(sh.startTime);
                        const e = timeToMinutesMove(sh.endTime);
                        if (tMin >= s && tMin < e) return { start: s, end: e };
                      }
                      return null;
                    };

                    // Get occupied window for a reservation time
                    const getWindowMove = (t: string) => {
                      if (singleTurnPerShiftMove) {
                        const bounds = getShiftBoundsMove(t);
                        if (bounds) return bounds;
                      }
                      const start = timeToMinutesMove(t);
                      return { start, end: start + durationMove };
                    };

                    // Check if table is already reserved (excluding current reservation being moved)
                    const isReservedByOther = selectedReservation?.time
                      ? reservations.some((r) => {
                          // Skip the current reservation being moved
                          if (r.id === selectedReservation.id) return false;
                          // Skip cancelled/no-show/completed
                          if (finalStatusCodes.has(r.status)) return false;
                          // `reservations` solo contiene las de `selectedDate`
                          // (el día visible en pantalla). Si la reserva que se
                          // mueve es de OTRO día (abierta por `?id=`), ninguna
                          // de estas ocupa esa fecha real: solo cuenta como
                          // ocupante si su fecha coincide con la de la
                          // reserva que se está moviendo (mismo criterio que
                          // `moveOverride` más abajo).
                          if (
                            format(new Date(r.date), "yyyy-MM-dd") !==
                            format(new Date(selectedReservation.date), "yyyy-MM-dd")
                          )
                            return false;
                          // Check overlap using shift-aware windows
                          const movingW = getWindowMove(
                            selectedReservation.time,
                          );
                          const rW = getWindowMove(r.time);
                          if (
                            !(movingW.start < rW.end && movingW.end > rW.start)
                          )
                            return false;
                          // Check if this table is assigned to another reservation
                          const isDirectMatch = r.tableId === table.id;
                          const isInCombination =
                            r.tableCombination?.tables.some(
                              (t: { table: { id: string } }) =>
                                t.table.id === table.id,
                            ) ?? false;
                          return isDirectMatch || isInCombination;
                        })
                      : false;

                    // Capacidad efectiva: solo se resuelve si la reserva que se
                    // mueve pertenece a la fecha mostrada en el mapa (los
                    // overrides solo se cargan para `selectedDate`).
                    const moveOverride =
                      selectedReservation?.time &&
                      format(new Date(selectedReservation.date), "yyyy-MM-dd") ===
                        format(selectedDate, "yyyy-MM-dd")
                        ? getTableCapacityOverride(
                            table.id,
                            deriveShiftFromTime(selectedReservation.time),
                          )
                        : null;
                    const effectiveMinMove =
                      moveOverride?.minCapacity ?? table.minCapacity;
                    const effectiveMaxMove =
                      moveOverride?.maxCapacity ?? table.maxCapacity;

                    // Bloqueo SOFT para staff: se muestra y se pide
                    // confirmación, pero no se impide la selección.
                    const blockForMove = getTableBlockForTime(
                      table.id,
                      selectedReservation?.time,
                    );
                    const isBlockedTable = !!blockForMove;

                    return (
                      <div
                        key={table.id}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={async () => {
                          // Don't allow selecting reserved tables
                          if (isReservedByOther) return;

                          if (isSelected) {
                            setSelectedTablesForMove((prev) =>
                              prev.filter((id) => id !== table.id),
                            );
                            return;
                          }
                          if (isBlockedTable) {
                            const proceed = await confirm({
                              title: "Mesa bloqueada",
                              message: `La mesa ${table.name} está bloqueada${
                                blockForMove?.reason
                                  ? ` (${blockForMove.reason})`
                                  : ""
                              }. ¿Asignarla de todos modos?`,
                              confirmText: "Asignar de todos modos",
                              cancelText: "Cancelar",
                              danger: true,
                            });
                            if (!proceed) return;
                          }
                          setSelectedTablesForMove((prev) => [
                            ...prev,
                            table.id,
                          ]);
                        }}
                        className={`
                          absolute flex flex-col items-center justify-center border md:border-2 overflow-hidden
                          transition-all duration-150
                          ${
                            isReservedByOther
                              ? "bg-red-100 border-red-300 text-red-400 cursor-not-allowed opacity-60"
                              : isSelected
                                ? isBlockedTable
                                  ? "bg-red-300 border-red-600 text-red-900 ring-1 md:ring-2 ring-red-500 md:ring-offset-2 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                  : isOriginal
                                    ? "bg-primary-500 border-blue-700 text-white ring-2 ring-blue-400 md:ring-offset-2 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                    : "bg-primary-500 border-primary-600 text-white ring-1 md:ring-2 ring-primary-300 md:ring-offset-2 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                : isBlockedTable
                                  ? "bg-red-50 border-red-500 border-dashed text-red-700 hover:bg-red-100 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                  : isOriginal
                                    ? "bg-blue-50 border-blue-400 border-dashed text-blue-700 hover:bg-blue-100 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                    : moveOverride
                                      ? "bg-gray-100 border-amber-400 ring-1 ring-amber-300 text-gray-600 hover:bg-gray-200 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                                      : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 cursor-pointer md:hover:scale-105 hover:shadow-lg hover:z-10"
                          }
                          ${table.shape === "circle" ? "rounded-full" : "rounded md:rounded-lg"}
                        `}
                        style={{
                          left: `${table.positionX * cellSize}%`,
                          top: `${table.positionY * cellSize}%`,
                          width: `${(table.width || 1) * cellSize}%`,
                          height: `${(table.height || 1) * cellSize}%`,
                        }}
                        title={
                          isReservedByOther
                            ? `${table.name} - Reservada`
                            : isBlockedTable
                              ? `${table.name} - Bloqueada${blockForMove?.reason ? `: ${blockForMove.reason}` : ""}`
                              : `${table.name} (${effectiveMinMove}-${effectiveMaxMove} personas${moveOverride ? ", aforo ajustado" : ""})${isOriginal ? " - Mesa actual" : ""}`
                        }
                      >
                        {isBlockedTable && !isReservedByOther && (
                          <div className="absolute -top-0.5 -left-0.5 md:-top-1 md:-left-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-red-600 rounded-full flex items-center justify-center z-20">
                            <Lock className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                        {(() => {
                          const boxWidthPx =
                            (table.width || 1) * modalMapViewport.cellPx;
                          const boxHeightPx =
                            (table.height || 1) * modalMapViewport.cellPx;
                          const { fontPx: nameFontPx, lines: nameLines } =
                            fitNameToTwoLines(
                              table.name,
                              boxWidthPx,
                              clampNum(modalMapViewport.cellPx * 0.5, 8, 14),
                              8,
                            );
                          const infoFontPx = clampNum(
                            modalMapViewport.cellPx * 0.4,
                            7,
                            11,
                          );
                          const lineCount = countFittingLines(boxHeightPx, [
                            ...Array(nameLines).fill(nameFontPx),
                            infoFontPx,
                          ]);
                          const infoThreshold = nameLines + 1;
                          return (
                            <>
                              {lineCount >= 1 && (
                                <span
                                  className="font-normal leading-tight break-words line-clamp-2 max-w-full px-0.5"
                                  style={{ fontSize: nameFontPx }}
                                >
                                  {table.name}
                                </span>
                              )}
                              {lineCount >= infoThreshold && (
                                <span
                                  className={`font-normal leading-tight truncate max-w-full px-0.5 ${
                                    isBlockedTable && !isReservedByOther
                                      ? "text-red-700"
                                      : isOriginal && !isReservedByOther
                                        ? isSelected
                                          ? "text-white/90"
                                          : "text-blue-700"
                                        : moveOverride && !isReservedByOther
                                          ? "text-amber-700"
                                          : "opacity-75"
                                  }`}
                                  style={{ fontSize: infoFontPx }}
                                >
                                  {isReservedByOther
                                    ? "Ocupada"
                                    : isBlockedTable
                                      ? "Bloqueada"
                                      : isOriginal
                                        ? "Actual"
                                        : `${effectiveMinMove}-${effectiveMaxMove}`}
                                </span>
                              )}
                            </>
                          );
                        })()}
                        {/* Etiqueta "Actual": mesa que la reserva ya tenía
                            asignada antes de abrir esta modal. Independiente
                            de si está seleccionada o no (se ve junto al
                            check verde si el usuario la vuelve a marcar). */}
                        {isOriginal && !isReservedByOther && (
                          <div
                            className="absolute -bottom-0.5 -left-0.5 md:-bottom-1 md:-left-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-blue-600 rounded-full flex items-center justify-center z-20"
                            title="Mesa actual de la reserva"
                          >
                            <MapPin className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                        {isSelected && !isReservedByOther && (
                          <div className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-green-500 rounded-full flex items-center justify-center">
                            <Check className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                        {isReservedByOther && (
                          <div className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-red-500 rounded-full flex items-center justify-center">
                            <Ban className="w-2 h-2 md:w-3 md:h-3 text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No hay salas configuradas
                </div>
              )}
              </div>
              {currentRoom && (
                <ZoomControls
                  onZoomIn={modalMapViewport.zoomIn}
                  onZoomOut={modalMapViewport.zoomOut}
                  onFit={() =>
                    modalMapViewport.fitToContent(
                      computeContentBBox(
                        currentRoom.tables ?? [],
                        currentRoom.decorativeObjects ?? [],
                      ),
                    )
                  }
                />
              )}
            </div>

            {/* Legend */}
            <div className="px-6 py-3 border-t border-gray-100 flex items-center gap-6 text-xs text-gray-500 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-100 border-2 border-gray-300" />
                <span>Disponible</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-50 border-2 border-dashed border-blue-400" />
                <span>Actual</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-primary-500 border-2 border-primary-600" />
                <span>Seleccionada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-300" />
                <span>Ocupada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-50 border-2 border-dashed border-red-500" />
                <span>Bloqueada</span>
              </div>
            </div>

            {/* Footer */}
            <div className="px-3 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-white flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2">
              <div className="text-xs md:text-sm text-gray-600">
                {selectedTablesForMove.length > 0 ? (
                  <span>
                    {selectedTablesForMove.length} mesa(s):{" "}
                    {selectedTablesForMove
                      .map((id) => {
                        const table = rooms
                          .flatMap((r) => r.tables)
                          .find((t) => t.id === id);
                        return table?.name;
                      })
                      .join(", ")}
                  </span>
                ) : (
                  <span className="text-gray-400">
                    Selecciona la(s) mesa(s) de destino
                  </span>
                )}
              </div>
              <div className="flex gap-2 md:gap-3">
                <button
                  onClick={() => setSelectedTablesForMove([])}
                  className="px-3 py-1.5 text-xs md:text-sm text-gray-600 hover:text-gray-800"
                >
                  Limpiar
                </button>
                <button
                  onClick={() => {
                    setShowMoveTableModal(false);
                    setSelectedTablesForMove([]);
                    setMoveOriginalTableIds([]);
                  }}
                  className="px-3 md:px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs md:text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleMoveTable}
                  disabled={
                    selectedTablesForMove.length === 0 || moveSubmitting
                  }
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {moveSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Confirmar selección"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ficha 360 del cliente (estadísticas, historial, comunicaciones) —
          la misma que en la página de Clientes. Desde el nombre en las
          tarjetas o el botón "Ver ficha" del detalle. */}
      {viewCustomerId && (
        <CustomerDetailModal
          customerId={viewCustomerId}
          onClose={() => setViewCustomerId(null)}
          onEdit={(id) => {
            setViewCustomerId(null);
            void openCustomerEditById(id);
          }}
          onChanged={() => fetchReservations(true)}
        />
      )}

      {/* Ficha del cliente (mismo formulario que la página de Clientes). Va
          después del detalle en el árbol para quedar por encima (mismo z-50). */}
      {editingCustomer && (
        <CustomerEditModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSave={handleCustomerSaved}
        />
      )}

      {/* Modal de Email */}
      {showEmailModal && selectedReservation && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-lg max-h-[95dvh] md:max-h-[90dvh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex justify-between items-center bg-blue-50">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900">
                  Enviar Email
                </h3>
                <p className="text-xs md:text-sm text-gray-500">
                  Para: {selectedReservation.customer.name} (
                  {selectedReservation.customer.email})
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailFormData({ subject: "", message: "" });
                  setEmailAttachments([]);
                }}
                className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Asunto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto
                </label>
                <input
                  type="text"
                  value={emailFormData.subject}
                  onChange={(e) =>
                    setEmailFormData({
                      ...emailFormData,
                      subject: e.target.value,
                    })
                  }
                  placeholder="Asunto del email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Mensaje */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje
                </label>
                <textarea
                  value={emailFormData.message}
                  onChange={(e) =>
                    setEmailFormData({
                      ...emailFormData,
                      message: e.target.value,
                    })
                  }
                  placeholder="Escribe tu mensaje..."
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Adjuntos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adjuntos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        setEmailAttachments(Array.from(e.target.files));
                      }
                    }}
                    className="hidden"
                    id="email-attachments"
                  />
                  <label
                    htmlFor="email-attachments"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Paperclip className="w-6 h-6 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      Haz clic para adjuntar archivos
                    </span>
                  </label>
                </div>
                {emailAttachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {emailAttachments.map((file: File, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg text-sm"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          onClick={() => {
                            setEmailAttachments(
                              emailAttachments.filter(
                                (_: File, i: number) => i !== index,
                              ),
                            );
                          }}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex justify-end gap-2 md:gap-3 bg-gray-50">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailFormData({ subject: "", message: "" });
                  setEmailAttachments([]);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmail}
                disabled={
                  emailSending ||
                  !emailFormData.subject.trim() ||
                  !emailFormData.message.trim()
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {emailSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Reservation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[70] md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full md:max-w-lg max-h-[95dvh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">
                    Cancelar reserva
                  </h3>
                  <p className="text-xs text-gray-500">
                    Esta acción no se puede deshacer
                  </p>
                </div>
              </div>
              <button
                onClick={closeCancelModal}
                disabled={cancelling}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="px-4 md:px-6 py-4 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Motivo de la cancelación{" "}
                  <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) =>
                    setCancelReason(e.target.value.slice(0, 500))
                  }
                  rows={4}
                  placeholder="Ej: Avería en cocina, problema con el suministro eléctrico, aforo completo por reserva privada..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  disabled={cancelling}
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500">
                    Si lo indicas, el cliente lo verá en el aviso.
                  </p>
                  <span className="text-xs text-gray-400">
                    {cancelReason.length}/500
                  </span>
                </div>
              </div>

              <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={notifyCustomerOnCancel}
                  onChange={(e) => setNotifyCustomerOnCancel(e.target.checked)}
                  disabled={cancelling}
                  className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Notificar al cliente por email y WhatsApp
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Se enviará un mensaje al cliente con el motivo de la
                    cancelación.
                  </div>
                </div>
              </label>
            </div>

            {/* Footer */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex justify-end gap-2 md:gap-3 bg-gray-50">
              <button
                onClick={closeCancelModal}
                disabled={cancelling}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Volver
              </button>
              <button
                onClick={confirmCancelReservation}
                disabled={cancelling}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {cancelling ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Cancelar reserva
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
